import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Clock,
  Cloud,
  Copy,
  FilePenLine,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderUp,
  ChevronLeft,
  ChevronRight,
  Headphones,
  Link2,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Share2,
  ShieldCheck,
  Star,
  Trash2,
  Undo2,
  Upload,
  X,
} from 'lucide-react';
import { PortalChip } from '../../arkorbit-ui/PortalChip';
import { PortalSwitch } from '../../arkorbit-ui/PortalSwitch';
import { PortalShortcutHint } from '../PortalShortcutHint';
import { usePortalShortcutAria } from '../portalShortcutContext';
import {
  ARKO_SCHREIBEN_PDF_URL,
  PORTAL_DOCUMENT_UPLOAD_URL,
  PORTAL_DOCUMENT_FOLDER_URL,
  PORTAL_NEXTCLOUD_SHARE_IMPORT_URL,
  PORTAL_PROTOKOLLE_COLLECTION,
  getPortalNextcloudShareAllowedHosts,
  getPortalPocketBaseUrl,
  getPortalPbToken,
  getPortalTenantN8nExtras,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  portalAuthzSidecarExpected,
  resolveOnlyofficeIssueJwtUrl,
  validateNextcloudSharePasteUrl,
} from '../../../constants';
import { createBlankDocxFile } from '../blankDocx';
import {
  PORTAL_DOCUMENT_FILE_ACCEPT,
  PORTAL_IMAGE_OCR_NOTICE,
  type PortalDocumentOpenFields,
  type PortalProtokoll,
  type PortalRecentDocument,
  canOpenDocumentInPortal,
  classifyPortalDocumentGroup,
  fileExtensionFromName,
  formatDeAtDateTime,
  formatPortalFileSize,
  portalDocumentTypeLabel,
  isAudioFile,
  isPortalPdfDocument,
  parsePortalDocumentN8nResponse,
  portalImageUploadPolicyError,
  resolveProtokollDocumentUrl,
  portalDocumentRelativeFolder,
  sanitizePortalFolderInput,
  sanitizePortalFileName,
  portalFolderDisplayPath,
  portalUploadFolderPathFromWebkit,
  filterPortalFolderUploadFiles,
  fetchPortalProtokollById,
  portalDocumentOpenFieldsFromProtokoll,
  isPortalFolderDataTransfer,
  portalDragMayAcceptExternalFiles,
  collectPortalDropFiles,
  loadPortalVirtualFolders,
  savePortalVirtualFolders,
  loadPortalDocFavorites,
  savePortalDocFavorites,
  togglePortalDocFavorite,
  loadPortalDocPins,
  savePortalDocPins,
  upsertPortalDocPin,
  applyPortalDocumentRenameToShortcuts,
  applyPortalDocumentRenameToRecent,
  saveRecentPortalDocument,
  loadRecentPortalDocuments,
  portalExplorerChildFolders,
  portalExplorerParentFolder,
  portalExplorerDocInCurrentFolder,
  portalExplorerBreadcrumb,
  portalFolderApiErrorMessage,
  loadPortalUploadOpenAfterSave,
  savePortalUploadOpenAfterSave,
  renamePortalDocumentViaFolderApi,
  portalFolderRelativeFromNcPath,
  portalSeatRootNcPath,
  type PortalDocShortcut,
} from '../portalDocuments';
import { PortalAktenwaechterPanel } from '../PortalAktenwaechterPanel';
import { PortalAkteMitgliederPanel } from '../PortalAkteMitgliederPanel';
import {
  PortalAkteSchreibenPanel,
  type PortalAkteSchreibenExportResult,
} from '../PortalAkteSchreibenPanel';
import { PortalDocumentSharePanel } from '../PortalDocumentSharePanel';
import { PortalFristenAktePanel } from '../verfahrensmotor/PortalFristenAktePanel';
import type { PortalAkteSchreibenFormat } from '../portalAkteSchreiben';
import ContractGeneratorView from '../../contract/ContractGeneratorView';
import {
  describePortalDocumentVisibility,
  ensurePortalFolderRecord,
  listPortalDocumentShares,
  listPortalFolders,
  placeSharePopover,
  sharesForDocument,
  type PortalDocumentShare,
  type PortalFolderRecord,
} from '../portalDocumentShares';
import {
  listChecklistItemsForAkte,
  listPortalAktenForCustomer,
  listPortalAkteMembers,
  listKanzleiSeatsForAktePicker,
  portalDokumentBelongsToAkte,
  updatePortalAkteNcPath,
  updatePortalAkteTitle,
  addChecklistItemToAkte,
  createChecklistItemForAkte,
  removeChecklistItemFromAkte,
  updateChecklistItemLabel,
  updateChecklistItemNote,
  updateChecklistItemStatus,
} from '../portalAkten';
import type { KanzleiSeatForAktePicker, PortalAkteRecord, PortalAktenChecklistRecord } from '../portalAktenTypes';
import { filterPortalAkteList } from '../portalAkteListSearch';
import { listPortalFristenForAkte } from '../verfahrensmotor/portalFristen';
import { isItAdminAkteOverview } from '../portalCustomerAccess';
import type { PortalRole } from '../portalCustomerAccess';
import {
  portalAkteCustomerFolderHint,
  portalAkteHubFristenPath,
  resolveVisiblePortalAkteForHub,
  type PortalAkteHubTab,
} from '../portalAkteHub';
import {
  akteLageSection,
  akteLocalToday,
  buildAkteVerlaufAnchors,
  formatAkteDay,
  toggleAkteRowLage,
  nextAkteFrist,
  portalAkteRahmenSections,
  clampAkteOoEditorPct,
  recallAkteOoSplit,
  recallPortalAkteRahmen,
  recallPortalAkteRahmenOpen,
  rememberAkteOoSplit,
  rememberPortalAkteRahmen,
  rememberPortalAkteRahmenOpen,
  planAkteRahmenRestoreAfterViewReturn,
  type AkteFristRef,
  type PortalAkteRahmenSectionId,
} from '../portalAkteRahmen';
import { peekAkteChatId } from '../portalArkoThreadBind';
import { CHECKLIST_FAVORITE_NOTE, CHECKLIST_HIDDEN_NOTE, checklistForAkte, checklistStand, planCustomChecklistPoint, pushChecklistUndo, type ChecklistUndoStep } from '../portalAkteChecklist';
import { VERTRAG_BERATUNG_CHECKLIST } from '../aktenTemplates';
import { PORTAL_EIGEN_FRIST_REGEL_ID, eigenFristIsPrepared } from '../verfahrensmotor/portalEigenFrist';
import { countPortalPdfPages } from '../portalAkteDocFirstPage';
import { akteOnlyofficePlusKind, decidePortalOnlyofficeSurface, filterDocsByFilename, portalAkteFirstPageMode, portalFileTypeMark } from '../portalOnlyofficeSurface';
import { PortalAkteRahmenChrome } from './PortalAkteRahmenChrome';
import { PortalAkteArkoHost } from '../portalAkteArkoHost';
import {
  collectPortalAkteExplorerFolders,
  normalizePortalDocumentNcPath,
  portalAkteAssignTargetFolder,
  portalAkteExplorerChildFolders,
  portalAkteNcPathFromMovedDocument,
  portalAkteRelativeDocFolder,
  portalAkteTargetFolder,
  portalDocumentCopyKeepsSource,
  portalDocumentMovePersistedInAkte,
  portalFolderApiAkteId,
  portalFolderPathContainsSmokeFixture,
} from '../portalAkteDocumentActions';
import { usePopoverDismissOutside } from '../usePopoverDismissOutside';
import { DokumenteRowActions } from './DokumenteRowActions';

type DokumenteTileId = 'documents' | 'cases' | 'quick';
export type DokumenteHubMode = 'documents' | 'akten';

const FOLDER_RENAME_HINT = 'Ordner in Nextcloud umbenennen ist noch nicht angebunden.';
const FOLDER_DELETE_HINT = 'Ordner in Nextcloud löschen ist noch nicht angebunden.';
const AKTE_DELETE_HINT = 'Akten-Löschen ist noch nicht angebunden.';
/** Ab dieser Inhaltsbreite (nach Sidebar) nebeneinander; darunter stapeln — 13″ / ~1280×800.
 *  Spaltenbreiten fest — Fokus nur per Border/Ring, sonst Layout-Thrash (Lesson 24abf175). */
const TILE_SPLIT_DOCS = '@min-[1180px]:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)]';

/** Schwarzer Grund, feine lila Kante — Kanzlei-Liste wie die Aktenliste. */
const KANZLEI_DOC_GROUND = 'bg-[#07070a]';
const KANZLEI_DOC_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
/** Knopf wie Neue Akte und Öffnen. */
const KANZLEI_DOC_BTN =
  'inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45';
const KANZLEI_DOC_ICON =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45';
const KANZLEI_DOC_ICON_ON =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400 bg-violet-600 text-white disabled:cursor-not-allowed disabled:opacity-45';

/**
 * Dieselbe Würfel-Drehung wie Arko ON/off: rotateX, 500ms, Perspektive 280.
 * Nur in einer group-Klappleiste (details.group).
 */
function KanzleiDocCube({
  front,
  back,
}: {
  front: React.ReactNode;
  back: React.ReactNode;
}) {
  const face =
    'absolute inset-0 inline-flex h-full w-full items-center justify-center [backface-visibility:hidden]';
  return (
    <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
      <span className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d] group-open:rotate-x-180">
        <span className={`${face} rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200`}>
          {front}
        </span>
        <span className={`${face} rounded-md bg-violet-600 text-white`} style={{ transform: 'rotateX(180deg)' }}>
          {back}
        </span>
      </span>
    </span>
  );
}

function dokumenteTileChromeClass(isActive: boolean, isDropTarget: boolean): string {
  return [
    'min-w-0 flex flex-col rounded-lg overflow-hidden border transition-colors duration-200 bg-[#07070a]',
    isActive ? 'border-violet-400/50' : 'border-violet-400/30',
    isDropTarget ? 'ring-2 ring-violet-400/40' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

const PORTAL_DOC_DRAG_MIME = 'application/x-arkorbit-portal-doc';

function portalDragIsInternalDoc(
  dataTransfer: DataTransfer | null | undefined,
  draggingDocId: string | null,
): boolean {
  if (draggingDocId) return true;
  if (!dataTransfer) return false;
  const types = Array.from(dataTransfer.types || []);
  return (
    types.includes(PORTAL_DOC_DRAG_MIME) ||
    (types.includes('text/plain') && !types.includes('Files'))
  );
}

/** Chrome lässt Drop nur zu, wenn dragover preventDefault + dropEffect zum effectAllowed passt. */
function allowPortalAkteDragOver(e: React.DragEvent, _draggingDocId: string | null): void {
  e.preventDefault();
  e.stopPropagation();
  e.dataTransfer.dropEffect = 'copy';
}

function portalIngestMsgLooksOk(msg: string): boolean {
  return (
    msg.includes('gespeichert') ||
    msg.includes('importiert') ||
    msg.includes('hochgeladen') ||
    msg.includes('→ Diktat') ||
    msg.includes('angeheftet') ||
    msg.includes('umbenannt') ||
    msg.includes('zugeordnet') ||
    msg.includes('angelegt') ||
    msg.includes('Kopie')
  );
}

function startPortalDocumentDrag(e: React.DragEvent, docId: string): void {
  e.dataTransfer.effectAllowed = 'copyMove';
  e.dataTransfer.setData(PORTAL_DOC_DRAG_MIME, docId);
  e.dataTransfer.setData('text/plain', docId);
}

function readPortalDragDocId(dataTransfer: DataTransfer, fallback: string | null): string {
  return (
    dataTransfer.getData(PORTAL_DOC_DRAG_MIME).trim() ||
    dataTransfer.getData('text/plain').trim() ||
    fallback ||
    ''
  );
}

function shortcutFromProtokoll(p: PortalProtokoll): PortalDocShortcut {
  return {
    id: `doc:${p.id}`,
    kind: 'document',
    title: p.filename,
    pb_record_id: p.id,
    nextcloud_path: p.nextcloud_path,
    document_url: p.nc_url,
  };
}

function resolvePortalDocumentIcon(ext: string): {
  Icon: typeof FileText;
  colorClass: string;
} {
  switch (ext) {
    case 'pdf':
      return { Icon: FileText, colorClass: 'text-red-400' };
    case 'xlsx':
    case 'xls':
    case 'csv':
      return { Icon: FileSpreadsheet, colorClass: 'text-emerald-400' };
    case 'pptx':
    case 'ppt':
      return { Icon: FileText, colorClass: 'text-orange-400' };
    case 'docx':
    case 'doc':
    case 'odt':
      return { Icon: FilePenLine, colorClass: 'text-blue-400' };
    default:
      return { Icon: FileText, colorClass: 'text-slate-400' };
  }
}

export interface DokumenteViewProps {
  customerId: string;
  /** PB `customers.kanzlei_id` — Akten-Liste wie Docs (Org + eigener Sitz). */
  kanzleiId?: string;
  portalRole?: PortalRole;
  isPrimary?: boolean;
  /** PB `customers.kanzlei` — Co-Brand-Header „… für {KANZLEI_NAME}“ in blankDocx. */
  kanzleiName?: string;
  /** PB `customers.status` — FIELD_DEMO / FIRMA_DEMO → n8n tenant=field|demo. */
  customerStatus?: string;
  /** PB `customers.email` — Fallback für Firma-Demo ohne status-Feld (orbis-demo@…). */
  customerEmail?: string;
  isPremium: boolean;
  /** false = Praktikant / Leih: Liste+Öffnen, keine Schreib-CTAs. */
  canWriteDocuments?: boolean;
  protokolle: PortalProtokoll[];
  protokolleLoading: boolean;
  protokolleError: string;
  recentDocs: PortalRecentDocument[];
  /** Zuletzt gewählter Ordner bleibt beim Wechsel in Chat/Editor erhalten. */
  initialFolder?: string;
  onCurrentFolderChange?: (folder: string) => void;
  onRefresh: () => void | Promise<void>;
  /** Direkt nach Upload/NC-Import — Hub-Liste optimistisch ergänzen. */
  onDocumentIngested?: (fields: PortalDocumentOpenFields) => void;
  /** Nach erfolgreichem Folder-API rename (MOVE+PATCH) — Parent aktualisiert Liste/Tabs ohne PB-Rehydrate. */
  onDocumentRenamed?: (args: {
    pb_record_id: string;
    display_name: string;
    nextcloud_path?: string;
  }) => void;
  onOpenDocument: (fields: PortalDocumentOpenFields) => void;
  /** Dokument dieser Akte: eigene OnlyOffice-Fläche, nicht die Kanzlei-Instanz. */
  onOpenAkteOnlyoffice?: (fields: PortalDocumentOpenFields) => void;
  onCloseAkteOnlyoffice?: () => void;
  akteOnlyoffice?: React.ReactNode;
  /** Geöffnete Dokumente nur dieser Akte, wechselbar im Reiter OnlyOffice. */
  akteOfficeDocs?: ReadonlyArray<{ id: string; title: string }>;
  akteOfficeActiveId?: string;
  onSelectAkteOfficeDoc?: (id: string) => void;
  /** Hauptreiter Akten: Fallliste, auch wenn die Akte schon offen ist. */
  aktenHomeTick?: number;
  onRouteAudioToDiktat: (file: File) => void;
  onRecentDocsChange?: () => void;
  /** PB-Record hervorheben (z. B. nach Diktat-Upload). */
  highlightRecordId?: string | null;
  onHighlightClear?: () => void;
  /** Mandanten-PB-Token für Aktenwächter (Phase 1.2). */
  authToken?: string | null;
  completedByEmail?: string;
  /** Aktenraum v1: gehobene Bindung, geteilt mit ARKO-Chat. */
  boundAkteId?: string | null;
  onBoundAkteChange?: (akte: PortalAkteRecord) => void;
  onAktenListChange?: (akten: PortalAkteRecord[]) => void;
  /** S1: Öffnet die Akte am Klick-Hub; ARKO ist dafür nicht erforderlich. */
  onOpenAkte?: (akte: PortalAkteRecord) => void;
  /** Optionaler, ausdrücklicher ARKO-Weg neben dem Akt-Hub. */
  onOpenAkteInChat?: (akte: PortalAkteRecord) => void;
  /** Bestehender Vertragsersteller bleibt im Schreiben-Tab unverändert eingebettet. */
  contractGeneratorAvailable?: boolean;
  /** documents = Ablage ohne Wizard; akten = Anlegen/Fokus/Zuordnen. */
  hubMode?: DokumenteHubMode;
  /** Ablage: Hinweis „Reiter Akten“ zum Anlegen, ohne Zwang. */
  onGoToAkten?: () => void;
  /** PB matrixRoomId — Share-Popover Element-Link host-aware. */
  matrixRoomId?: string;
  /** Element am eigenen Sitz. Ohne Sitz bleibt die Chat-Zeile ehrlich leer. */
  elementSeat?: boolean;
  seatMxid?: string;
  /** Primärnav führt ARKO. Sonst keine Zeile. */
  arkoBooked?: boolean;
  /** Akte an den bestehenden ARKO-Chat hängen, ohne die Ansicht zu verlassen. */
  onBindAkteForArko?: (akte: PortalAkteRecord) => void;
  /** Geöffnete Akte füllt die Arbeitsfläche. */
  onRahmenActiveChange?: (active: boolean) => void;
  /** Nach „In Akte speichern“: OnlyOffice dieser Akte, nicht die Kanzlei-Fläche. */
  akteSaveFocus?: { akteId: string; docId: string; tick: number } | null;
  /**
   * Bereits geladene Akten vom Parent (überlebt View-Wechsel Diktat→Akten).
   * Verhindert Restore auf leerer Erstliste.
   */
  seedAkten?: readonly PortalAkteRecord[];
}

function AkteFileTypeName({ filename }: { filename: string }) {
  const mark = portalFileTypeMark(filename);
  const tone =
    mark.tone === 'pdf'
      ? 'bg-red-500/15 text-red-300'
      : mark.tone === 'docx'
        ? 'bg-blue-500/15 text-blue-200'
        : 'bg-white/[0.06] text-slate-400';
  return (
    <>
      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${tone}`}>{mark.label}</span>
      <span className="min-w-0 break-all text-left text-[11px] text-slate-200">{filename}</span>
    </>
  );
}

const AKTE_VIOLET_BTN =
  'inline-flex shrink-0 items-center justify-center rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500';

function portalStatDate(iso?: string | null): string | null {
  const raw = iso?.trim();
  if (!raw) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDeAtDateTime(raw);
}

/** Hover-Karte nur in der Dokumentliste: Statistik, kein Seitenbild. */
function AkteDocLinkPreview({
  name,
  typeLabel,
  tone,
  sizeBytes,
  revisionId,
  createdIso,
  updatedIso,
  pdfUrl,
  pdfPath,
}: {
  name: string;
  typeLabel: string;
  tone: 'pdf' | 'docx' | 'plain';
  sizeBytes?: number;
  revisionId?: number;
  createdIso?: string;
  updatedIso?: string;
  pdfUrl?: string;
  pdfPath?: string;
}) {
  const [hovered, setHovered] = useState(false);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [counted, setCounted] = useState(false);
  useEffect(() => {
    if (!hovered || counted || (!pdfUrl && !pdfPath)) return;
    let cancel = false;
    void countPortalPdfPages({ documentUrl: pdfUrl, nextcloudPath: pdfPath }).then((count) => {
      if (cancel) return;
      setCounted(true);
      if (typeof count === 'number' && count >= 1) setPageCount(count);
    });
    return () => {
      cancel = true;
    };
  }, [hovered, counted, pdfPath, pdfUrl]);
  const typeTone =
    tone === 'pdf'
      ? 'bg-red-600 text-white'
      : tone === 'docx'
        ? 'bg-blue-600 text-white'
        : 'bg-slate-600 text-white';
  const rows: { label: string; value: string }[] = [];
  if (typeof sizeBytes === 'number' && Number.isFinite(sizeBytes) && sizeBytes >= 0) {
    const sizeLabel = formatPortalFileSize(sizeBytes);
    if (sizeLabel && sizeLabel !== '—') rows.push({ label: 'Größe', value: sizeLabel });
  }
  if (typeof pageCount === 'number' && pageCount >= 1) {
    rows.push({ label: 'Seiten', value: String(pageCount) });
  }
  if (typeof revisionId === 'number' && Number.isFinite(revisionId) && revisionId >= 1) {
    rows.push({ label: 'Revision', value: String(Math.floor(revisionId)) });
  }
  const createdLabel = portalStatDate(createdIso);
  if (createdLabel) rows.push({ label: 'Erstellt', value: createdLabel });
  const changedLabel = portalStatDate(updatedIso);
  if (changedLabel) rows.push({ label: 'Zuletzt geändert', value: changedLabel });
  return (
    <span
      className="relative block min-w-0 max-w-full overflow-visible"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="block min-w-0 truncate text-sm font-medium text-white">{name}</span>
      <AnimatePresence>
        {hovered ? (
          <motion.div
            data-testid="portal-akte-doc-preview"
            initial={{ opacity: 0, y: 14, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            className="absolute left-0 z-30 w-64 rounded-md border border-violet-600 bg-[#140a1c] px-3 py-2.5 text-left shadow-[0_16px_36px_rgba(0,0,0,0.45)] pointer-events-none"
            style={{ bottom: 'calc(100% + 8px)' }}
          >
            {typeLabel ? (
              <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold ${typeTone}`}>{typeLabel}</span>
            ) : null}
            {rows.length ? (
              <div className="mt-2 flex flex-col gap-1">
                {rows.map((row) => (
                  <div key={row.label} className="flex items-baseline justify-between gap-3">
                    <span className="text-[10px] text-violet-200">{row.label}</span>
                    <span className="text-[11px] font-medium text-white tabular-nums">{row.value}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </span>
  );
}

function AkteOoChatFlip({
  closed,
  onToggle,
}: {
  closed: boolean;
  onToggle: () => void;
}) {
  const face = `${AKTE_VIOLET_BTN} absolute inset-0 h-full w-full whitespace-nowrap [backface-visibility:hidden]`;
  return (
    <button
      type="button"
      data-testid="portal-akte-oo-chat-flip"
      aria-label={closed ? 'Arko: off' : 'Arko: ON'}
      onClick={onToggle}
      className="relative h-7 w-[5.75rem] shrink-0 [perspective:280px]"
    >
      <span
        className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: closed ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className={face}>Arko: ON</span>
        <span className={face} style={{ transform: 'rotateX(180deg)' }}>Arko: off</span>
      </span>
    </button>
  );
}

function ChecklistRowModes({
  open,
  favorite,
  onDelete,
  onEdit,
  onFavorite,
}: {
  open: boolean;
  favorite: boolean;
  onDelete: () => void;
  onEdit: () => void;
  onFavorite: () => void;
}) {
  const mode =
    'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55';
  return (
    <div
      data-testid="portal-akte-checklist-modes"
      className={`relative h-7 shrink-0 overflow-hidden [perspective:280px] transition-[width] duration-500 ${open ? 'w-[5.75rem]' : 'w-7'}`}
    >
      <div
        className="relative h-7 w-[5.75rem] transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className="absolute left-0 top-0 h-7 w-7 rounded-md border border-violet-400/40 bg-violet-600/35 [backface-visibility:hidden]" />
        <span
          className="absolute left-0 top-0 flex h-7 w-[5.75rem] items-center gap-1 [backface-visibility:hidden]"
          style={{ transform: 'rotateX(180deg)' }}
        >
          <button type="button" data-testid="portal-akte-checklist-delete" title="Löschen" aria-label="Löschen" onClick={onDelete} className={mode}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
          <button type="button" data-testid="portal-akte-checklist-edit" title="Bearbeiten" aria-label="Bearbeiten" onClick={onEdit} className={mode}>
            <Pencil className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            data-testid="portal-akte-checklist-favorite"
            title="Favorit"
            aria-label="Favorit"
            onClick={onFavorite}
            className={favorite ? `${mode} bg-violet-600 text-white` : mode}
          >
            <Star className={`h-4 w-4 ${favorite ? 'fill-current' : ''}`} aria-hidden />
          </button>
        </span>
      </div>
    </div>
  );
}

export const DokumenteView: React.FC<DokumenteViewProps> = ({
  customerId,
  kanzleiId,
  portalRole,
  isPrimary,
  kanzleiName,
  customerStatus,
  customerEmail,
  isPremium,
  canWriteDocuments = true,
  protokolle,
  protokolleLoading,
  protokolleError,
  recentDocs,
  initialFolder,
  onCurrentFolderChange,
  onRefresh,
  onDocumentIngested,
  onDocumentRenamed,
  onOpenDocument,
  onOpenAkteOnlyoffice,
  onCloseAkteOnlyoffice,
  akteOnlyoffice = null,
  akteOfficeDocs = [],
  akteOfficeActiveId = '',
  onSelectAkteOfficeDoc,
  aktenHomeTick = 0,
  onRouteAudioToDiktat,
  onRecentDocsChange,
  highlightRecordId,
  onHighlightClear,
  authToken,
  completedByEmail,
  boundAkteId = null,
  onBoundAkteChange,
  onAktenListChange,
  onOpenAkte,
  contractGeneratorAvailable = false,
  hubMode = 'documents',
  onGoToAkten,
  matrixRoomId = '',
  elementSeat = false,
  seatMxid = '',
  arkoBooked = false,
  onBindAkteForArko,
  onRahmenActiveChange,
  seedAkten,
  akteSaveFocus = null,
}) => {
  const neuShortcut = usePortalShortcutAria('neu');
  const ncShareAllowedHosts = useMemo(() => getPortalNextcloudShareAllowedHosts(), []);
  const canPortalDocumentUpload = Boolean(PORTAL_DOCUMENT_UPLOAD_URL) && canWriteDocuments;
  const canPortalNcShareImport = Boolean(
    PORTAL_NEXTCLOUD_SHARE_IMPORT_URL && ncShareAllowedHosts.size > 0 && canWriteDocuments,
  );

  const [ingestTab, setIngestTab] = useState<'upload' | 'nextcloud'>('upload');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [ncShareUrlInput, setNcShareUrlInput] = useState('');
  const [ncShareBusy, setNcShareBusy] = useState(false);
  const [ingestMsg, setIngestMsg] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState<'alle' | 'word' | 'pdf' | 'protokolle' | 'archiv'>('alle');
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(() =>
    sanitizePortalFolderInput(initialFolder || ''),
  );
  const [newFolderName, setNewFolderName] = useState('');
  const [folderBusy, setFolderBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [virtualFolders, setVirtualFolders] = useState<string[]>(() =>
    loadPortalVirtualFolders(customerId),
  );
  const [ncFolders, setNcFolders] = useState<string[]>([]);
  const [akteNcFolders, setAkteNcFolders] = useState<string[]>([]);
  const [akteExplorerFolder, setAkteExplorerFolder] = useState('');
  const [ncFoldersLoading, setNcFoldersLoading] = useState(false);
  const [draggingDocId, setDraggingDocId] = useState<string | null>(null);
  const [dropTargetFolder, setDropTargetFolder] = useState<string | null>(null);
  const [movingDocId, setMovingDocId] = useState<string | null>(null);
  const [folderUploadBusy, setFolderUploadBusy] = useState(false);
  const [folderUploadProgress, setFolderUploadProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const isAktenHub = hubMode === 'akten';
  const [portalAkten, setPortalAkten] = useState<PortalAkteRecord[]>(() =>
    hubMode === 'akten' && seedAkten?.length ? [...seedAkten] : [],
  );
  const [akteListQuery, setAkteListQuery] = useState('');
  const [expandedAkteId, setExpandedAkteId] = useState<string | null>(null);
  const [lageSeatLabels, setLageSeatLabels] = useState<string[]>([]);
  const [lageChecklistStand, setLageChecklistStand] = useState('');
  const [storedChecklist, setStoredChecklist] = useState<PortalAktenChecklistRecord[]>([]);
  const [customChecklistName, setCustomChecklistName] = useState('');
  const [checklistBusy, setChecklistBusy] = useState('');
  const [checklistMenuId, setChecklistMenuId] = useState<string | null>(null);
  const [checklistUndoByAkte, setChecklistUndoByAkte] = useState<Record<string, ChecklistUndoStep[]>>({});
  const [checklistRenameId, setChecklistRenameId] = useState<string | null>(null);
  const [checklistRenameDraft, setChecklistRenameDraft] = useState('');
  const [ooPlusOpen, setOoPlusOpen] = useState(false);
  const [ooPlusAkteQuery, setOoPlusAkteQuery] = useState('');
  const [akteDocListQuery, setAkteDocListQuery] = useState('');
  const [akteFristen, setAkteFristen] = useState<AkteFristRef[]>([]);
  useEffect(() => {
    if (!isAktenHub || !authToken?.trim() || !customerId) {
      setAkteFristen([]);
      return;
    }
    let cancel = false;
    void listPortalFristenForAkte(customerId, authToken).then((rows) => {
      if (cancel) return;
      const next: AkteFristRef[] = [];
      for (const row of rows) {
        const akteId = row.akte_id?.trim();
        const name = row.frist_typ?.trim();
        const datum = (row.regel_id === PORTAL_EIGEN_FRIST_REGEL_ID
          ? row.start_datum
          : row.ende_datum || row.start_datum
        )?.trim();
        if (!akteId || !name || !datum) continue;
        const prepared = eigenFristIsPrepared(row.kalender_trace);
        next.push({ akteId, name, datum, scharf: !prepared });
      }
      setAkteFristen(next);
    });
    return () => {
      cancel = true;
    };
  }, [authToken, customerId, isAktenHub]);
  useEffect(() => {
    if (!expandedAkteId || !authToken?.trim() || !kanzleiId?.trim()) {
      setLageSeatLabels([]);
      return;
    }
    let cancel = false;
    const akteId = expandedAkteId;
    void Promise.all([
      listPortalAkteMembers(authToken, akteId, kanzleiId),
      listKanzleiSeatsForAktePicker(authToken, kanzleiId),
    ]).then(([members, seats]) => {
      if (cancel) return;
      const byId = new Map(seats.map((seat) => [seat.id, seat]));
      const leadId = portalAkten.find((row) => row.id === akteId)?.aktenfuehrer_id;
      setLageSeatLabels(
        members
          .map((member) => {
            const seat = byId.get(member.customer_id);
            const label = seat?.name?.trim() || seat?.email?.trim() || '';
            if (!label) return '';
            return member.customer_id === leadId ? `${label} · Aktenführer` : label;
          })
          .filter(Boolean),
      );
    });
    return () => {
      cancel = true;
    };
  }, [authToken, expandedAkteId, kanzleiId, portalAkten]);
  useEffect(() => {
    if (!expandedAkteId || !authToken?.trim() || !customerId) {
      setLageChecklistStand('');
      return;
    }
    let cancel = false;
    const akteId = expandedAkteId;
    void listChecklistItemsForAkte(customerId, authToken, akteId).then((rows) => {
      if (cancel) return;
      const own = rows.filter((row) => row.akte_id === akteId);
      setLageChecklistStand(
        checklistStand(checklistForAkte(akteId, VERTRAG_BERATUNG_CHECKLIST.items, own)),
      );
    });
    return () => {
      cancel = true;
    };
  }, [authToken, customerId, expandedAkteId]);
  const akteListExtras = useMemo(() => {
    const extras: Record<string, { documentNames: string[]; fristNames: string[] }> = {};
    for (const akte of portalAkten) {
      extras[akte.id] = {
        documentNames: protokolle
          .filter(
            (doc) =>
              (doc.akte_id?.trim() || '') === akte.id || portalDokumentBelongsToAkte(doc, akte),
          )
          .map((doc) => doc.filename)
          .filter(Boolean),
        fristNames: akteFristen
          .filter((frist) => (frist.akteId || '').trim() === akte.id)
          .flatMap((frist) => [frist.name || '', formatAkteDay(frist.datum)].filter(Boolean)),
      };
    }
    return extras;
  }, [akteFristen, portalAkten, protokolle]);
  const listedAkten = useMemo(
    () => filterPortalAkteList(portalAkten, akteListQuery, akteListExtras),
    [akteListExtras, akteListQuery, portalAkten],
  );
  const [memberAkteIds, setMemberAkteIds] = useState<Set<string> | null>(null);
  const [aktenListLoaded, setAktenListLoaded] = useState(
    () => hubMode === 'akten' && Boolean(seedAkten && seedAkten.length > 0),
  );
  const [focusedAkte, setFocusedAkte] = useState<PortalAkteRecord | null>(null);
  const [focusedTile, setFocusedTile] = useState<DokumenteTileId>(isAktenHub ? 'cases' : 'documents');
  const [akteHubTab, setAkteHubTab] = useState<PortalAkteHubTab>('dokumente');
  const [rahmenOpen, setRahmenOpen] = useState(false);
  const [rahmenSection, setRahmenSection] = useState<PortalAkteRahmenSectionId>('verlauf');
  const [akteOoEditorPct, setAkteOoEditorPct] = useState(62);
  const [akteOoChatClosed, setAkteOoChatClosed] = useState(false);
  const akteOoSplitRef = useRef<HTMLDivElement | null>(null);
  const [contractGeneratorOpen, setContractGeneratorOpen] = useState(false);
  const [tileDropTarget, setTileDropTarget] = useState<DokumenteTileId | ''>('');
  const [akteDropTargetId, setAkteDropTargetId] = useState<string | null>(null);
  const [movePopover, setMovePopover] = useState<{
    docId: string;
    left: number;
    top: number;
  } | null>(null);
  const [moveTargetAkteId, setMoveTargetAkteId] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  const movePopoverRef = useRef<HTMLDivElement | null>(null);
  const moveAnchorRef = useRef<HTMLElement | null>(null);
  const closeMovePopover = useCallback(() => {
    moveAnchorRef.current = null;
    setMovePopover(null);
    setMoveError(null);
    setMoveTargetAkteId('');
  }, []);
  usePopoverDismissOutside({
    open: Boolean(movePopover),
    onClose: closeMovePopover,
    rootRef: movePopoverRef,
    ignoreCloseRef: moveAnchorRef,
    closeOnScroll: false,
  });
  const [rowContextMenu, setRowContextMenu] = useState<{
    docId: string;
    x: number;
    y: number;
  } | null>(null);
  const [uploadAkteId, setUploadAkteId] = useState('');
  const [createAkteRequestId, setCreateAkteRequestId] = useState(0);
  const [pbFolders, setPbFolders] = useState<PortalFolderRecord[]>([]);
  const [docShares, setDocShares] = useState<PortalDocumentShare[]>([]);
  const [kanzleiSeats, setKanzleiSeats] = useState<KanzleiSeatForAktePicker[]>([]);
  const [sharePopover, setSharePopover] = useState<{
    docId: string;
    left: number;
    top: number;
  } | null>(null);
  const shareAnchorRef = useRef<HTMLElement | null>(null);
  const closeSharePopover = useCallback(() => {
    shareAnchorRef.current = null;
    setSharePopover(null);
  }, []);
  const [favorites, setFavorites] = useState<PortalDocShortcut[]>(() =>
    loadPortalDocFavorites(customerId),
  );
  const [pins, setPins] = useState<PortalDocShortcut[]>(() => loadPortalDocPins(customerId));
  const [renameAkteId, setRenameAkteId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameDocId, setRenameDocId] = useState<string | null>(null);
  const [renameDocDraft, setRenameDocDraft] = useState('');
  const [openAfterSave, setOpenAfterSave] = useState(() => loadPortalUploadOpenAfterSave(customerId));
  const [akteBrowseId, setAkteBrowseId] = useState<string | null>(() => boundAkteId ?? null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const akteHubFileInputRef = useRef<HTMLInputElement>(null);
  const akteOoPlusFileRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const manualRefreshBusyRef = useRef(false);
  const visibleAkteIds = useMemo(() => new Set(portalAkten.map((row) => row.id)), [portalAkten]);
  const handleAktenChange = useCallback((akten: PortalAkteRecord[]) => {
    // Leere Zwischenmeldung der Panel-Liste darf eine bereits geseedete/geladene
    // Liste nicht wegwischen — sonst landet Restore auf der Fallliste.
    setPortalAkten((prev) => (akten.length === 0 && prev.length > 0 ? prev : akten));
    setAktenListLoaded(true);
  }, []);
  const handleMemberAkteIdsChange = useCallback((ids: string[] | null) => {
    setMemberAkteIds(ids ? new Set(ids) : null);
  }, []);

  useEffect(() => {
    setVirtualFolders(loadPortalVirtualFolders(customerId));
    setPins(loadPortalDocPins(customerId));
    setOpenAfterSave(loadPortalUploadOpenAfterSave(customerId));
  }, [customerId]);

  useEffect(() => {
    savePortalVirtualFolders(customerId, virtualFolders);
  }, [customerId, virtualFolders]);

  useEffect(() => {
    savePortalDocPins(customerId, pins);
  }, [customerId, pins]);

  useEffect(() => {
    const id = boundAkteId?.trim();
    if (!id) return;
    const row = portalAkten.find((akte) => akte.id === id);
    if (!row) return;
    setFocusedAkte((prev) => (prev?.id === row.id && prev.title === row.title && prev.nc_path === row.nc_path ? prev : row));
    setAkteBrowseId((prev) => (prev === row.id ? prev : row.id));
    if (isAktenHub) setFocusedTile('cases');
  }, [boundAkteId, isAktenHub, portalAkten]);

  useEffect(() => {
    if (isAktenHub) return;
    const token = authToken?.trim();
    if (!token || !customerId) return;
    let cancelled = false;
    void listPortalAktenForCustomer(customerId, token, kanzleiId)
      .then((list) => {
        if (!cancelled) setPortalAkten(list);
      })
      .catch(() => {
        /* Ablage bleibt ohne Aktenliste nutzbar */
      });
    return () => {
      cancelled = true;
    };
  }, [authToken, customerId, isAktenHub, kanzleiId]);

  const refreshSitzePrivacy = useCallback(async () => {
    const token = authToken?.trim();
    if (!token) {
      setPbFolders([]);
      setDocShares([]);
      setKanzleiSeats([]);
      return;
    }
    const [folders, shares, seats] = await Promise.all([
      listPortalFolders(token),
      listPortalDocumentShares(token),
      kanzleiId ? listKanzleiSeatsForAktePicker(token, kanzleiId) : Promise.resolve([]),
    ]);
    setPbFolders(folders);
    setDocShares(shares);
    setKanzleiSeats(seats);
  }, [authToken, kanzleiId]);

  useEffect(() => {
    void refreshSitzePrivacy();
  }, [customerId, refreshSitzePrivacy]);

  useEffect(() => {
    if (!rowContextMenu) return;
    const close = () => setRowContextMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [rowContextMenu]);

  useEffect(() => {
    onAktenListChange?.(portalAkten);
  }, [portalAkten, onAktenListChange]);

  useEffect(() => {
    onCurrentFolderChange?.(currentFolder);
  }, [currentFolder, onCurrentFolderChange]);

  type DocFilterKey = typeof groupFilter;

  const DOC_FILTER_CHIPS: { id: DocFilterKey; label: string }[] = [
    { id: 'alle', label: 'Alle' },
    { id: 'word', label: 'Word' },
    { id: 'pdf', label: 'PDF' },
    { id: 'protokolle', label: 'Protokolle' },
    { id: 'archiv', label: 'Archiv' },
  ];

  const matchesDocFilter = useCallback((p: PortalProtokoll): boolean => {
    if (groupFilter === 'alle') return true;
    const group = classifyPortalDocumentGroup(p.nextcloud_path, p.filename);
    const ext =
      fileExtensionFromName(p.filename) ||
      fileExtensionFromName(p.nextcloud_path || '') ||
      fileExtensionFromName(p.nc_url || '');
    if (groupFilter === 'word') {
      return ext === 'doc' || ext === 'docx' || ext === 'odt';
    }
    if (groupFilter === 'pdf') {
      return isPortalPdfDocument(p.filename, p.nextcloud_path, p.nc_url);
    }
    if (groupFilter === 'protokolle') return group === 'other';
    if (groupFilter === 'archiv') return group === 'contracts-final';
    return true;
  }, [groupFilter]);

  const filteredProtokolle = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return protokolle.filter((p) => {
      if (!matchesDocFilter(p)) return false;
      if (!q) return true;
      return (
        p.filename.toLowerCase().includes(q) ||
        portalDocumentRelativeFolder(p.nextcloud_path).toLowerCase().includes(q)
      );
    });
  }, [matchesDocFilter, protokolle, searchQuery]);

  const knownFolders = useMemo(() => {
    const folders = new Set<string>();
    const addWithParents = (raw: string) => {
      const clean = sanitizePortalFolderInput(raw);
      if (!clean) return;
      const parts = clean.split('/');
      let acc = '';
      for (const part of parts) {
        acc = acc ? `${acc}/${part}` : part;
        folders.add(acc);
      }
    };
    for (const doc of protokolle) addWithParents(portalDocumentRelativeFolder(doc.nextcloud_path));
    for (const folder of virtualFolders) addWithParents(folder);
    for (const folder of pbFolders) addWithParents(portalFolderRelativeFromNcPath(folder.nc_path));
    for (const folder of ncFolders) {
      const akteId = folder.replace(/\\/g, '/').match(/(?:^|\/)akten\/([a-zA-Z0-9]{10,20})(?:\/|$)/)?.[1];
      if (aktenListLoaded && akteId && !visibleAkteIds.has(akteId)) continue;
      addWithParents(folder);
    }
    if (currentFolder) addWithParents(currentFolder);
    return Array.from(folders)
      .filter((folder) => !portalFolderPathContainsSmokeFixture(folder))
      .sort((a, b) => a.localeCompare(b, 'de'));
  }, [aktenListLoaded, currentFolder, ncFolders, pbFolders, protokolle, virtualFolders, visibleAkteIds]);

  const callPortalFolderApi = useCallback(
    async (
      action: 'create' | 'share' | 'move' | 'copy' | 'list' | 'rename',
      folderPath: string,
      extras?: {
        pb_record_id?: string;
        sourcePath?: string;
        targetFolderPath?: string;
        newName?: string;
        akte_id?: string;
      },
    ) => {
      // Firma ohne portal-authz: n8n erwartet X-Arkorbit-Customer-Id oder Upload-Secret;
      // Secret-Cutover-FE sendet nur Bearer → 401 + Console-Noise (Auto-list beim Dokumente-Tab).
      if (!portalAuthzSidecarExpected()) {
        throw new Error('Ordner-API: Auf diesem Portal noch nicht freigeschaltet (AuthZ).');
      }
      if (!hasPortalAuthzToken()) {
        throw new Error('Ordner-API: Bitte neu anmelden (kein gültiges Portal-Token).');
      }
      const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_FOLDER_URL);
      const moveAkteId =
        action === 'move'
          ? portalFolderApiAkteId(extras?.targetFolderPath || folderPath, extras?.akte_id)
          : (extras?.akte_id || '');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...portalAuthzHeaders(url),
        },
        credentials: 'omit',
        body: JSON.stringify({
          action,
          folderPath,
          customerId,
          permissions: 19,
          ...getPortalTenantN8nExtras(customerStatus, customerEmail),
          ...extras,
          ...(moveAkteId ? { akte_id: moveAkteId } : {}),
        }),
      });
      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* ignore */
      }
      if (!res.ok || data.ok === false) {
        throw new Error(portalFolderApiErrorMessage(data, res.status));
      }
      if (
        action === 'move' &&
        typeof data.pb_warning === 'string' &&
        data.pb_warning.trim()
      ) {
        throw new Error(`Verschieben: Dateiindex nicht aktualisiert (${data.pb_warning.trim()})`);
      }
      return data;
    },
    [customerId, customerStatus, customerEmail],
  );

  const refreshNcFolders = useCallback(
    async (
      parentFolder: string,
      options?: { silent?: boolean; relativeToAkteId?: string },
    ) => {
      if (!portalAuthzSidecarExpected() || !hasPortalAuthzToken()) {
        setNcFolders([]);
        setAkteNcFolders([]);
        return;
      }
      if (!options?.silent) setNcFoldersLoading(true);
      try {
        const data = await callPortalFolderApi('list', parentFolder, {
          ...(options?.relativeToAkteId ? { akte_id: options.relativeToAkteId } : {}),
        });
        const listed = Array.isArray(data.folders)
          ? data.folders.filter((f): f is string => typeof f === 'string' && Boolean(f.trim()))
          : [];
        if (options?.relativeToAkteId) {
          setAkteNcFolders(listed);
          return;
        }
        const folders = listed.filter((folder) => !portalFolderPathContainsSmokeFixture(folder));
        setNcFolders(folders);
        if (folders.length) {
          setVirtualFolders((prev) => {
            const merged = new Set(
              [...prev, ...folders].filter((folder) => !portalFolderPathContainsSmokeFixture(folder)),
            );
            return Array.from(merged);
          });
        }
      } catch {
        /* NC-Liste optional — PB/virtuelle Ordner bleiben nutzbar */
      } finally {
        if (!options?.silent) setNcFoldersLoading(false);
      }
    },
    [callPortalFolderApi],
  );

  const createFolder = useCallback(async () => {
    const segment = sanitizePortalFolderInput(newFolderName);
    if (!segment) {
      setIngestMsg('Ordnername ungültig oder leer.');
      return;
    }
    if (portalFolderPathContainsSmokeFixture(segment)) {
      setIngestMsg('Interner Testordner ist kein Ziel.');
      return;
    }
    const targetPath = currentFolder ? `${currentFolder}/${segment}` : segment;
    setFolderBusy(true);
    setIngestMsg(null);
    try {
      await callPortalFolderApi('create', targetPath);
      setNewFolderName('');
      setVirtualFolders((prev) => (prev.includes(targetPath) ? prev : [...prev, targetPath]));
      const token = authToken?.trim();
      const root = portalSeatRootNcPath(kanzleiId || '', customerId);
      if (token && root) {
        await ensurePortalFolderRecord(token, {
          name: segment,
          kanzlei_id: kanzleiId || '',
          customer_id: customerId,
          nc_path: `${root}${targetPath}/`,
        });
        await refreshSitzePrivacy();
      }
      setIngestMsg(`Ordner „${segment}" angelegt`);
      await Promise.resolve(onRefresh());
    } catch (err) {
      setIngestMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setFolderBusy(false);
    }
  }, [newFolderName, currentFolder, callPortalFolderApi, onRefresh, authToken, kanzleiId, customerId, refreshSitzePrivacy]);

  const shareCurrentFolder = useCallback(async () => {
    setShareBusy(true);
    setIngestMsg(null);
    try {
      const data = await callPortalFolderApi('share', currentFolder);
      const link = typeof data.share_url === 'string' ? data.share_url : null;
      if (!link) throw new Error('Kein Freigabe-Link erhalten.');
      setShareLink(link);
      setIngestMsg('Freigabe-Link erstellt — zum Mitbearbeiten teilen.');
    } catch (err) {
      setIngestMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setShareBusy(false);
    }
  }, [currentFolder, callPortalFolderApi]);

  const moveDocumentToFolder = useCallback(
    async (doc: PortalProtokoll, targetFolderPath: string, targetAkteId?: string) => {
      if (!doc.nextcloud_path?.trim()) {
        const error = 'Dokument hat keinen Nextcloud-Pfad — Verschieben nicht möglich.';
        setIngestMsg(error);
        return { ok: false as const, error };
      }
      if (!doc.id.trim() || doc.id.startsWith('pending:')) {
        const error = 'Verschieben braucht einen bestätigten Ablage-Eintrag.';
        setIngestMsg(error);
        return { ok: false as const, error };
      }
      if (portalFolderPathContainsSmokeFixture(targetFolderPath)) {
        const error = 'Interner Testordner ist kein Ziel — Datei bleibt in der Liste.';
        setIngestMsg(error);
        return { ok: false as const, error };
      }
      setMovingDocId(doc.id);
      setIngestMsg(null);
      try {
        const data = await callPortalFolderApi('move', targetFolderPath, {
          pb_record_id: doc.id,
          sourcePath: doc.nextcloud_path,
          targetFolderPath,
          akte_id: portalFolderApiAkteId(targetFolderPath, targetAkteId),
        });
        const nextcloudPath =
          typeof data.nextcloud_path === 'string' ? data.nextcloud_path.trim() : '';
        const responseAkteId =
          typeof data.akte_id === 'string' ? data.akte_id.trim() : '';
        if (!nextcloudPath) {
          throw new Error('Verschieben konnte in der Ablage nicht bestätigt werden.');
        }
        const token = authToken?.trim() || getPortalPbToken()?.trim() || '';
        if (!token) {
          throw new Error('Verschieben: Bitte neu anmelden.');
        }
        const fresh = await fetchPortalProtokollById(
          getPortalPocketBaseUrl(),
          PORTAL_PROTOKOLLE_COLLECTION,
          doc.id,
          token,
        );
        if (
          !fresh ||
          normalizePortalDocumentNcPath(fresh.nextcloud_path) !==
            normalizePortalDocumentNcPath(nextcloudPath) ||
          (fresh.akte_id?.trim() || '') !== responseAkteId
        ) {
          throw new Error(
            'Verschieben wurde nicht als Erfolg bestätigt — Dokumentliste bitte neu laden.',
          );
        }
        await Promise.resolve(onRefresh());
        setIngestMsg(`„${doc.filename}" → ${portalFolderDisplayPath(targetFolderPath)}`);
        return {
          ok: true as const,
          nextcloudPath,
          record: fresh,
        };
      } catch (err) {
        const error = err instanceof Error ? err.message : String(err);
        setIngestMsg(error);
        return { ok: false as const, error };
      } finally {
        setMovingDocId(null);
        setDraggingDocId(null);
        setDropTargetFolder(null);
      }
    },
    [authToken, callPortalFolderApi, onRefresh],
  );

  useEffect(() => {
    if (!highlightRecordId) return;
    const el = rowRefs.current[highlightRecordId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const timer = window.setTimeout(() => onHighlightClear?.(), 6000);
    return () => window.clearTimeout(timer);
  }, [highlightRecordId, protokolle, onHighlightClear]);

  const documentGroups = useMemo(() => {
    type WorkingGroup = {
      key: string;
      title: string;
      akte: PortalAkteRecord | null;
      documents: PortalProtokoll[];
    };
    const bySpecificity = [...portalAkten].sort(
      (a, b) => (b.nc_path?.length ?? 0) - (a.nc_path?.length ?? 0),
    );
    const sorted = [...filteredProtokolle].sort(
      (a, b) =>
        new Date(b.updated || b.created).getTime() -
        new Date(a.updated || a.created).getTime(),
    );
    const groups = new Map<string, WorkingGroup>();

    for (const doc of sorted) {
      const matchingAkte =
        (doc.akte_id
          ? bySpecificity.find((candidate) => candidate.id === doc.akte_id)
          : undefined) ??
        bySpecificity.find((candidate) => portalDokumentBelongsToAkte(doc, candidate)) ??
        null;
      const key = matchingAkte ? `akte:${matchingAkte.id}` : 'unassigned';
      const group = groups.get(key) ?? {
        key,
        title: matchingAkte?.title || 'Ohne Aktenzuordnung',
        akte: matchingAkte,
        documents: [],
      };
      group.documents.push(doc);
      groups.set(key, group);
    }

    return Array.from(groups.values())
      .map((group) => {
        const folders = new Map<string, PortalProtokoll[]>();
        for (const doc of group.documents) {
          const folder = portalDocumentRelativeFolder(doc.nextcloud_path);
          const rows = folders.get(folder) ?? [];
          rows.push(doc);
          folders.set(folder, rows);
        }
        return {
          ...group,
          newestAt: new Date(
            group.documents[0]?.updated || group.documents[0]?.created || 0,
          ).getTime(),
          folders: Array.from(folders.entries())
            .map(([path, documents]) => ({
              path,
              label: path ? portalFolderDisplayPath(path) : 'Ohne Ordner',
              documents,
              newestAt: new Date(
                documents[0]?.updated || documents[0]?.created || 0,
              ).getTime(),
            }))
            .sort((a, b) => b.newestAt - a.newestAt),
        };
      })
      .sort((a, b) => b.newestAt - a.newestAt);
  }, [filteredProtokolle, portalAkten]);

  const browseAkte = useMemo(
    () =>
      resolveVisiblePortalAkteForHub(
        portalAkten,
        akteBrowseId || focusedAkte?.id || boundAkteId,
      ),
    [akteBrowseId, boundAkteId, focusedAkte?.id, portalAkten],
  );

  // AKTE_HUB_CLICK_NOT_CHAT: kein Fallback auf eine nur lokal/sessionseitig
  // bekannte Akte. Erst die sichtbare PB-/Kreis-Liste schaltet Hub-Daten frei.
  const activeAkte = browseAkte;
  useEffect(() => {
    const memory = recallAkteOoSplit(activeAkte?.id || '');
    setAkteOoEditorPct(memory.editorPct);
    setAkteOoChatClosed(memory.chatClosed);
  }, [activeAkte?.id]);
  const commitAkteOoSplit = useCallback((editorPct: number, chatClosed: boolean) => {
    const pct = clampAkteOoEditorPct(editorPct);
    setAkteOoEditorPct(pct);
    setAkteOoChatClosed(chatClosed);
    const akteId = activeAkte?.id || '';
    if (akteId) rememberAkteOoSplit(akteId, { editorPct: pct, chatClosed });
    window.dispatchEvent(new Event('resize'));
  }, [activeAkte?.id]);
  const onAkteOoSplitPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const root = akteOoSplitRef.current;
    const handle = event.currentTarget;
    if (!root) return;
    event.preventDefault();
    handle.setPointerCapture(event.pointerId);
    const widthAt = (clientX: number) => {
      const rect = root.getBoundingClientRect();
      if (rect.width <= 0) return akteOoEditorPct;
      return clampAkteOoEditorPct(((clientX - rect.left) / rect.width) * 100);
    };
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== event.pointerId) return;
      setAkteOoEditorPct(widthAt(ev.clientX));
    };
    const up = (ev: PointerEvent) => {
      if (ev.pointerId !== event.pointerId) return;
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      commitAkteOoSplit(widthAt(ev.clientX), false);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
  }, [akteOoEditorPct, commitAkteOoSplit]);
  const activeAkteFristenPath = useMemo(
    () => portalAkteHubFristenPath(activeAkte),
    [activeAkte],
  );

  const rahmenSections = useMemo(
    () => portalAkteRahmenSections(arkoBooked),
    [arkoBooked],
  );

  useEffect(() => {
    onRahmenActiveChange?.(Boolean(isAktenHub && rahmenOpen));
    return () => onRahmenActiveChange?.(false);
  }, [isAktenHub, onRahmenActiveChange, rahmenOpen]);

  useEffect(() => {
    if (rahmenSection === 'arko' && !arkoBooked) setRahmenSection('verlauf');
  }, [arkoBooked, rahmenSection]);
  const aktenHomeSeen = useRef(aktenHomeTick);
  useEffect(() => {
    if (!aktenHomeTick || aktenHomeSeen.current === aktenHomeTick) return;
    aktenHomeSeen.current = aktenHomeTick;
    rememberPortalAkteRahmenOpen(null);
    setRahmenOpen(false);
    onCloseAkteOnlyoffice?.();
  }, [aktenHomeTick, onCloseAkteOnlyoffice]);

  const selectRahmenSection = useCallback((id: PortalAkteRahmenSectionId) => {
    const akteId = activeAkte?.id || '';
    setRahmenSection(id);
    if (id === 'arko' && activeAkte && arkoBooked) onBindAkteForArko?.(activeAkte);
    rememberPortalAkteRahmen(akteId, id, '', {
      docId: id === 'onlyoffice' ? akteOfficeActiveId : '',
      chatId: id === 'arko' ? peekAkteChatId(customerId, akteId) : '',
    });
  }, [activeAkte, akteOfficeActiveId, arkoBooked, customerId, onBindAkteForArko]);

  const verlaufAnchors = useMemo(() => {
    if (!activeAkte?.id) return [];
    const recent = loadRecentPortalDocuments(customerId, activeAkte.id)[0];
    return buildAkteVerlaufAnchors({
      akteId: activeAkte.id,
      today: akteLocalToday(),
      fristen: akteFristen,
      lastDocument: recent?.display_name?.trim() && recent.document_url?.trim()
        ? {
            id: recent.pb_record_id || '',
            title: recent.display_name.trim(),
            akteId: activeAkte.id,
          }
        : null,
      chatId: arkoBooked ? peekAkteChatId(customerId, activeAkte.id) : '',
      arkoBooked,
    });
  }, [activeAkte?.id, akteFristen, akteOfficeActiveId, arkoBooked, customerId]);

  const reloadAkteChecklist = useCallback(async (akteId: string) => {
    if (!akteId || !authToken?.trim() || !customerId) {
      setStoredChecklist([]);
      return;
    }
    const rows = await listChecklistItemsForAkte(customerId, authToken, akteId);
    setStoredChecklist(rows.filter((row) => row.akte_id === akteId));
  }, [authToken, customerId]);

  useEffect(() => {
    if (!activeAkte?.id) {
      setStoredChecklist([]);
      return;
    }
    void reloadAkteChecklist(activeAkte.id);
  }, [activeAkte?.id, reloadAkteChecklist]);

  const checklistView = useMemo(
    () => checklistForAkte(activeAkte?.id || '', VERTRAG_BERATUNG_CHECKLIST.items, storedChecklist),
    [activeAkte?.id, storedChecklist],
  );
  const checklistUndoCount = (checklistUndoByAkte[activeAkte?.id || ''] ?? []).length;

  const rememberChecklistUndo = useCallback((akteId: string, step: ChecklistUndoStep) => {
    setChecklistUndoByAkte((prev) => ({
      ...prev,
      [akteId]: pushChecklistUndo(prev[akteId] ?? [], step),
    }));
  }, []);

  const toggleChecklistPoint = useCallback(async (itemId: string) => {
    const akteId = activeAkte?.id || '';
    const point = checklistView.find((row) => row.itemId === itemId);
    if (!akteId || !point || !authToken?.trim() || checklistBusy) return;
    const nextStatus = point.status === 'done' ? 'open' : 'done';
    setChecklistBusy(itemId);
    try {
      if (point.recordId) {
        const updated = await updateChecklistItemStatus(authToken, point.recordId, nextStatus, customerEmail);
        if (updated.ok === false || updated.data.akte_id !== akteId) return;
      } else {
        const created = await createChecklistItemForAkte(customerId, authToken, {
          akteId,
          itemId: point.itemId,
          label: point.label,
          required: point.required,
          sortOrder: point.order,
          status: nextStatus,
        });
        if (created.ok === false || created.data.akte_id !== akteId) return;
      }
      await reloadAkteChecklist(akteId);
    } finally {
      setChecklistBusy('');
    }
  }, [activeAkte?.id, authToken, checklistBusy, checklistView, customerEmail, customerId, reloadAkteChecklist]);

  const addCustomChecklistPoint = useCallback(async () => {
    const akteId = activeAkte?.id || '';
    const plan = planCustomChecklistPoint(customChecklistName);
    if (!akteId || plan.ok === false || !authToken?.trim() || checklistBusy) return;
    setChecklistBusy('custom');
    try {
      const created = await addChecklistItemToAkte(customerId, authToken, akteId, plan.label, false);
      if (created.ok === false || created.data.akte_id !== akteId) return;
      rememberChecklistUndo(akteId, { kind: 'add', itemId: created.data.item_id });
      setCustomChecklistName('');
      await reloadAkteChecklist(akteId);
    } finally {
      setChecklistBusy('');
    }
  }, [activeAkte?.id, authToken, checklistBusy, customChecklistName, customerId, reloadAkteChecklist, rememberChecklistUndo]);

  const renameChecklistPoint = useCallback(async (itemId: string, name: string) => {
    const akteId = activeAkte?.id || '';
    const point = checklistView.find((row) => row.itemId === itemId);
    const plan = planCustomChecklistPoint(name);
    if (!akteId || !point || plan.ok === false || !authToken?.trim() || checklistBusy) return;
    if (plan.label === point.label) {
      setChecklistRenameId(null);
      return;
    }
    setChecklistBusy(itemId);
    try {
      if (point.recordId) {
        const updated = await updateChecklistItemLabel(authToken, point.recordId, plan.label);
        if (updated.ok === false || updated.data.akte_id !== akteId) return;
      } else {
        const created = await createChecklistItemForAkte(customerId, authToken, {
          akteId,
          itemId: point.itemId,
          label: plan.label,
          required: point.required,
          sortOrder: point.order,
          status: point.status === 'done' ? 'done' : 'open',
        });
        if (created.ok === false || created.data.akte_id !== akteId) return;
      }
      rememberChecklistUndo(akteId, { kind: 'rename', itemId: point.itemId, previousLabel: point.label });
      setChecklistRenameId(null);
      await reloadAkteChecklist(akteId);
    } finally {
      setChecklistBusy('');
    }
  }, [activeAkte?.id, authToken, checklistBusy, checklistView, customerId, reloadAkteChecklist, rememberChecklistUndo]);

  const deleteChecklistPoint = useCallback(async (itemId: string) => {
    const akteId = activeAkte?.id || '';
    const point = checklistView.find((row) => row.itemId === itemId);
    if (!akteId || !point || !authToken?.trim() || checklistBusy) return;
    setChecklistBusy(itemId);
    try {
      if (point.custom) {
        if (!point.recordId) return;
        const removed = await removeChecklistItemFromAkte(authToken, point.recordId);
        if (removed.ok === false) return;
      } else if (point.recordId) {
        const hidden = await updateChecklistItemNote(authToken, point.recordId, CHECKLIST_HIDDEN_NOTE);
        if (hidden.ok === false || hidden.data.akte_id !== akteId) return;
      } else {
        const created = await createChecklistItemForAkte(customerId, authToken, {
          akteId,
          itemId: point.itemId,
          label: point.label,
          required: point.required,
          sortOrder: point.order,
          status: 'open',
          note: CHECKLIST_HIDDEN_NOTE,
        });
        if (created.ok === false || created.data.akte_id !== akteId) return;
      }
      rememberChecklistUndo(akteId, {
        kind: 'delete',
        itemId: point.itemId,
        label: point.label,
        favorite: point.favorite,
        custom: point.custom,
        required: point.required,
        order: point.order,
        status: point.status,
      });
      setChecklistMenuId(null);
      await reloadAkteChecklist(akteId);
    } finally {
      setChecklistBusy('');
    }
  }, [activeAkte?.id, authToken, checklistBusy, checklistView, customerId, reloadAkteChecklist, rememberChecklistUndo]);

  const favoriteChecklistPoint = useCallback(async (itemId: string) => {
    const akteId = activeAkte?.id || '';
    const point = checklistView.find((row) => row.itemId === itemId);
    if (!akteId || !point || !authToken?.trim() || checklistBusy) return;
    const nextNote = point.favorite ? '' : CHECKLIST_FAVORITE_NOTE;
    setChecklistMenuId(null);
    setChecklistBusy(itemId);
    try {
      if (point.recordId) {
        const updated = await updateChecklistItemNote(authToken, point.recordId, nextNote);
        if (updated.ok === false || updated.data.akte_id !== akteId) return;
      } else if (nextNote) {
        const created = await createChecklistItemForAkte(customerId, authToken, {
          akteId,
          itemId: point.itemId,
          label: point.label,
          required: point.required,
          sortOrder: point.order,
          status: point.status === 'done' ? 'done' : 'open',
          note: nextNote,
        });
        if (created.ok === false || created.data.akte_id !== akteId) return;
      } else {
        return;
      }
      rememberChecklistUndo(akteId, {
        kind: 'favorite',
        itemId: point.itemId,
        previousFavorite: point.favorite,
      });
      await reloadAkteChecklist(akteId);
    } finally {
      setChecklistBusy('');
    }
  }, [activeAkte?.id, authToken, checklistBusy, checklistView, customerId, reloadAkteChecklist, rememberChecklistUndo]);

  const undoChecklistStep = useCallback(async () => {
    const akteId = activeAkte?.id || '';
    const stack = checklistUndoByAkte[akteId] ?? [];
    const step = stack[stack.length - 1];
    if (!akteId || !step || !authToken?.trim() || checklistBusy) return;
    setChecklistBusy('undo');
    let done = false;
    try {
      if (step.kind === 'delete') {
        const visible = checklistView.find((row) => row.itemId === step.itemId);
        if (visible) {
          done = true;
        } else if (step.custom) {
          const created = await createChecklistItemForAkte(customerId, authToken, {
            akteId,
            itemId: step.itemId,
            label: step.label,
            required: step.required,
            sortOrder: step.order,
            status: step.status,
            note: step.favorite ? CHECKLIST_FAVORITE_NOTE : '',
          });
          done = created.ok === true && created.data.akte_id === akteId;
        } else {
          const hidden = storedChecklist.find((row) => row.akte_id === akteId && row.item_id === step.itemId);
          if (!hidden?.id) return;
          const updated = await updateChecklistItemNote(
            authToken,
            hidden.id,
            step.favorite ? CHECKLIST_FAVORITE_NOTE : '',
          );
          done = updated.ok === true && updated.data.akte_id === akteId;
        }
      } else if (step.kind === 'rename') {
        const point = checklistView.find((row) => row.itemId === step.itemId);
        if (!point) return;
        if (point.label === step.previousLabel) {
          done = true;
        } else if (point.recordId) {
          const updated = await updateChecklistItemLabel(authToken, point.recordId, step.previousLabel);
          done = updated.ok === true && updated.data.akte_id === akteId;
        } else {
          const created = await createChecklistItemForAkte(customerId, authToken, {
            akteId,
            itemId: point.itemId,
            label: step.previousLabel,
            required: point.required,
            sortOrder: point.order,
            status: point.status === 'done' ? 'done' : 'open',
            note: point.favorite ? CHECKLIST_FAVORITE_NOTE : '',
          });
          done = created.ok === true && created.data.akte_id === akteId;
        }
      } else if (step.kind === 'favorite') {
        const point = checklistView.find((row) => row.itemId === step.itemId);
        if (!point) return;
        if (point.favorite === step.previousFavorite) {
          done = true;
        } else if (point.recordId) {
          const updated = await updateChecklistItemNote(
            authToken,
            point.recordId,
            step.previousFavorite ? CHECKLIST_FAVORITE_NOTE : '',
          );
          done = updated.ok === true && updated.data.akte_id === akteId;
        } else if (step.previousFavorite) {
          const created = await createChecklistItemForAkte(customerId, authToken, {
            akteId,
            itemId: point.itemId,
            label: point.label,
            required: point.required,
            sortOrder: point.order,
            status: point.status === 'done' ? 'done' : 'open',
            note: CHECKLIST_FAVORITE_NOTE,
          });
          done = created.ok === true && created.data.akte_id === akteId;
        } else {
          done = true;
        }
      } else {
        const point = checklistView.find((row) => row.itemId === step.itemId);
        if (!point) {
          done = true;
        } else if (point.recordId) {
          const removed = await removeChecklistItemFromAkte(authToken, point.recordId);
          done = removed.ok === true;
        }
      }
      if (!done) return;
      setChecklistUndoByAkte((prev) => {
        const current = prev[akteId] ?? [];
        if (current[current.length - 1] !== step) return prev;
        return { ...prev, [akteId]: current.slice(0, -1) };
      });
      await reloadAkteChecklist(akteId);
    } finally {
      setChecklistBusy('');
    }
  }, [activeAkte?.id, authToken, checklistBusy, checklistUndoByAkte, checklistView, customerId, reloadAkteChecklist, storedChecklist]);

  useEffect(() => {
    setAkteNcFolders([]);
    setAkteExplorerFolder('');
    setContractGeneratorOpen(false);
    if (activeAkte?.id) setAkteHubTab('beteiligte');
  }, [activeAkte?.id]);

  useEffect(() => {
    const parent =
      isAktenHub && activeAkte?.id
        ? akteExplorerFolder
          ? `${portalAkteTargetFolder(activeAkte.id)}/${akteExplorerFolder}`
          : portalAkteTargetFolder(activeAkte.id)
        : currentFolder;
    void refreshNcFolders(parent, {
      silent: true,
      relativeToAkteId: isAktenHub && activeAkte?.id ? activeAkte.id : undefined,
    });
  }, [activeAkte?.id, akteExplorerFolder, currentFolder, isAktenHub, refreshNcFolders]);
  const folderDocumentGroups = useMemo(() => {
    const sorted = [...filteredProtokolle].sort(
      (a, b) =>
        new Date(b.updated || b.created).getTime() -
        new Date(a.updated || a.created).getTime(),
    );
    const folders = new Map<string, PortalProtokoll[]>();
    for (const doc of sorted) {
      const folder = portalDocumentRelativeFolder(doc.nextcloud_path);
      const rows = folders.get(folder) ?? [];
      rows.push(doc);
      folders.set(folder, rows);
    }
    const folderRows = Array.from(folders.entries())
      .map(([path, documents]) => ({
        path,
        label: path ? portalFolderDisplayPath(path) : 'Ohne Ordner',
        documents,
        newestAt: new Date(documents[0]?.updated || documents[0]?.created || 0).getTime(),
      }))
      .sort((a, b) => b.newestAt - a.newestAt);
    if (!folderRows.length) return [];
    return [
      {
        key: 'all-docs',
        title: 'Dokumente',
        akte: null as PortalAkteRecord | null,
        documents: sorted,
        newestAt: folderRows[0]?.newestAt ?? 0,
        folders: folderRows,
      },
    ];
  }, [filteredProtokolle]);

  const poolDocumentGroups = useMemo(() => {
    if (!isAktenHub) return folderDocumentGroups;
    const unassigned = documentGroups
      .filter((group) => !group.akte)
      .map((group) => ({ ...group, title: 'Zum Zuordnen — ohne Akte' }));
    if (activeAkte) return unassigned;
    return documentGroups;
  }, [activeAkte, documentGroups, folderDocumentGroups, isAktenHub]);

  const focusedAkteDocumentGroup = useMemo(() => {
    if (!activeAkte) return null;
    const existing = documentGroups.find((group) => group.akte?.id === activeAkte.id);
    return (
      existing ?? {
        key: `akte:${activeAkte.id}`,
        title: activeAkte.title,
        akte: activeAkte,
        documents: [] as PortalProtokoll[],
        newestAt: 0,
        folders: [] as { path: string; label: string; documents: PortalProtokoll[]; newestAt: number }[],
      }
    );
  }, [activeAkte, documentGroups]);

  const explorerSearching = Boolean(searchQuery.trim());
  const explorerRoot = '';

  const explorerCwd = useMemo(() => {
    const cwd = sanitizePortalFolderInput(currentFolder);
    if (explorerRoot) {
      if (!cwd || (cwd !== explorerRoot && !cwd.startsWith(`${explorerRoot}/`))) {
        return explorerRoot;
      }
    }
    return cwd;
  }, [currentFolder, explorerRoot]);

  const poolCwd = isAktenHub && activeAkte ? '' : explorerCwd;

  const poolChildFolders = useMemo(
    () => (explorerSearching ? [] : portalExplorerChildFolders(knownFolders, poolCwd)),
    [explorerSearching, knownFolders, poolCwd],
  );

  const poolExplorerDocs = useMemo(() => {
    const docs = poolDocumentGroups.flatMap((group) => group.documents);
    if (explorerSearching) return docs;
    return docs.filter((doc) =>
      portalExplorerDocInCurrentFolder(portalDocumentRelativeFolder(doc.nextcloud_path), poolCwd),
    );
  }, [explorerSearching, poolCwd, poolDocumentGroups]);

  const poolHasRows = poolChildFolders.length > 0 || poolExplorerDocs.length > 0;
  const poolInSubfolder = Boolean(poolCwd);
  const explorerCanGoUp = Boolean(explorerCwd) && explorerCwd !== explorerRoot;

  const akteKnownFolders = useMemo(
    () =>
      collectPortalAkteExplorerFolders({
        akteId: activeAkte?.id || '',
        documents: focusedAkteDocumentGroup?.documents,
        listedFolders: akteNcFolders,
      }),
    [activeAkte?.id, akteNcFolders, focusedAkteDocumentGroup],
  );

  const akteChildFolders = useMemo(
    () =>
      explorerSearching ? [] : portalAkteExplorerChildFolders(akteKnownFolders, akteExplorerFolder),
    [akteExplorerFolder, akteKnownFolders, explorerSearching],
  );

  const akteExplorerDocs = useMemo(() => {
    const docs = focusedAkteDocumentGroup?.documents ?? [];
    if (explorerSearching) return docs;
    return docs.filter((doc) =>
      portalExplorerDocInCurrentFolder(
        portalAkteRelativeDocFolder(doc.nextcloud_path, activeAkte?.id),
        akteExplorerFolder,
      ),
    );
  }, [activeAkte?.id, akteExplorerFolder, explorerSearching, focusedAkteDocumentGroup]);

  const akteOoPlusDocs = useMemo(() => {
    const inAkte: PortalProtokoll[] = [];
    const pool: PortalProtokoll[] = [];
    if (!activeAkte?.id) return { inAkte, pool };
    for (const doc of protokolle) {
      const kind = akteOnlyofficePlusKind(doc, activeAkte);
      if (kind === 'this-akte') inAkte.push(doc);
      else if (kind === 'pool') pool.push(doc);
    }
    return { inAkte, pool };
  }, [activeAkte, protokolle]);

  const akteDocSearchHits = useMemo(() => {
    if (!akteDocListQuery.trim() || !focusedAkteDocumentGroup) return null;
    return filterDocsByFilename(focusedAkteDocumentGroup.documents, akteDocListQuery);
  }, [akteDocListQuery, focusedAkteDocumentGroup]);

  useEffect(() => {
    setOoPlusOpen(false);
    setOoPlusAkteQuery('');
    setAkteDocListQuery('');
  }, [activeAkte?.id]);

  const akteHasRows = akteChildFolders.length > 0 || akteExplorerDocs.length > 0;
  const akteInSubfolder = Boolean(akteExplorerFolder);

  const skipFavoritesSaveRef = useRef(true);
  useEffect(() => {
    skipFavoritesSaveRef.current = true;
    setFavorites(loadPortalDocFavorites(customerId, activeAkte?.id));
  }, [activeAkte?.id, customerId]);

  useEffect(() => {
    if (skipFavoritesSaveRef.current) {
      skipFavoritesSaveRef.current = false;
      return;
    }
    savePortalDocFavorites(customerId, favorites, activeAkte?.id);
  }, [activeAkte?.id, customerId, favorites]);

  const handleSelectedFile = useCallback(
    (file: File | null) => {
      if (!file) {
        setUploadFile(null);
        return;
      }
      if (isAudioFile(file)) {
        onRouteAudioToDiktat(file);
        setIngestMsg(`„${file.name}" → Diktat`);
        setUploadFile(null);
        return;
      }
      const imagePolicyError = portalImageUploadPolicyError({
        name: file.name,
        mimeType: file.type,
      });
      if (imagePolicyError) {
        setUploadFile(null);
        setIngestMsg(imagePolicyError);
        return;
      }
      setUploadFile(file);
      setIngestMsg(null);
    },
    [onRouteAudioToDiktat],
  );

  const handleManualRefresh = useCallback(async () => {
    if (manualRefreshBusyRef.current) return;
    manualRefreshBusyRef.current = true;
    setManualRefreshing(true);
    try {
      await Promise.resolve(onRefresh());
      const parent =
        isAktenHub && activeAkte?.id
          ? akteExplorerFolder
            ? `${portalAkteTargetFolder(activeAkte.id)}/${akteExplorerFolder}`
            : portalAkteTargetFolder(activeAkte.id)
          : currentFolder;
      await refreshNcFolders(parent, {
        relativeToAkteId: isAktenHub && activeAkte?.id ? activeAkte.id : undefined,
      });
    } finally {
      manualRefreshBusyRef.current = false;
      setManualRefreshing(false);
    }
  }, [activeAkte?.id, akteExplorerFolder, currentFolder, isAktenHub, onRefresh, refreshNcFolders]);

  const uploadPortalFileCore = useCallback(
    async (file: File, targetFolderOverride?: string, targetAkteIdOverride?: string) => {
      if (!hasPortalAuthzToken()) {
        throw new Error('Upload: Bitte neu anmelden (kein gültiges Portal-Token).');
      }
      const imagePolicyError = portalImageUploadPolicyError({
        name: file.name,
        mimeType: file.type,
      });
      if (imagePolicyError) throw new Error(imagePolicyError);
      const fd = new FormData();
      fd.append('file', file);
      fd.set('customerId', customerId);
      const folderTarget =
        targetFolderOverride !== undefined
          ? sanitizePortalFolderInput(targetFolderOverride)
          : currentFolder.trim()
            ? sanitizePortalFolderInput(currentFolder)
            : '';
      if (folderTarget) {
        fd.set('folderPath', folderTarget);
      }
      const uploadAkteId = (
        targetAkteIdOverride ||
        akteBrowseId ||
        focusedAkte?.id ||
        ''
      ).trim();
      if (uploadAkteId) {
        fd.set('akte_id', uploadAkteId);
      }
      const tenantExtras = getPortalTenantN8nExtras(customerStatus, customerEmail);
      if (tenantExtras.tenant) {
        fd.set('tenant', tenantExtras.tenant);
      }
      const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
      const res = await fetch(url, {
        method: 'POST',
        body: fd,
        credentials: 'omit',
        headers: {
          ...portalAuthzHeaders(url),
        },
      });
      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* ignore */
      }
      return parsePortalDocumentN8nResponse(data, res);
    },
    [akteBrowseId, customerEmail, customerId, customerStatus, currentFolder, focusedAkte?.id],
  );

  const routeDocumentOpen = useCallback((fields: PortalDocumentOpenFields) => {
    const inAkteFrame = Boolean(isAktenHub && rahmenOpen && activeAkte?.id);
    const decision = decidePortalOnlyofficeSurface({
      entry: inAkteFrame ? 'akte' : 'kanzlei',
      documentAkteId: fields.akte_id,
      openAkteId: inAkteFrame ? activeAkte?.id : null,
    });
    if (decision.ok === false) {
      setIngestMsg(decision.reason);
      return;
    }
    if (decision.surface === 'akte') {
      if (!onOpenAkteOnlyoffice) {
        setIngestMsg('Akten-Dokumente öffnen nur in der Akte.');
        return;
      }
      setRahmenSection('onlyoffice');
      rememberPortalAkteRahmen(activeAkte?.id || '', 'onlyoffice', '', { docId: fields.pb_record_id });
      onOpenAkteOnlyoffice(fields);
      return;
    }
    onOpenDocument(fields);
  }, [activeAkte?.id, isAktenHub, onOpenAkteOnlyoffice, onOpenDocument, rahmenOpen]);

  const ingestPortalFile = useCallback(
    async (
      file: File,
      targetFolderOverride?: string,
      opts?: { openAfterSave?: boolean; expectedAkteId?: string },
    ) => {
      setUploadBusy(true);
      setIngestMsg(null);
      try {
        const expectedAkteId = opts?.expectedAkteId?.trim() || '';
        const fields = await uploadPortalFileCore(
          file,
          targetFolderOverride,
          expectedAkteId || undefined,
        );
        if (expectedAkteId) {
          const pbRecordId = fields.pb_record_id?.trim() ?? '';
          const nextcloudPath = fields.nextcloud_path?.trim() ?? '';
          const token = authToken?.trim() || getPortalPbToken()?.trim() || '';
          if (!pbRecordId || !nextcloudPath || !token) {
            throw new Error(
              'Upload wurde nicht aktgebunden bestätigt — Datei liegt nicht in dieser Akte.',
            );
          }
          const fresh = await fetchPortalProtokollById(
            getPortalPocketBaseUrl(),
            PORTAL_PROTOKOLLE_COLLECTION,
            pbRecordId,
            token,
          );
          if (!fresh || !portalDocumentMovePersistedInAkte(fresh, expectedAkteId, nextcloudPath)) {
            throw new Error(
              'Datei wurde nicht an diese Akte gebunden — kein Erfolg bestätigt.',
            );
          }
          const confirmed = {
            ...portalDocumentOpenFieldsFromProtokoll(fresh),
            display_name: fresh.filename || fields.display_name,
            document_url: fresh.nc_url || fields.document_url,
            akte_id: expectedAkteId,
          };
          onDocumentIngested?.(confirmed);
          let msg = `„${confirmed.display_name}" gespeichert`;
          if (fields.pb_warning) {
            msg += ` — Hinweis: ${fields.pb_warning}`;
          }
          setIngestMsg(msg);
          setUploadFile(null);
          if (groupFilter !== 'alle' && isPortalPdfDocument(confirmed.display_name, confirmed.nextcloud_path, confirmed.document_url)) {
            setGroupFilter('pdf');
          }
          await Promise.resolve(onRefresh());
          onDocumentIngested?.(confirmed);
          const shouldOpen = opts?.openAfterSave ?? openAfterSave;
          if (
            shouldOpen
            && canOpenDocumentInPortal(isPremium, confirmed.display_name, confirmed.nextcloud_path, confirmed.document_url)
          ) {
            routeDocumentOpen(confirmed);
          }
          return confirmed;
        }
        let msg = `„${fields.display_name}" gespeichert`;
        if (!fields.pb_record_id) {
          msg += ' — Index noch nicht verfügbar (Aktualisieren)';
        }
        if (fields.pb_warning) {
          msg += ` — Hinweis: ${fields.pb_warning}`;
        }
        setIngestMsg(msg);
        setUploadFile(null);
        onDocumentIngested?.(fields);
        if (groupFilter !== 'alle' && isPortalPdfDocument(fields.display_name, fields.nextcloud_path, fields.document_url)) {
          setGroupFilter('pdf');
        }
        await Promise.resolve(onRefresh());
        if (!fields.pb_record_id) {
          await new Promise((resolve) => window.setTimeout(resolve, 1200));
          await Promise.resolve(onRefresh());
        }
        const shouldOpen = opts?.openAfterSave ?? openAfterSave;
        if (
          shouldOpen
          && canOpenDocumentInPortal(isPremium, fields.display_name, fields.nextcloud_path, fields.document_url)
        ) {
          routeDocumentOpen(fields);
        }
        return fields;
      } catch (err) {
        setIngestMsg(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setUploadBusy(false);
      }
    },
    [
      authToken,
      uploadPortalFileCore,
      isPremium,
      groupFilter,
      onDocumentIngested,
      routeDocumentOpen,
      onRefresh,
      openAfterSave,
    ],
  );

  const uploadFolderFiles = useCallback(
    async (fileList: FileList | File[] | null, targetFolderBase?: string) => {
      if (!fileList || !hasPortalAuthzToken()) return;
      const files = filterPortalFolderUploadFiles(fileList);
      if (!files.length) {
        setIngestMsg('Keine gültigen Dateien im Ordner gefunden.');
        return;
      }
      const audioFiles = files.filter((f) => isAudioFile(f));
      const docFiles = files.filter((f) => !isAudioFile(f));
      if (audioFiles.length === 1 && docFiles.length === 0) {
        onRouteAudioToDiktat(audioFiles[0]);
        setIngestMsg(`„${audioFiles[0].name}" → Diktat`);
        return;
      }
      const uploadBase = targetFolderBase !== undefined ? targetFolderBase : currentFolder;
      setFolderUploadBusy(true);
      setFolderUploadProgress({ done: 0, total: docFiles.length });
      setIngestMsg(null);
      let okCount = 0;
      let failCount = 0;
      try {
        for (let i = 0; i < docFiles.length; i++) {
          const file = docFiles[i];
          const rel =
            (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
          const targetFolder = portalUploadFolderPathFromWebkit(rel, uploadBase);
          setFolderUploadProgress({ done: i, total: docFiles.length });
          try {
            const fields = await uploadPortalFileCore(file, targetFolder);
            onDocumentIngested?.(fields);
            okCount += 1;
          } catch {
            failCount += 1;
          }
        }
        await Promise.resolve(onRefresh());
        const parts: string[] = [];
        if (okCount) parts.push(`${okCount} Datei${okCount === 1 ? '' : 'en'} hochgeladen`);
        if (failCount) parts.push(`${failCount} fehlgeschlagen`);
        if (audioFiles.length) parts.push(`${audioFiles.length} Audio → Diktat übersprungen`);
        setIngestMsg(parts.join(' · ') || 'Ordner-Upload abgeschlossen');
      } finally {
        setFolderUploadBusy(false);
        setFolderUploadProgress(null);
        if (folderInputRef.current) folderInputRef.current.value = '';
      }
    },
    [currentFolder, uploadPortalFileCore, onRefresh, onRouteAudioToDiktat, onDocumentIngested],
  );

  const uploadFolder = useCallback(
    async (fileList: FileList | null) => {
      await uploadFolderFiles(fileList);
    },
    [uploadFolderFiles],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent, targetFolderBase?: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const collected = await collectPortalDropFiles(e.dataTransfer);
      if (!collected.length) {
        setIngestMsg('Drop konnte nicht gelesen werden — bitte erneut ablegen oder Datei wählen.');
        return;
      }
      if (
        isPortalFolderDataTransfer(e.dataTransfer) ||
        collected.length > 1 ||
        collected.some((f) => /[/\\]/.test((f as File & { webkitRelativePath?: string }).webkitRelativePath || ''))
      ) {
        void uploadFolderFiles(collected, targetFolderBase);
        return;
      }
      handleSelectedFile(collected[0]);
    },
    [handleSelectedFile, uploadFolderFiles],
  );

  const handleFolderInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      void uploadFolder(e.target.files);
    },
    [uploadFolder],
  );

  const importNextcloudShareDocument = useCallback(
    async (shareUrl: string): Promise<PortalDocumentOpenFields> => {
      if (!hasPortalAuthzToken()) {
        throw new Error('Nextcloud-Import: Bitte neu anmelden (kein gültiges Portal-Token).');
      }
      if (!PORTAL_NEXTCLOUD_SHARE_IMPORT_URL) {
        throw new Error('Nextcloud-Import ist in diesem Portal-Build nicht konfiguriert.');
      }
      const validated = validateNextcloudSharePasteUrl(shareUrl, ncShareAllowedHosts);
      if (validated.ok === false) throw new Error(validated.error);

      const url = resolveOnlyofficeIssueJwtUrl(PORTAL_NEXTCLOUD_SHARE_IMPORT_URL);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...portalAuthzHeaders(url),
        },
        credentials: 'omit',
        body: JSON.stringify({
          shareUrl: validated.href,
          customerId,
          ...(getPortalTenantN8nExtras(customerStatus, customerEmail)),
        }),
      });
      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* gemeinsamer Parser meldet eine fehlende JSON-Antwort sichtbar */
      }
      return parsePortalDocumentN8nResponse(data, res);
    },
    [customerEmail, customerId, customerStatus, ncShareAllowedHosts],
  );

  const handleNextcloudShareImport = useCallback(async () => {
    setNcShareBusy(true);
    setIngestMsg(null);
    try {
      const fields = await importNextcloudShareDocument(ncShareUrlInput);
      setNcShareUrlInput('');
      let msg = `„${fields.display_name}" importiert`;
      if (!fields.pb_record_id) {
        msg += ' — Index noch nicht verfügbar (Aktualisieren)';
      }
      setIngestMsg(msg);
      onDocumentIngested?.(fields);
      await Promise.resolve(onRefresh());
      if (
        openAfterSave
        && canOpenDocumentInPortal(isPremium, fields.display_name, fields.nextcloud_path, fields.document_url)
      ) {
        routeDocumentOpen(fields);
      }
    } catch (err) {
      setIngestMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setNcShareBusy(false);
    }
  }, [
    importNextcloudShareDocument,
    isPremium,
    ncShareUrlInput,
    onDocumentIngested,
    routeDocumentOpen,
    onRefresh,
    openAfterSave,
  ]);

  const archiveDocument = useCallback(
    async (recordId: string) => {
      const token = getPortalPbToken();
      if (!token) {
        setIngestMsg('Löschen: Bitte neu anmelden.');
        return;
      }
      setArchivingId(recordId);
      try {
        const res = await fetch(
          `${getPortalPocketBaseUrl()}/api/collections/${PORTAL_PROTOKOLLE_COLLECTION}/records/${recordId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status: 'archived' }),
          },
        );
        if (res.ok) {
          setDeleteConfirmId(null);
          onRefresh();
          onRecentDocsChange?.();
        } else if (res.status === 403) {
          setIngestMsg('Dieses Dokument gehört zu einer anderen Akte und lässt sich hier nicht entfernen.');
        } else {
          setIngestMsg('Dokument konnte nicht aus der Liste entfernt werden.');
        }
      } finally {
        setArchivingId(null);
      }
    },
    [onRefresh, onRecentDocsChange],
  );

  const renameFocusedAkte = useCallback(
    async (akteId: string) => {
      const token = authToken?.trim() || '';
      if (!token) {
        setIngestMsg('Umbenennen: Bitte neu anmelden.');
        return;
      }
      const nextTitle = renameDraft.trim();
      if (!nextTitle) {
        setIngestMsg('Akten-Titel fehlt.');
        return;
      }
      setRenameBusy(true);
      try {
        const result = await updatePortalAkteTitle(token, akteId, nextTitle);
        if (result.ok === false) {
          setIngestMsg(result.error);
          return;
        }
        setPortalAkten((prev) => prev.map((row) => (row.id === akteId ? result.data : row)));
        setFocusedAkte((prev) => (prev?.id === akteId ? result.data : prev));
        setPins((prev) =>
          prev.map((pin) => (pin.akte_id === akteId ? { ...pin, title: result.data.title } : pin)),
        );
        setRenameAkteId(null);
        setIngestMsg(`Akte umbenannt: ${result.data.title}`);
      } finally {
        setRenameBusy(false);
      }
    },
    [authToken, renameDraft],
  );

  const applyLocalDocumentRename = useCallback(
    (pbRecordId: string, title: string, nextcloudPath?: string) => {
      setFavorites((prev) => applyPortalDocumentRenameToShortcuts(prev, { pb_record_id: pbRecordId, title, nextcloud_path: nextcloudPath }));
      setPins((prev) => applyPortalDocumentRenameToShortcuts(prev, { pb_record_id: pbRecordId, title, nextcloud_path: nextcloudPath }));
      applyPortalDocumentRenameToRecent(customerId, {
        pb_record_id: pbRecordId,
        display_name: title,
        nextcloud_path: nextcloudPath,
      });
      onRecentDocsChange?.();
    },
    [customerId, onRecentDocsChange],
  );

  const renamePortalDocument = useCallback(
    async (doc: PortalProtokoll) => {
      if (!canWriteDocuments) {
        setIngestMsg('Umbenennen nur mit Schreibrecht.');
        return;
      }
      if (!doc.nextcloud_path?.trim()) {
        setIngestMsg('Dokument hat keinen Nextcloud-Pfad — Umbenennen nicht möglich.');
        return;
      }
      const nextName = sanitizePortalFileName(renameDocDraft);
      if (!nextName) {
        setIngestMsg('Dateiname fehlt oder ist ungültig.');
        return;
      }
      setRenameBusy(true);
      setIngestMsg(null);
      try {
        const saved = await renamePortalDocumentViaFolderApi({
          customerId,
          customerStatus,
          customerEmail,
          pbRecordId: doc.id,
          sourcePath: doc.nextcloud_path,
          newName: nextName,
        });
        const savedPath = saved.nextcloud_path ?? doc.nextcloud_path;
        applyLocalDocumentRename(doc.id, saved.display_name, savedPath);
        onDocumentRenamed?.({
          pb_record_id: doc.id,
          display_name: saved.display_name,
          nextcloud_path: savedPath,
        });
        setRenameDocId(null);
        setIngestMsg(`Datei umbenannt: ${saved.display_name}`);
      } catch (err) {
        setIngestMsg(err instanceof Error ? err.message : String(err));
      } finally {
        setRenameBusy(false);
      }
    },
    [
      applyLocalDocumentRename,
      canWriteDocuments,
      customerEmail,
      customerId,
      customerStatus,
      onDocumentRenamed,
      renameDocDraft,
    ],
  );

  const openAkte = useCallback((
    akteRow: PortalAkteRecord,
    section?: PortalAkteRahmenSectionId,
    docId?: string,
  ) => {
    onBoundAkteChange?.(akteRow);
    setFocusedAkte(akteRow);
    setAkteBrowseId(akteRow.id);
    setFocusedTile('cases');
    setCurrentFolder(isAktenHub ? '' : akteRow.nc_path ? sanitizePortalFolderInput(akteRow.nc_path) : '');
    const memory = recallPortalAkteRahmen(akteRow.id, arkoBooked);
    let target = section ?? memory.sectionId;
    if (target === 'arko' && !arkoBooked) target = 'verlauf';
    setRahmenSection(target);
    setRahmenOpen(true);
    const focusDoc = target === 'onlyoffice' ? (docId || memory.docId) : '';
    if (target === 'onlyoffice' && focusDoc) {
      const stored = protokolle.find((row) => row.id === focusDoc);
      const belongs = Boolean(
        stored
        && ((stored.akte_id?.trim() || '') === akteRow.id || portalDokumentBelongsToAkte(stored, akteRow)),
      );
      const recent = loadRecentPortalDocuments(customerId, akteRow.id)
        .find((row) => row.pb_record_id === focusDoc);
      if (stored?.nc_url && belongs) {
        onOpenAkteOnlyoffice?.(portalDocumentOpenFieldsFromProtokoll({
          ...stored,
          akte_id: akteRow.id,
        }));
      } else if (recent?.document_url?.trim() && recent.pb_record_id === focusDoc) {
        onOpenAkteOnlyoffice?.({
          display_name: recent.display_name,
          document_url: recent.document_url,
          pb_record_id: recent.pb_record_id,
          nextcloud_path: recent.nextcloud_path,
          akte_id: akteRow.id,
        });
      }
    }
    rememberPortalAkteRahmen(akteRow.id, target, '', {
      docId: focusDoc,
      chatId: target === 'arko' ? (memory.chatId || peekAkteChatId(customerId, akteRow.id) || '') : '',
    });
    rememberPortalAkteRahmenOpen(akteRow.id);
    onOpenAkte?.(akteRow);
  }, [arkoBooked, customerId, isAktenHub, onBoundAkteChange, onOpenAkte, onOpenAkteOnlyoffice, protokolle]);

  const saveFocusSeen = useRef(0);
  useEffect(() => {
    if (!isAktenHub || !akteSaveFocus) return;
    if (saveFocusSeen.current === akteSaveFocus.tick) return;
    const row = portalAkten.find((akte) => akte.id === akteSaveFocus.akteId);
    if (!row) return;
    saveFocusSeen.current = akteSaveFocus.tick;
    onBoundAkteChange?.(row);
    setFocusedAkte(row);
    setAkteBrowseId(row.id);
    setFocusedTile('cases');
    setRahmenSection('onlyoffice');
    setRahmenOpen(true);
    rememberPortalAkteRahmen(row.id, 'onlyoffice', '', { docId: akteSaveFocus.docId });
    rememberPortalAkteRahmenOpen(row.id);
    onOpenAkte?.(row);
  }, [akteSaveFocus, isAktenHub, onBoundAkteChange, onOpenAkte, portalAkten]);

  const restoreOpenDone = useRef(false);
  useEffect(() => {
    if (!isAktenHub || restoreOpenDone.current) return;
    const plan = planAkteRahmenRestoreAfterViewReturn({
      lastOpenAkteId: recallPortalAkteRahmenOpen(),
      akten: portalAkten,
      listReady: aktenListLoaded,
    });
    if (plan === 'wait') return;
    if (plan === 'list') {
      restoreOpenDone.current = true;
      return;
    }
    if (plan === 'clear-stale') {
      rememberPortalAkteRahmenOpen(null);
      restoreOpenDone.current = true;
      return;
    }
    const openId = recallPortalAkteRahmenOpen();
    const row = portalAkten.find((akte) => akte.id === openId);
    if (!row) return;
    restoreOpenDone.current = true;
    openAkte(row);
  }, [aktenListLoaded, isAktenHub, openAkte, portalAkten]);

  const handleFocusedAkteChange = useCallback((akte: PortalAkteRecord | null) => {
    if (!akte) return;
    setFocusedAkte(akte);
    setAkteBrowseId(akte.id);
    onBoundAkteChange?.(akte);
    setFocusedTile('cases');
    setCurrentFolder(isAktenHub ? '' : akte.nc_path ? sanitizePortalFolderInput(akte.nc_path) : '');
  }, [isAktenHub, onBoundAkteChange]);

  const applyAkteNcPath = useCallback(
    async (akte: PortalAkteRecord, folder: string): Promise<PortalAkteRecord | null> => {
      const token = authToken?.trim() || '';
      if (!token) {
        setIngestMsg('Bitte neu anmelden.');
        return null;
      }
      const trimmed = folder.trim();
      if (!trimmed) {
        setIngestMsg('Ordnerpfad fehlt — Zuordnung nicht möglich.');
        return null;
      }
      const result = await updatePortalAkteNcPath(token, akte.id, trimmed);
      if (result.ok === false) {
        setIngestMsg(result.error);
        return null;
      }
      setFocusedAkte(result.data);
      setAkteBrowseId(result.data.id);
      setPortalAkten((prev) => prev.map((row) => (row.id === result.data.id ? result.data : row)));
      if (!isAktenHub) {
        setCurrentFolder(sanitizePortalFolderInput(result.data.nc_path || trimmed));
      } else if (portalFolderPathContainsSmokeFixture(currentFolder)) {
        setCurrentFolder('');
      }
      onBoundAkteChange?.(result.data);
      return result.data;
    },
    [authToken, currentFolder, isAktenHub, onBoundAkteChange],
  );

  /**
   * S4: Ein bereits serverseitig erzeugtes Dokument erst dann als Erfolg melden,
   * wenn PocketBase dieselbe sichtbare akte_id und den Zielpfad zurückliefert.
   */
  const bindGeneratedDocumentToAkte = useCallback(
    async (
      fields: PortalDocumentOpenFields,
      targetAkte: PortalAkteRecord,
    ): Promise<PortalDocumentOpenFields> => {
      if (!targetAkte.id.trim() || !visibleAkteIds.has(targetAkte.id)) {
        throw new Error('Keine sichtbare Ziel-Akte — Dokument wurde nicht als Akt-Dokument bestätigt.');
      }
      if (!canWriteDocuments) {
        throw new Error('Für diese Akte besteht nur Leserecht.');
      }
      const pbRecordId = fields.pb_record_id?.trim() || '';
      const sourcePath = fields.nextcloud_path?.trim() || '';
      const token = authToken?.trim() || getPortalPbToken()?.trim() || '';
      if (!pbRecordId || !sourcePath || !token) {
        throw new Error(
          'Erzeugtes Dokument hat keinen bestätigten Ablage-Eintrag — keine Aktbindung behauptet.',
        );
      }

      const targetFolder = portalAkteAssignTargetFolder(targetAkte.id);
      if (!targetFolder) {
        throw new Error('Zielpfad der Akte fehlt.');
      }
      try {
        await callPortalFolderApi('create', targetFolder, { akte_id: targetAkte.id });
      } catch {
        /* Bereits vorhandener Ordner ist erlaubt; Move prüft AuthZ und Ziel erneut. */
      }

      let fresh = await fetchPortalProtokollById(
        getPortalPocketBaseUrl(),
        PORTAL_PROTOKOLLE_COLLECTION,
        pbRecordId,
        token,
      );
      if (!fresh || !portalDocumentMovePersistedInAkte(fresh, targetAkte.id, sourcePath)) {
        const moved = await callPortalFolderApi('move', targetFolder, {
          pb_record_id: pbRecordId,
          sourcePath,
          targetFolderPath: targetFolder,
          akte_id: targetAkte.id,
        });
        const movedPath =
          typeof moved.nextcloud_path === 'string' ? moved.nextcloud_path.trim() : '';
        const movedAkteId = typeof moved.akte_id === 'string' ? moved.akte_id.trim() : '';
        if (!movedPath || movedAkteId !== targetAkte.id) {
          throw new Error('Aktbindung wurde vom Dokumentdienst nicht bestätigt.');
        }
        fresh = await fetchPortalProtokollById(
          getPortalPocketBaseUrl(),
          PORTAL_PROTOKOLLE_COLLECTION,
          pbRecordId,
          token,
        );
        if (!fresh || !portalDocumentMovePersistedInAkte(fresh, targetAkte.id, movedPath)) {
          throw new Error(
            'Die Ablage bestätigt das erzeugte Dokument nicht in dieser Akte.',
          );
        }
      }

      const boundFields: PortalDocumentOpenFields = {
        ...portalDocumentOpenFieldsFromProtokoll(fresh),
        display_name: fresh.filename || fields.display_name,
        document_url: fresh.nc_url || fields.document_url,
        akte_id: targetAkte.id,
      };
      onDocumentIngested?.(boundFields);
      await Promise.resolve(onRefresh());
      onDocumentIngested?.(boundFields);
      return boundFields;
    },
    [
      authToken,
      callPortalFolderApi,
      canWriteDocuments,
      onDocumentIngested,
      onRefresh,
      visibleAkteIds,
    ],
  );

  const exportPortalAkteSchreiben = useCallback(
    async (
      format: PortalAkteSchreibenFormat,
      draft: { text: string; displayName: string },
    ): Promise<PortalAkteSchreibenExportResult> => {
      const targetAkte = activeAkte;
      if (!targetAkte?.id || !visibleAkteIds.has(targetAkte.id)) {
        return {
          ok: false,
          message: 'Keine sichtbare Akte — Schreiben wurde nicht erzeugt.',
        };
      }
      if (!canWriteDocuments) {
        return { ok: false, message: 'Für diese Akte besteht nur Leserecht.' };
      }

      try {
        const targetFolder = portalAkteAssignTargetFolder(targetAkte.id);
        if (!targetFolder) throw new Error('Zielpfad der Akte fehlt.');

        if (format === 'docx') {
          if (!PORTAL_DOCUMENT_UPLOAD_URL) {
            throw new Error('DOCX ist in diesem Portal-Build nicht angebunden.');
          }
          try {
            await callPortalFolderApi('create', targetFolder, { akte_id: targetAkte.id });
          } catch {
            /* Upload prüft das Ziel erneut; vorhandener Ordner ist erlaubt. */
          }
          const fields = await ingestPortalFile(
            createBlankDocxFile(draft.displayName, {
              kanzleiName,
              bodyText: draft.text,
            }),
            targetFolder,
            { openAfterSave: false, expectedAkteId: targetAkte.id },
          );
          setIngestMsg(`„${fields.display_name}" in Akte „${targetAkte.title}" gespeichert`);
          return {
            ok: true,
            message: `DOCX wurde in Akte „${targetAkte.title}“ gespeichert.`,
          };
        }

        if (
          !ARKO_SCHREIBEN_PDF_URL ||
          !PORTAL_NEXTCLOUD_SHARE_IMPORT_URL ||
          !PORTAL_DOCUMENT_FOLDER_URL
        ) {
          throw new Error('PDF ist in diesem Portal-Build nicht vollständig angebunden.');
        }
        if (!hasPortalAuthzToken()) {
          throw new Error('Bitte neu anmelden — es fehlt ein gültiges Portal-Token.');
        }

        const pdfUrl = resolveOnlyofficeIssueJwtUrl(ARKO_SCHREIBEN_PDF_URL);
        const pdfRes = await fetch(pdfUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...portalAuthzHeaders(pdfUrl),
          },
          credentials: 'omit',
          body: JSON.stringify({
            text: draft.text,
            customerId,
            display_name: draft.displayName,
            ...(kanzleiName?.trim() ? { kanzlei: kanzleiName.trim() } : {}),
            akte_id: targetAkte.id,
            folderPath: targetFolder,
            ...getPortalTenantN8nExtras(customerStatus, customerEmail),
          }),
        });
        let pdfData: Record<string, unknown> = {};
        try {
          pdfData = (await pdfRes.json()) as Record<string, unknown>;
        } catch {
          /* sichtbarer Fehler unten */
        }
        const generatedShareUrl =
          typeof pdfData.pdf_url === 'string' ? pdfData.pdf_url.trim() : '';
        if (!pdfRes.ok || !generatedShareUrl) {
          const detail =
            typeof pdfData.error === 'string' && pdfData.error.trim()
              ? pdfData.error.trim()
              : `PDF-Dienst antwortete mit HTTP ${pdfRes.status}.`;
          throw new Error(detail);
        }

        // Bestehende PDF-Ausgabe serverseitig importieren (kein Browser-CORS),
        // danach atomar per Folder-API in die sichtbare Akte verschieben.
        const imported = await importNextcloudShareDocument(generatedShareUrl);
        const bound = await bindGeneratedDocumentToAkte(imported, targetAkte);
        setIngestMsg(`„${bound.display_name}" in Akte „${targetAkte.title}" gespeichert`);
        return {
          ok: true,
          message: `PDF wurde in Akte „${targetAkte.title}“ gespeichert.`,
        };
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Schreiben konnte nicht in der Akte gespeichert werden.';
        setIngestMsg(message);
        return { ok: false, message };
      }
    },
    [
      activeAkte,
      bindGeneratedDocumentToAkte,
      callPortalFolderApi,
      canWriteDocuments,
      customerEmail,
      customerId,
      customerStatus,
      importNextcloudShareDocument,
      ingestPortalFile,
      kanzleiName,
      visibleAkteIds,
    ],
  );

  const handleContractDocumentReadyInAkte = useCallback(
    async (data: Record<string, unknown>, res: Response) => {
      const targetAkte = activeAkte;
      if (!targetAkte?.id || !visibleAkteIds.has(targetAkte.id)) {
        throw new Error('Keine sichtbare Ziel-Akte — Vertrag wurde nicht als Akt-Dokument bestätigt.');
      }
      const fields = parsePortalDocumentN8nResponse(data, res);
      const bound = await bindGeneratedDocumentToAkte(fields, targetAkte);
      setIngestMsg(`„${bound.display_name}" in Akte „${targetAkte.title}" gespeichert`);
      routeDocumentOpen(bound);
    },
    [activeAkte, bindGeneratedDocumentToAkte, routeDocumentOpen, visibleAkteIds],
  );

  const createBlankDocumentInAkte = useCallback(async () => {
    if (!isAktenHub) {
      await ingestPortalFile(
        createBlankDocxFile(undefined, { kanzleiName }),
        currentFolder || undefined,
        { openAfterSave: true },
      );
      return;
    }
    const targetAkte = browseAkte || focusedAkte;
    const folder = targetAkte ? portalAkteAssignTargetFolder(targetAkte.id) : '';
    if (folder) {
      try {
        await callPortalFolderApi('create', folder, { akte_id: targetAkte.id });
      } catch {
        /* Ordner kann bereits existieren */
      }
    }
    const fields = await ingestPortalFile(
      createBlankDocxFile(undefined, { kanzleiName }),
      folder || undefined,
      { openAfterSave: true, expectedAkteId: targetAkte?.id },
    );
    if (targetAkte && folder) {
      const saved = await applyAkteNcPath(targetAkte, folder);
      if (saved) {
        setIngestMsg(`„${fields.display_name}" in Akte „${saved.title}" angelegt`);
      }
    }
  }, [applyAkteNcPath, browseAkte, callPortalFolderApi, currentFolder, focusedAkte, ingestPortalFile, isAktenHub, kanzleiName]);

  const openAkteDocFromPlus = useCallback((doc: PortalProtokoll) => {
    if (!activeAkte?.id) return;
    if (akteOnlyofficePlusKind(doc, activeAkte) !== 'this-akte') {
      setIngestMsg('Dieses Dokument gehört nicht zu dieser Akte.');
      return;
    }
    routeDocumentOpen({
      ...portalDocumentOpenFieldsFromProtokoll(doc),
      akte_id: activeAkte.id,
    });
    setOoPlusOpen(false);
  }, [activeAkte, routeDocumentOpen]);

  const addPcFileFromPlus = useCallback(async (file: File) => {
    if (!activeAkte?.id) return;
    if (isAudioFile(file)) {
      setIngestMsg('Audio gehört in den Diktat-Reiter — in der Akte wurde nichts gespeichert.');
      return;
    }
    const folder = portalAkteAssignTargetFolder(activeAkte.id);
    try {
      await ingestPortalFile(file, folder || undefined, {
        openAfterSave: true,
        expectedAkteId: activeAkte.id,
      });
    } catch {
      /* Meldung setzt ingestPortalFile */
    }
    setOoPlusOpen(false);
  }, [activeAkte, ingestPortalFile]);

  const copyDocumentToAkte = useCallback(
    async (doc: PortalProtokoll, akte: PortalAkteRecord, relativeFolder?: string) => {
      const fail = (error: string) => {
        setIngestMsg(error);
        return { ok: false as const, error };
      };
      if (!canWriteDocuments) {
        return fail('Zuordnen nur mit Schreibrecht.');
      }
      if (!doc.nextcloud_path?.trim()) {
        return fail('Dokument hat keinen Nextcloud-Pfad — Kopieren nicht möglich.');
      }
      if (!doc.id.trim() || doc.id.startsWith('pending:')) {
        return fail('Kopieren braucht einen bestätigten Ablage-Eintrag.');
      }
      const rel = sanitizePortalFolderInput(relativeFolder || '');
      const targetFolder = rel
        ? `${portalAkteAssignTargetFolder(akte.id)}/${rel}`
        : portalAkteAssignTargetFolder(akte.id);
      if (!targetFolder) {
        return fail('Akte braucht einen Ordnernamen — bitte umbenennen und erneut zuordnen.');
      }
      if (portalFolderPathContainsSmokeFixture(targetFolder)) {
        return fail('Interner Testordner ist kein Ziel — Datei bleibt in der Liste.');
      }
      try {
        await callPortalFolderApi('create', targetFolder, { akte_id: akte.id });
      } catch {
        /* Ordner kann bereits existieren */
      }
      setMovingDocId(doc.id);
      setIngestMsg(null);
      try {
        const sourceBefore = {
          id: doc.id,
          akte_id: doc.akte_id,
          nextcloud_path: doc.nextcloud_path,
        };
        const data = await callPortalFolderApi('copy', targetFolder, {
          pb_record_id: doc.id,
          sourcePath: doc.nextcloud_path,
          targetFolderPath: targetFolder,
          akte_id: portalFolderApiAkteId(targetFolder, akte.id),
        });
        const copyId = typeof data.pb_record_id === 'string' ? data.pb_record_id.trim() : '';
        const nextcloudPath =
          typeof data.nextcloud_path === 'string' ? data.nextcloud_path.trim() : '';
        if (!copyId || copyId === doc.id || !nextcloudPath) {
          throw new Error('Kopieren konnte in der Ablage nicht bestätigt werden.');
        }
        const token = authToken?.trim() || getPortalPbToken()?.trim() || '';
        if (!token) {
          throw new Error('Kopieren: Bitte neu anmelden.');
        }
        const freshCopy = await fetchPortalProtokollById(
          getPortalPocketBaseUrl(),
          PORTAL_PROTOKOLLE_COLLECTION,
          copyId,
          token,
        );
        const freshSource = await fetchPortalProtokollById(
          getPortalPocketBaseUrl(),
          PORTAL_PROTOKOLLE_COLLECTION,
          doc.id,
          token,
        );
        if (
          !freshCopy ||
          !freshSource ||
          !portalDocumentCopyKeepsSource({
            sourceBefore,
            sourceAfter: freshSource,
            copy: freshCopy,
            targetAkteId: akte.id,
          }) ||
          !portalDocumentMovePersistedInAkte(freshCopy, akte.id, nextcloudPath)
        ) {
          throw new Error(
            'Kopieren fehlgeschlagen — die Pool-Datei bleibt unverändert, bitte erneut versuchen.',
          );
        }
        const canonicalAktePath = portalAkteNcPathFromMovedDocument(
          freshCopy.nextcloud_path,
          akte.id,
        );
        if (!canonicalAktePath) {
          throw new Error('Der bestätigte Kopierpfad passt nicht zur Ziel-Akte.');
        }
        const saved = await applyAkteNcPath(akte, canonicalAktePath);
        if (!saved) throw new Error('Aktenpfad konnte nach dem Kopieren nicht bestätigt werden.');
        await Promise.resolve(onRefresh());
        setIngestMsg(`Kopie in Akte „${saved.title}" — Original bleibt im Pool.`);
        return {
          ok: true as const,
          fields: {
            ...portalDocumentOpenFieldsFromProtokoll(freshCopy),
            akte_id: akte.id,
          },
        };
      } catch (err) {
        const error = err instanceof Error ? err.message : String(err);
        setIngestMsg(error);
        return { ok: false as const, error };
      } finally {
        setMovingDocId(null);
        setDraggingDocId(null);
        setDropTargetFolder(null);
      }
    },
    [applyAkteNcPath, authToken, callPortalFolderApi, canWriteDocuments, onRefresh],
  );

  const addPoolDocFromPlus = useCallback(async (doc: PortalProtokoll) => {
    if (!activeAkte?.id) return;
    if (akteOnlyofficePlusKind(doc, activeAkte) !== 'pool') {
      setIngestMsg('Dieses Dokument gehört nicht zu dieser Akte.');
      return;
    }
    const added = await copyDocumentToAkte(doc, activeAkte);
    if (added.ok && added.fields) routeDocumentOpen(added.fields);
    setOoPlusOpen(false);
  }, [activeAkte, copyDocumentToAkte, routeDocumentOpen]);

  const assignDocumentToAkte = useCallback(
    async (doc: PortalProtokoll, akte: PortalAkteRecord) => {
      const fail = (error: string) => {
        setIngestMsg(error);
        return { ok: false as const, error };
      };
      if (!canWriteDocuments) {
        return fail('Zuordnen nur mit Schreibrecht.');
      }
      if (!doc.nextcloud_path?.trim()) {
        return fail('Dokument hat keinen Nextcloud-Pfad — Zuordnung nicht möglich.');
      }
      const targetFolder = portalAkteAssignTargetFolder(akte.id);
      if (!targetFolder) {
        return fail('Akte braucht einen Ordnernamen — bitte umbenennen und erneut zuordnen.');
      }
      try {
        await callPortalFolderApi('create', targetFolder, { akte_id: akte.id });
      } catch {
        /* Ordner kann bereits existieren */
      }

      const moved = await moveDocumentToFolder(doc, targetFolder, akte.id);
      if (moved.ok === false) return moved;
      if (!portalDocumentMovePersistedInAkte(moved.record, akte.id, moved.nextcloudPath)) {
        return fail(
          'Verschieben wurde nicht an die Ziel-Akte gebunden — kein Erfolg wurde bestätigt.',
        );
      }
      const canonicalAktePath = portalAkteNcPathFromMovedDocument(
        moved.nextcloudPath,
        akte.id,
      );
      if (!canonicalAktePath) {
        return fail('Der bestätigte Dokumentpfad passt nicht zur Ziel-Akte.');
      }
      const saved = await applyAkteNcPath(akte, canonicalAktePath);
      if (!saved) return fail('Aktenpfad konnte nach dem Verschieben nicht bestätigt werden.');
      setIngestMsg(`„${doc.filename}" zugeordnet — Akte „${saved.title}"`);
      return { ok: true as const };
    },
    [applyAkteNcPath, callPortalFolderApi, canWriteDocuments, moveDocumentToFolder],
  );

  const saveUploadFile = useCallback(async () => {
    if (!uploadFile) return;
    const akte = uploadAkteId ? portalAkten.find((row) => row.id === uploadAkteId) ?? null : null;
    let folder: string | undefined = currentFolder || undefined;
    if (akte) {
      let target = portalAkteAssignTargetFolder(akte.id);
      if (target) {
        try {
          await callPortalFolderApi('create', target, { akte_id: akte.id });
        } catch {
          /* Ordner kann bereits existieren */
        }
        folder = target;
      }
    }
    const fields = await ingestPortalFile(uploadFile, folder, {
      expectedAkteId: akte?.id,
    });
    if (akte && folder) {
      const saved = await applyAkteNcPath(akte, folder);
      if (saved) {
        setIngestMsg(`„${fields.display_name}" gespeichert in Akte „${saved.title}"`);
      }
    }
  }, [
    applyAkteNcPath,
    callPortalFolderApi,
    currentFolder,
    ingestPortalFile,
    portalAkten,
    uploadAkteId,
    uploadFile,
  ]);

  const beginMoveToAkte = useCallback(
    (doc: PortalProtokoll, anchor: HTMLElement) => {
      setRowContextMenu(null);
      closeSharePopover();
      if (!canWriteDocuments) {
        setIngestMsg('Zuordnen nur mit Schreibrecht.');
        return;
      }
      if (!doc.nextcloud_path?.trim()) {
        setIngestMsg('Dokument hat keinen Nextcloud-Pfad — Zuordnung nicht möglich.');
        return;
      }
      moveAnchorRef.current = anchor;
      const position = placeSharePopover(anchor.getBoundingClientRect(), {
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setMoveError(null);
      setMoveTargetAkteId(activeAkte?.id || (portalAkten.length === 1 ? portalAkten[0].id : ''));
      setMovePopover({ docId: doc.id, ...position });
    },
    [activeAkte?.id, canWriteDocuments, closeSharePopover, portalAkten],
  );

  const assignDroppedToAkte = useCallback(
    async (e: React.DragEvent, akte: PortalAkteRecord) => {
      if (!canWriteDocuments) {
        setIngestMsg('Drop in eine Akte braucht Schreibrecht.');
        return;
      }
      const draggedId = readPortalDragDocId(e.dataTransfer, draggingDocId);
      const draggedDoc = draggedId ? protokolle.find((row) => row.id === draggedId) : undefined;
      if (draggedDoc) {
        await copyDocumentToAkte(draggedDoc, akte);
        return;
      }
      const collected = await collectPortalDropFiles(e.dataTransfer);
      const documentFiles = collected.filter((row) => !isAudioFile(row));
      if (documentFiles.length !== 1 || collected.length !== 1) {
        setIngestMsg(
          collected.length > 0 && documentFiles.length === 0
            ? 'Audio gehört in den Diktat-Reiter — in der Akte wurde nichts gespeichert.'
            : 'Bitte genau eine Dokumentdatei auf die offene Akte ziehen — es wurde nichts gespeichert.',
        );
        return;
      }
      const file = documentFiles[0];
      const folder = portalAkteAssignTargetFolder(akte.id);
      if (!folder) {
        setIngestMsg('Die Ziel-Akte konnte nicht eindeutig aufgelöst werden.');
        return;
      }
      setUploadBusy(true);
      setIngestMsg(null);
      try {
        await callPortalFolderApi('create', folder, { akte_id: akte.id });
      } catch {
        /* Ordner kann bereits existieren */
      }
      try {
        const fields = await uploadPortalFileCore(file, folder, akte.id);
        const pbRecordId = fields.pb_record_id?.trim() ?? '';
        const nextcloudPath = fields.nextcloud_path?.trim() ?? '';
        const token = authToken?.trim() || getPortalPbToken()?.trim() || '';
        if (!pbRecordId || !nextcloudPath || !token) {
          throw new Error(
            'Upload wurde nicht aktgebunden bestätigt — bitte neu laden und erneut versuchen.',
          );
        }
        const fresh = await fetchPortalProtokollById(
          getPortalPocketBaseUrl(),
          PORTAL_PROTOKOLLE_COLLECTION,
          pbRecordId,
          token,
        );
        if (!fresh || !portalDocumentMovePersistedInAkte(fresh, akte.id, nextcloudPath)) {
          throw new Error(
            'Datei wurde nicht an diese Akte gebunden — kein Erfolg bestätigt.',
          );
        }
        const canonicalAktePath = portalAkteNcPathFromMovedDocument(
          fresh.nextcloud_path,
          akte.id,
        );
        if (!canonicalAktePath) {
          throw new Error('Der gespeicherte Dokumentpfad passt nicht zur Ziel-Akte.');
        }
        const saved = await applyAkteNcPath(akte, canonicalAktePath);
        if (!saved) throw new Error('Aktenpfad konnte nach dem Upload nicht bestätigt werden.');
        onDocumentIngested?.(fields);
        await Promise.resolve(onRefresh());
        setIngestMsg(`„${file.name}" in Akte „${saved.title}" gespeichert.`);
      } catch (error) {
        setIngestMsg(error instanceof Error ? error.message : String(error));
      } finally {
        setUploadBusy(false);
      }
    },
    [
      applyAkteNcPath,
      authToken,
      callPortalFolderApi,
      canWriteDocuments,
      copyDocumentToAkte,
      draggingDocId,
      onDocumentIngested,
      onRefresh,
      protokolle,
      uploadPortalFileCore,
    ],
  );

  const beginRenameDocument = useCallback((doc: PortalProtokoll) => {
    setRenameDocId(doc.id);
    setRenameDocDraft(doc.filename);
    setFocusedTile('documents');
  }, []);

  const pinShortcut = useCallback((item: PortalDocShortcut) => {
    setPins((prev) => upsertPortalDocPin(prev, item));
    setIngestMsg(`An Schnellzugriff angeheftet: ${item.title}`);
  }, []);

  const handleTileDrop = useCallback(
    async (tile: DokumenteTileId, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setTileDropTarget('');
      setFocusedTile(tile);

      const draggedId = readPortalDragDocId(e.dataTransfer, draggingDocId);
      const draggedDoc = draggedId ? protokolle.find((row) => row.id === draggedId) : undefined;

      if (tile === 'documents') {
        if (isAktenHub) {
          if (draggedDoc) {
            setIngestMsg('Bitte die Datei auf die offene Akte rechts ziehen — im Pool wird nichts verschoben.');
            return;
          }
          void handleDrop(e, '');
          return;
        }
        if (draggedDoc) {
          void moveDocumentToFolder(draggedDoc, currentFolder);
          return;
        }
        void handleDrop(e, currentFolder);
        return;
      }

      if (tile === 'quick') {
        if (draggedDoc) {
          const item = shortcutFromProtokoll(draggedDoc);
          setFavorites((prev) =>
            prev.some((row) => row.id === item.id) ? prev : togglePortalDocFavorite(prev, item),
          );
          setIngestMsg(`Als Favorit markiert: ${draggedDoc.filename}`);
          return;
        }
        const collected = await collectPortalDropFiles(e.dataTransfer);
        if (collected[0] && !isAudioFile(collected[0])) {
          try {
            const fields = await ingestPortalFile(collected[0], currentFolder || undefined);
            const item: PortalDocShortcut = {
              id: `doc:${fields.pb_record_id || fields.display_name}`,
              kind: 'document',
              title: fields.display_name,
              pb_record_id: fields.pb_record_id,
              nextcloud_path: fields.nextcloud_path,
              document_url: fields.document_url,
            };
            setFavorites((prev) =>
              prev.some((row) => row.id === item.id) ? prev : togglePortalDocFavorite(prev, item),
            );
            setIngestMsg(`Als Favorit markiert: ${fields.display_name}`);
          } catch {
            /* ingestPortalFile setzt bereits ingestMsg */
          }
          return;
        }
        setIngestMsg('Favoriten: Datei oder vorhandenes Dokument hier ablegen.');
        return;
      }

      if (!isAktenHub) {
        if (draggedDoc) {
          void moveDocumentToFolder(draggedDoc, currentFolder);
          return;
        }
        void handleDrop(e, currentFolder);
        return;
      }

      if (draggedDoc) {
        const assignTarget = browseAkte || focusedAkte;
        if (!assignTarget) {
          setIngestMsg('Bitte zuerst eine Akte fokussieren — Zuordnung wird nicht geraten.');
          return;
        }
        void copyDocumentToAkte(draggedDoc, assignTarget);
        return;
      }

      const assignTarget = browseAkte || focusedAkte;
      if (!assignTarget) {
        setIngestMsg('Bitte zuerst eine Akte fokussieren — Zuordnung wird nicht geraten.');
        return;
      }
      void assignDroppedToAkte(e, assignTarget);
    },
    [
      assignDroppedToAkte,
      browseAkte,
      copyDocumentToAkte,
      currentFolder,
      currentFolder,
      draggingDocId,
      focusedAkte,
      handleDrop,
      ingestPortalFile,
      isAktenHub,
      moveDocumentToFolder,
      pinShortcut,
      protokolle,
    ],
  );

  const openProtokoll = useCallback(
    (p: PortalProtokoll) => {
      if (!p.nc_url) return;
      const fields: PortalDocumentOpenFields = {
        display_name: p.filename,
        document_url: p.nc_url,
        pb_record_id: p.id,
        nextcloud_path: p.nextcloud_path,
        updated: p.updated,
        akte_id:
          p.akte_id?.trim()
          || (isAktenHub && rahmenOpen && activeAkte && portalDokumentBelongsToAkte(p, activeAkte)
            ? activeAkte.id
            : undefined),
      };
      saveRecentPortalDocument(
        customerId,
        {
          display_name: p.filename,
          pb_record_id: p.id,
          nextcloud_path: p.nextcloud_path,
          document_url: p.nc_url,
          openedAt: new Date().toISOString(),
        },
        activeAkte?.id,
      );
      onRecentDocsChange?.();
      if (canOpenDocumentInPortal(isPremium, p.filename, p.nextcloud_path, p.nc_url)) {
        routeDocumentOpen(fields);
      } else {
        window.open(p.nc_url, '_blank', 'noopener,noreferrer');
      }
    },
    [activeAkte, customerId, isAktenHub, isPremium, onRecentDocsChange, rahmenOpen, routeDocumentOpen],
  );

  const openExplorerFolder = useCallback((folderPath: string) => {
    setCurrentFolder(sanitizePortalFolderInput(folderPath));
    setShareLink(null);
  }, []);

  const openAkteExplorerFolder = useCallback((folderPath: string) => {
    setAkteExplorerFolder(sanitizePortalFolderInput(folderPath));
  }, []);

  const renderExplorerFolderRow = (
    folderPath: string,
    opts?: { onOpen?: (path: string) => void; copyOntoAkte?: boolean; kanzleiLook?: boolean },
  ) => {
    const label = folderPath.includes('/')
      ? folderPath.slice(folderPath.lastIndexOf('/') + 1)
      : folderPath || 'Ordner';
    const openFolder = opts?.onOpen || openExplorerFolder;
    return (
      <div
        key={`folder:${folderPath || 'root'}`}
        onDragOver={(e) => {
          if (!draggingDocId && !canPortalDocumentUpload) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = opts?.copyOntoAkte
            ? 'copy'
            : portalDragIsInternalDoc(e.dataTransfer, draggingDocId)
              ? 'move'
              : 'copy';
          setDropTargetFolder(folderPath);
        }}
        onDragLeave={() => {
          if (dropTargetFolder === folderPath) setDropTargetFolder(null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const docId = readPortalDragDocId(e.dataTransfer, draggingDocId);
          const doc = docId ? protokolle.find((row) => row.id === docId) : undefined;
          if (portalFolderPathContainsSmokeFixture(folderPath)) {
            setIngestMsg('Interner Testordner ist kein Ziel — Datei bleibt in der Liste.');
            setDropTargetFolder(null);
            return;
          }
          if (doc) {
            if (opts?.copyOntoAkte && activeAkte) {
              void copyDocumentToAkte(doc, activeAkte, folderPath);
            } else {
              void moveDocumentToFolder(doc, folderPath);
            }
            setDropTargetFolder(null);
            return;
          }
          if (
            canPortalDocumentUpload &&
            (portalDragMayAcceptExternalFiles(e.dataTransfer) ||
              isPortalFolderDataTransfer(e.dataTransfer))
          ) {
            void handleDrop(e, folderPath);
          }
          setDropTargetFolder(null);
        }}
        className={`flex items-center justify-between gap-3 px-4 py-1.5 ${
          opts?.kanzleiLook
            ? dropTargetFolder === folderPath
              ? 'border-y border-violet-400/40 bg-violet-600/15'
              : 'border-y border-violet-400/20 bg-[#07070a]'
            : dropTargetFolder === folderPath
              ? 'border-y border-white/[0.04] bg-violet-500/15 ring-1 ring-violet-400/40 ring-inset'
              : 'border-y border-white/[0.04] bg-slate-950/20'
        }`}
      >
        <button
          type="button"
          onClick={() => openFolder(folderPath)}
          className="flex items-center gap-2 min-w-0 text-left bg-transparent border-0 p-0 m-0 cursor-pointer"
          title="Ordner öffnen"
        >
          <Folder className="w-3 h-3 text-amber-400/70 flex-shrink-0" aria-hidden />
          <span className="text-[12px] text-slate-200 min-w-0 break-words line-clamp-2">{label}</span>
        </button>
        <DokumenteRowActions
          onOpen={() => openFolder(folderPath)}
          onRename={() => setIngestMsg(FOLDER_RENAME_HINT)}
          onDelete={() => setIngestMsg(FOLDER_DELETE_HINT)}
          openHint="Öffnen"
          renameDisabled
          renameHint={FOLDER_RENAME_HINT}
          deleteDisabled
          deleteHint={FOLDER_DELETE_HINT}
        />
      </div>
    );
  };

  const renderExplorerBreadcrumb = (
    cwd: string,
    rootPath: string,
    rootLabel: string,
    onOpen: (path: string) => void = openExplorerFolder,
    kanzleiLook = false,
  ) => {
    const crumbs = portalExplorerBreadcrumb(cwd, { rootPath, rootLabel });
    const parent = portalExplorerParentFolder(cwd, rootPath);
    const canGoUp = cwd !== rootPath && cwd !== '';
    return (
      <div
        className={
          kanzleiLook
            ? 'flex items-center gap-1.5 border-b border-violet-400/30 bg-[#07070a] px-3 py-2'
            : 'flex items-center gap-1.5 px-4 py-2 border-b border-white/[0.06] bg-white/[0.02]'
        }
      >
        <button
          type="button"
          onClick={() => canGoUp && onOpen(parent)}
          disabled={!canGoUp}
          className="inline-flex items-center justify-center rounded-md p-1 text-slate-400 hover:text-slate-100 disabled:opacity-30"
          title="Übergeordneter Ordner"
          aria-label="Übergeordneter Ordner"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden />
        </button>
        <nav className="flex items-center gap-0.5 min-w-0 flex-wrap" aria-label="Ordnerpfad">
          {crumbs.map((crumb, idx) => (
            <span key={`${crumb.path}:${idx}`} className="inline-flex items-center gap-0.5 min-w-0">
              {idx > 0 ? <ChevronRight className="w-3 h-3 text-slate-600 flex-shrink-0" aria-hidden /> : null}
              <button
                type="button"
                onClick={() => onOpen(crumb.path)}
                className={`text-[11px] bg-transparent border-0 p-0 m-0 cursor-pointer truncate max-w-[10rem] ${
                  idx === crumbs.length - 1 ? 'text-slate-200 font-semibold' : 'text-slate-500 hover:text-slate-200'
                }`}
              >
                {crumb.label}
              </button>
            </span>
          ))}
        </nav>
      </div>
    );
  };

  const bindDocumentDrag = (doc: PortalProtokoll | undefined) => {
    const canDrag =
      Boolean(canWriteDocuments && doc?.nextcloud_path?.trim()) && movingDocId !== doc?.id;
    return {
      draggable: canDrag,
      onDragStart: (e: React.DragEvent) => {
        if (!doc?.nextcloud_path?.trim()) return;
        setDraggingDocId(doc.id);
        startPortalDocumentDrag(e, doc.id);
      },
      onDragEnd: () => {
        setDraggingDocId(null);
        setDropTargetFolder(null);
        setAkteDropTargetId(null);
        setTileDropTarget('');
      },
    };
  };

  const renderDocumentRow = (p: PortalProtokoll, preview?: boolean) => {
    const showPreview = preview === true;
    const ext =
      fileExtensionFromName(p.filename) ||
      fileExtensionFromName(p.nextcloud_path || '') ||
      fileExtensionFromName(p.nc_url || '');
    const { Icon, colorClass } = resolvePortalDocumentIcon(ext);
    const openable = canOpenDocumentInPortal(isPremium, p.filename, p.nextcloud_path, p.nc_url);
    const typeLabel = portalDocumentTypeLabel(p.filename, p.mime_type);
    const typeMark = portalFileTypeMark(p.filename);
    const typeTone =
      typeMark.tone === 'pdf'
        ? 'bg-red-500/15 text-red-300'
        : typeMark.tone === 'docx'
          ? 'bg-blue-500/15 text-blue-200'
          : 'bg-white/[0.06] text-slate-400';
    const dateLabel = formatDeAtDateTime(p.updated || p.created);
    const highlighted = highlightRecordId === p.id;
    const favorite = favorites.some((row) => row.pb_record_id === p.id);
    const confirmingDelete = deleteConfirmId === p.id;
    const renaming = renameDocId === p.id;
    const rowShares = sharesForDocument(docShares, p.id);
    const visibility = describePortalDocumentVisibility({
      viewerId: customerId,
      ownerId: p.customer_id,
      akteId: p.akte_id,
      shareCount: rowShares.length,
    });
    const canRenameFile = canWriteDocuments && Boolean(p.nextcloud_path?.trim());
    const renameHint = canRenameFile
      ? 'Umbenennen'
      : !canWriteDocuments
        ? 'Umbenennen nur mit Schreibrecht'
        : 'Umbenennen braucht einen Nextcloud-Pfad';
    const openHint = openable
      ? 'Öffnen'
      : p.nc_url
        ? 'Öffnen (externer Link)'
        : 'Öffnen nicht möglich — kein Dokumentlink';

    return (
      <div
        key={p.id}
        ref={(node) => {
          rowRefs.current[p.id] = node;
        }}
        draggable={canWriteDocuments && Boolean(p.nextcloud_path?.trim()) && movingDocId !== p.id}
        onDragStart={(e) => {
          if (!p.nextcloud_path?.trim()) return;
          setDraggingDocId(p.id);
          startPortalDocumentDrag(e, p.id);
        }}
        onDragEnd={() => {
          setDraggingDocId(null);
          setDropTargetFolder(null);
          setAkteDropTargetId(null);
          setTileDropTarget('');
        }}
        onContextMenu={(e) => {
          if (!canWriteDocuments || !p.nextcloud_path?.trim()) return;
          e.preventDefault();
          closeSharePopover();
          setRowContextMenu({ docId: p.id, x: e.clientX, y: e.clientY });
        }}
        className={
          showPreview
            ? `flex w-full min-w-0 flex-nowrap items-center gap-2 px-3 py-1.5 hover:bg-slate-800/30 transition-colors ${
                highlighted ? 'bg-violet-500/15 ring-1 ring-violet-500/50 ring-inset' : ''
              } ${draggingDocId === p.id ? 'opacity-50' : ''}`
            : `grid grid-cols-[minmax(2.5rem,auto)_minmax(0,1fr)_auto] gap-2 items-center border-b border-violet-400/15 bg-[#07070a] px-3 py-2.5 transition-colors hover:bg-violet-600/10 ${
                highlighted ? 'bg-violet-500/15 ring-1 ring-violet-500/50 ring-inset' : ''
              } ${draggingDocId === p.id ? 'opacity-50' : ''}`
        }
      >
        <span
          className={
            showPreview
              ? `inline-flex w-12 shrink-0 items-center justify-center rounded px-1 py-0.5 text-center text-[10px] font-semibold ${typeTone}`
              : 'shrink-0 rounded border border-violet-400/30 bg-[#07070a] px-1 py-0.5 text-center text-[10px] font-semibold text-violet-200'
          }
        >
          {showPreview ? typeMark.label : typeLabel}
        </span>
        <div className={showPreview ? 'flex min-w-0 flex-1 items-center gap-2 overflow-visible' : 'min-w-0 overflow-hidden'}>
          {renaming ? (
            <input
              value={renameDocDraft}
              onChange={(e) => setRenameDocDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void renamePortalDocument(p);
                if (e.key === 'Escape') setRenameDocId(null);
              }}
              autoFocus
              className="w-full rounded border border-white/[0.12] bg-slate-900 px-2 py-1 text-xs text-slate-100"
              aria-label="Neuer Dateiname"
            />
          ) : (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                openProtokoll(p);
              }}
              title="Öffnen"
              aria-label={`${p.filename} öffnen`}
              className={
                showPreview
                  ? 'block min-w-[8rem] w-0 flex-1 overflow-visible text-left bg-transparent border-0 p-0 m-0 cursor-pointer'
                  : 'block w-full min-w-0 text-left bg-transparent border-0 p-0 m-0 cursor-pointer'
              }
            >
              {showPreview ? (
                <AkteDocLinkPreview
                  name={p.filename}
                  typeLabel={portalFileTypeMark(p.filename).label}
                  tone={portalFileTypeMark(p.filename).tone}
                  sizeBytes={p.file_size}
                  revisionId={p.last_revision_id}
                  createdIso={p.created}
                  updatedIso={p.updated}
                  pdfUrl={
                    portalAkteFirstPageMode(p.filename) === 'pdf' &&
                    activeAkte &&
                    portalDokumentBelongsToAkte(p, activeAkte)
                      ? p.nc_url
                      : undefined
                  }
                  pdfPath={
                    portalAkteFirstPageMode(p.filename) === 'pdf' &&
                    activeAkte &&
                    portalDokumentBelongsToAkte(p, activeAkte)
                      ? p.nextcloud_path
                      : undefined
                  }
                />
              ) : (
                <p className="text-white text-sm font-medium m-0 hover:text-violet-200 break-words line-clamp-2">{p.filename}</p>
              )}
            </button>
          )}
          <div
            className={
              showPreview
                ? 'flex shrink-0 flex-nowrap items-center gap-2 whitespace-nowrap text-[10px] text-slate-500'
                : 'mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-slate-500'
            }
          >
            {showPreview ? null : <Icon className={`w-3 h-3 flex-shrink-0 ${colorClass}`} aria-hidden />}
            {p.file_size != null ? (
              <span className="tabular-nums">{formatPortalFileSize(p.file_size)}</span>
            ) : null}
            {p.last_revision_id != null ? <span className="text-violet-300/80">OO Rev. {p.last_revision_id}</span> : null}
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" aria-hidden />
              {dateLabel}
            </span>
            <span className="text-slate-400" title={visibility.label}>
              {visibility.chip}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-1.5 shrink-0">
          {favorite ? (
            <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-200">
              Favorit
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => setFavorites((prev) => togglePortalDocFavorite(prev, shortcutFromProtokoll(p)))}
            title={favorite ? 'Favorit lösen' : 'Als Favorit merken'}
            aria-label={favorite ? 'Favorit lösen' : 'Als Favorit merken'}
            className={`p-1 rounded-md ${favorite ? 'text-amber-300' : 'text-slate-600 hover:text-amber-200'}`}
          >
            <Star className="w-3.5 h-3.5" aria-hidden fill={favorite ? 'currentColor' : 'none'} />
          </button>
          {confirmingDelete ? (
            <div className="flex items-center gap-1 rounded-md bg-red-500/10 px-1.5 py-0.5">
              <span className="text-[10px] text-red-200">Löschen?</span>
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="text-[10px] text-slate-400 hover:text-slate-200 px-1"
              >
                Nein
              </button>
              <button
                type="button"
                onClick={() => void archiveDocument(p.id)}
                disabled={!canWriteDocuments || archivingId === p.id}
                className="text-[10px] text-red-200 hover:text-white px-1"
              >
                Ja
              </button>
            </div>
          ) : renaming ? (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setRenameDocId(null)}
                className="text-[10px] text-slate-400 hover:text-slate-200"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void renamePortalDocument(p)}
                disabled={renameBusy || !canRenameFile}
                className="text-[10px] text-violet-200 hover:text-white"
              >
                Speichern
              </button>
            </div>
          ) : (
            <DokumenteRowActions
              onOpen={() => openProtokoll(p)}
              onRename={() => beginRenameDocument(p)}
              onShare={(e) => {
                const btn = e.currentTarget;
                if (sharePopover?.docId === p.id) {
                  closeSharePopover();
                  return;
                }
                shareAnchorRef.current = btn;
                const pos = placeSharePopover(btn.getBoundingClientRect(), {
                  width: window.innerWidth,
                  height: window.innerHeight,
                });
                setRowContextMenu(null);
                setSharePopover({ docId: p.id, ...pos });
              }}
              shareOpen={sharePopover?.docId === p.id}
              onMoveToAkte={(event) => beginMoveToAkte(p, event.currentTarget)}
              moveToAkteOpen={movePopover?.docId === p.id}
              onDelete={() => setDeleteConfirmId(p.id)}
              openDisabled={!openable && !p.nc_url}
              openHint={openHint}
              renameDisabled={!canRenameFile}
              renameHint={renameHint}
              shareHint="Teilen · wer sieht das"
              moveToAkteDisabled={!canWriteDocuments || !p.nextcloud_path?.trim()}
              moveToAkteHint={
                canWriteDocuments && p.nextcloud_path?.trim()
                  ? 'In Akte kopieren oder einfügen…'
                  : 'Zuordnen braucht Schreibrecht und einen Nextcloud-Pfad'
              }
              deleteDisabled={!canWriteDocuments}
              deleteHint={
                canWriteDocuments
                  ? 'Aus Liste entfernen (Nextcloud-Ablage bleibt)'
                  : 'Löschen nur mit Schreibrecht'
              }
              deleteBusy={archivingId === p.id}
              dragLocked={Boolean(draggingDocId)}
            />
          )}
        </div>
      </div>
    );
  };

  const hasAnyDocuments = protokolle.length > 0;
  const showIngestTabs = canPortalDocumentUpload && canPortalNcShareImport;
  const shareDoc = sharePopover
    ? protokolle.find((row) => row.id === sharePopover.docId) ?? null
    : null;
  const moveDoc = movePopover
    ? protokolle.find((row) => row.id === movePopover.docId) ?? null
    : null;

  return (
    <div
      data-testid={isAktenHub ? undefined : 'portal-kanzlei-dokumente'}
      className={
      isAktenHub && rahmenOpen
        ? 'flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden'
        : isAktenHub
          ? '@container w-full min-w-0 max-w-none overflow-x-hidden space-y-4 animate-fade-in'
          : `@container w-full min-w-0 max-w-none overflow-x-hidden space-y-3 px-3 py-3 animate-fade-in ${KANZLEI_DOC_GROUND}`
    }>
      {!isAktenHub ? (
      <header className={`flex flex-wrap items-end justify-between gap-3 px-3.5 py-3 ${KANZLEI_DOC_EDGE}`}>
        <div>
          <h2 className="text-sm font-semibold text-white m-0">Dokumente</h2>
          <p className="text-[11px] text-slate-500 m-0 mt-0.5">
            Ablegen, Ordner, Teilen und zuletzt geöffnet. Sichtbar ist, was Sie in dieser Kanzlei sehen dürfen.
          </p>
        </div>
      </header>
      ) : null}

      {isAktenHub && ingestMsg ? (
        <p
          role={portalIngestMsgLooksOk(ingestMsg) ? 'status' : 'alert'}
          data-testid="portal-akte-hub-ingest-msg"
          className={`m-0 rounded-lg border px-3 py-2 text-xs ${
            portalIngestMsgLooksOk(ingestMsg)
              ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200'
              : 'border-red-500/30 bg-red-500/10 text-red-100'
          }`}
        >
          {ingestMsg}
        </p>
      ) : null}

      {/* Zone: Hochladen + Nextcloud — nur Ablage, nicht im Akten-Reiter */}
      {!isAktenHub && (canPortalDocumentUpload || canPortalNcShareImport) && (
        <div className={`${KANZLEI_DOC_EDGE} w-full min-w-0`}>
          <div className="flex items-center justify-between gap-2 border-b border-violet-400/30 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <Upload className="w-3.5 h-3.5 text-violet-300 flex-shrink-0" />
              <span className="font-semibold text-slate-300 text-[12px]">Hinzufügen</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {canPortalDocumentUpload && (!showIngestTabs || ingestTab === 'upload') ? (
                <>
                  <button
                    type="button"
                    onClick={() => folderInputRef.current?.click()}
                    disabled={uploadBusy || folderUploadBusy}
                    className={KANZLEI_DOC_BTN}
                    title="Kompletten Ordner hochladen (Struktur bleibt erhalten)"
                  >
                    {folderUploadBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FolderUp className="w-3.5 h-3.5" />
                    )}
                    Ordner hochladen
                  </button>
                  <button
                    type="button"
                    data-testid="portal-doc-new"
                    onClick={() => void createBlankDocumentInAkte()}
                    disabled={uploadBusy || folderUploadBusy}
                    className={KANZLEI_DOC_BTN}
                    title="Leeres Word-Dokument erstellen und im Editor öffnen"
                  >
                    {uploadBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FilePenLine className="w-3.5 h-3.5" />
                    )}
                    Neues Dokument
                  </button>
                </>
              ) : null}
            {showIngestTabs && (
              <div className="flex gap-1" role="tablist" aria-label="Quelle">
                <button
                  type="button"
                  role="tab"
                  aria-selected={ingestTab === 'upload'}
                  aria-label="Hochladen"
                  title="Hochladen"
                  onClick={() => setIngestTab('upload')}
                  className={ingestTab === 'upload' ? KANZLEI_DOC_ICON_ON : KANZLEI_DOC_ICON}
                >
                  <Upload className="h-3.5 w-3.5" aria-hidden />
                  <span className="sr-only">Hochladen</span>
                </button>
              </div>
            )}
            </div>
          </div>

          <div className="space-y-2 px-3 py-2">
            {/* DnD critical — do not revert (button→div, Ordner ohne Files-Guard). Fix: 5b210c4 · lessons.md 11.07.2026 */}
            {canPortalDocumentUpload && (!showIngestTabs || ingestTab === 'upload') && (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = portalDragIsInternalDoc(e.dataTransfer, draggingDocId)
                    ? 'move'
                    : 'copy';
                  setDragOver(true);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                  setDragOver(false);
                }}
                onDrop={(e) => {
                  const draggedId = readPortalDragDocId(e.dataTransfer, draggingDocId);
                  if (isAktenHub && draggedId && akteBrowseId && (browseAkte || focusedAkte)) {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(false);
                    void handleTileDrop('cases', e);
                    return;
                  }
                  void handleDrop(e);
                }}
                className={`rounded-lg border border-dashed px-3 py-2 transition-colors ${
                  dragOver
                    ? 'border-violet-400 bg-violet-600/10'
                    : 'border-violet-400/30 hover:border-violet-400/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={PORTAL_DOCUMENT_FILE_ACCEPT}
                  className="hidden"
                  onChange={(e) => handleSelectedFile(e.target.files?.[0] || null)}
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  // @ts-expect-error webkitdirectory ist nicht in allen TS-DOM-Typen
                  webkitdirectory=""
                  onChange={handleFolderInputChange}
                />
                {uploadFile ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-8 h-8 text-violet-300 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{uploadFile.name}</p>
                        <p className="text-slate-500 text-xs">
                          {(uploadFile.size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <PortalSwitch
                        checked={openAfterSave}
                        onChange={(next) => {
                          setOpenAfterSave(next);
                          savePortalUploadOpenAfterSave(customerId, next);
                        }}
                        label="Sofort öffnen"
                        size="sm"
                        accent="violet"
                        title="Nach dem Speichern in OnlyOffice öffnen. Standard: aus."
                        data-testid="portal-switch-open-after-save"
                      />
                      <label
                        className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 select-none whitespace-nowrap"
                        title="Optional: Datei beim Speichern einer Akte zuordnen. Default: keine Akte."
                      >
                        <span className="hidden sm:inline">In Akte speichern</span>
                        <select
                          value={uploadAkteId}
                          onChange={(e) => setUploadAkteId(e.target.value)}
                          className="max-w-[10.5rem] rounded-md border border-violet-400/30 bg-[#07070a] px-1.5 py-0.5 text-[11px] text-slate-200"
                          aria-label="In Akte speichern (optional)"
                        >
                          <option value="">Keine Akte</option>
                          {portalAkten.map((akteRow) => (
                            <option key={akteRow.id} value={akteRow.id}>
                              {akteRow.title}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        onClick={() => setUploadFile(null)}
                        className="p-1.5 text-slate-500 hover:text-red-400"
                        aria-label="Entfernen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void saveUploadFile()}
                        disabled={uploadBusy}
                        className={KANZLEI_DOC_BTN}
                      >
                        {uploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Speichern
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    className="flex w-full cursor-pointer items-center gap-3 text-left"
                  >
                    <Upload className="h-5 w-5 shrink-0 text-violet-300/70" />
                    <span className="min-w-0">
                    <p className="m-0 text-sm text-slate-300">Datei ablegen oder wählen</p>
                    <p className="m-0 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-600">
                      <span>PDF, Fotos (JPG/PNG), Word, Tabellen, Text</span>
                      <span className="text-slate-700">·</span>
                      <span className="inline-flex items-center gap-1">
                        <FolderUp className="w-3 h-3" /> Ordner mit Struktur
                      </span>
                      <span className="text-slate-700">·</span>
                      <span className="inline-flex items-center gap-1">
                        <Headphones className="w-3 h-3" /> Audio → Diktat
                      </span>
                    </p>
                    <p className="m-0 mt-0.5 max-w-2xl text-[10px] leading-snug text-amber-300/70">
                      {PORTAL_IMAGE_OCR_NOTICE}
                    </p>
                    </span>
                  </div>
                )}
              </div>
            )}

            {canPortalNcShareImport && (!showIngestTabs || ingestTab === 'nextcloud') && (
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <input
                  type="url"
                  inputMode="url"
                  value={ncShareUrlInput}
                  onChange={(e) => setNcShareUrlInput(e.target.value)}
                  placeholder="Nextcloud-Freigabe-Link einfügen…"
                  autoComplete="off"
                  className="flex-1 min-w-0 rounded-md border border-violet-400/30 bg-[#07070a] px-3 py-2 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-violet-400"
                />
                <button
                  type="button"
                  onClick={() => void handleNextcloudShareImport()}
                  disabled={ncShareBusy || !ncShareUrlInput.trim()}
                  className={KANZLEI_DOC_BTN}
                >
                  {ncShareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
                  Importieren
                </button>
                <PortalSwitch
                  checked={openAfterSave}
                  onChange={(next) => {
                    setOpenAfterSave(next);
                    savePortalUploadOpenAfterSave(customerId, next);
                  }}
                  label="Sofort öffnen"
                  size="sm"
                  accent="violet"
                  title="Nach dem Speichern in OnlyOffice öffnen. Standard: aus."
                />
              </div>
            )}

            {ingestMsg && (
              <div
                onDragOver={(e) => {
                  if (!isAktenHub || !(browseAkte || focusedAkte)) return;
                  allowPortalAkteDragOver(e, draggingDocId);
                }}
                onDrop={(e) => {
                  if (!isAktenHub) return;
                  const target = browseAkte || focusedAkte;
                  if (!target) return;
                  e.preventDefault();
                  e.stopPropagation();
                  void assignDroppedToAkte(e, target);
                }}
              >
              <p
                className={`text-xs m-0 ${
                  portalIngestMsgLooksOk(ingestMsg) ? 'text-emerald-400' : 'text-red-200'
                }`}
                role={portalIngestMsgLooksOk(ingestMsg) ? 'status' : 'alert'}
              >
                {ingestMsg}
              </p>
              </div>
            )}
            {folderUploadProgress ? (
              <p className="text-xs text-violet-300 m-0">
                Ordner-Upload: {folderUploadProgress.done}/{folderUploadProgress.total}…
              </p>
            ) : null}
          </div>
        </div>
      )}

      <div
        className={
          isAktenHub && rahmenOpen
            ? 'flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden'
            : `grid w-full min-w-0 grid-cols-1 gap-3 items-stretch ${isAktenHub ? '' : TILE_SPLIT_DOCS}`
        }
      >
        <section
          className={`${isAktenHub ? 'hidden' : ''} ${dokumenteTileChromeClass(focusedTile === 'documents', tileDropTarget === 'documents')}`}
          onClick={() => setFocusedTile('documents')}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = portalDragIsInternalDoc(e.dataTransfer, draggingDocId)
              ? 'move'
              : 'copy';
            setTileDropTarget('documents');
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setTileDropTarget('');
          }}
          onDrop={(e) => void handleTileDrop('documents', e)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setFocusedTile('documents');
          }}
          aria-label="Dokumente / Ordner fokussieren"
        >
          <header className="border-b border-violet-400/30 bg-[#07070a] px-3.5 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-white m-0">
                  {isAktenHub ? 'Zum Zuordnen' : 'Dokumente / Ordner'}
                </h3>
                <p className="text-[11px] text-slate-500 m-0 mt-0.5">
                  {isAktenHub
                    ? 'Unzugeordnete Dateien auf eine Akte ziehen'
                    : 'PDF, DOCX, Ordner, Favoriten — ohne Akte nutzbar'}
                </p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${focusedTile === 'documents' ? 'bg-violet-500/20 text-violet-200' : 'text-slate-500'}`}>
                {focusedTile === 'documents' ? 'Aktiv' : 'Fokussieren'}
              </span>
            </div>
          </header>
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <div className="px-3 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
            <span className="font-semibold text-slate-300 text-[12px]">Dokumente / Ordner</span>
          </div>
          <span className="text-[10px] text-slate-500">
            {activeAkte
              ? `${poolDocumentGroups.reduce((n, g) => n + g.documents.length, 0)} ohne Akte`
              : `${filteredProtokolle.length} Dokument${filteredProtokolle.length === 1 ? '' : 'e'} · zuletzt bearbeitet`}
          </span>
        </div>

        <div className="space-y-1.5 border-b border-violet-400/30 bg-[#07070a] px-3 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Dokumente und Aktenordner suchen…"
              aria-label="Dokumente und Aktenordner durchsuchen"
              className="w-full rounded-md border border-violet-400/30 bg-[#07070a] py-2 pl-9 pr-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-violet-400 focus:outline-none"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter">
            {DOC_FILTER_CHIPS.map((chip) => (
              <PortalChip
                key={chip.id}
                label={chip.label}
                accent="violet"
                selected={groupFilter === chip.id}
                onClick={() => setGroupFilter(chip.id)}
                className="!px-2.5 !py-1 !text-[11px]"
              />
            ))}
          </div>
          {isAktenHub && activeAkte ? (
            <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 px-3 py-2">
              <p className="text-[11px] text-violet-100 m-0 min-w-0 break-words">
                Datei auf die Akte rechts ziehen — das legt eine Kopie.
              </p>
            </div>
          ) : !isAktenHub ? (
            <div className="rounded-lg border border-violet-400/30 bg-[#07070a] px-3 py-2">
              <p className="text-[11px] text-slate-400 m-0">
                In eine Akte: Icon „in Akte“ — kopieren oder einfügen. Anlegen im Reiter Akten
                {onGoToAkten ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={onGoToAkten}
                      className="text-violet-300 hover:text-violet-100 underline-offset-2 hover:underline"
                    >
                      öffnen
                    </button>
                  </>
                ) : null}
                . Optional.
              </p>
            </div>
          ) : null}
          <details className={`group ${KANZLEI_DOC_EDGE}`}>
            <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-[11px] text-violet-100">
              <KanzleiDocCube
                front={<Folder className="h-3.5 w-3.5" aria-hidden />}
                back={<FolderOpen className="h-3.5 w-3.5" aria-hidden />}
              />
              <span>Ordner & Ablageziel · {portalFolderDisplayPath(currentFolder)}</span>
            </summary>
            <div className="space-y-1.5 border-t border-violet-400/30 px-2.5 py-1.5">
              <div className="flex flex-col gap-2 sm:flex-row">
                {explorerCanGoUp ? (
                  <button
                    type="button"
                    onClick={() => openExplorerFolder(portalExplorerParentFolder(explorerCwd, explorerRoot))}
                    className={KANZLEI_DOC_ICON}
                    title="Übergeordneter Ordner"
                    aria-label="Zurück zur übergeordneten Ablage"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                ) : null}
                <select
                  value={currentFolder}
                  onChange={(e) => {
                    setCurrentFolder(e.target.value);
                    setShareLink(null);
                  }}
                  className="min-w-0 flex-1 rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1 text-xs text-slate-200"
                  aria-label="Ablageziel wählen"
                >
                  <option value="">Alle Dokumente / Wurzel</option>
                  {knownFolders.map((folder) => (
                    <option key={folder} value={folder}>
                      {portalFolderDisplayPath(folder)}
                    </option>
                  ))}
                </select>
                {canWriteDocuments ? (
                  <button
                    type="button"
                    onClick={() => void shareCurrentFolder()}
                    disabled={shareBusy}
                    className={KANZLEI_DOC_ICON}
                    title="Freigeben"
                    aria-label="Freigeben"
                  >
                    {shareBusy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Share2 className="h-3.5 w-3.5" aria-hidden />
                    )}
                    <span className="sr-only">Freigeben</span>
                  </button>
                ) : null}
              </div>
              {canWriteDocuments ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Unterordner im Ablageziel…"
                    className="min-w-0 flex-1 rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1 text-xs text-slate-200 placeholder:text-slate-600 focus:border-violet-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => void createFolder()}
                    disabled={folderBusy || !newFolderName.trim()}
                    data-portal-shortcut="neu"
                    aria-keyshortcuts={neuShortcut}
                    title={neuShortcut}
                    className={KANZLEI_DOC_BTN}
                  >
                    {folderBusy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FolderPlus className="w-4 h-4" />
                    )}
                    Ordner erstellen
                    <PortalShortcutHint id="neu" className="border-white/25 text-white/70" />
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-slate-500 m-0">
                  Praktikant / Leih: Akte nur lesen — kein Upload, Speichern oder Löschen.
                </p>
              )}
              {shareLink ? (
                <div className="flex flex-col gap-2 rounded-lg border border-violet-400/30 bg-[#07070a] px-3 py-2 sm:flex-row sm:items-center">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Link2 className="w-4 h-4 text-violet-300 flex-shrink-0" />
                    <span className="text-xs text-slate-300 truncate">{shareLink}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void navigator.clipboard.writeText(shareLink)}
                    className={KANZLEI_DOC_ICON}
                    title="Kopieren"
                    aria-label="Kopieren"
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                    <span className="sr-only">Kopieren</span>
                  </button>
                </div>
              ) : null}
              {ncFoldersLoading ? (
                <p className="flex items-center gap-1 text-[10px] text-slate-600 m-0">
                  <Loader2 className="w-3 h-3 animate-spin" /> Ordner werden aktualisiert…
                </p>
              ) : null}
            </div>
          </details>
        </div>

        {protokolleLoading && !hasAnyDocuments ? (
          <div className="flex items-center gap-2 px-5 py-4 text-slate-500 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Dokumentenliste wird geladen…
          </div>
        ) : explorerSearching && poolExplorerDocs.length > 0 ? (
          <div className="divide-y divide-violet-400/15">
            {poolExplorerDocs.map(renderDocumentRow)}
          </div>
        ) : !explorerSearching && (poolHasRows || poolInSubfolder) ? (
          <div>
            {renderExplorerBreadcrumb(poolCwd, '', isAktenHub ? 'Ohne Akte' : 'Ablage', openExplorerFolder, !isAktenHub)}
            <div className="divide-y divide-violet-400/20">
              {poolChildFolders.map((folder) => renderExplorerFolderRow(folder, { kanzleiLook: !isAktenHub }))}
            </div>
            {poolExplorerDocs.length > 0 ? (
              <div className="divide-y divide-violet-400/15 border-t border-violet-400/30">
                {poolExplorerDocs.map(renderDocumentRow)}
              </div>
            ) : poolChildFolders.length > 0 ? (
              <p className="px-4 py-3 text-[11px] text-slate-500 m-0">
                Ordner öffnen, um die Dokumente darin zu sehen. Leere Ordner bleiben sichtbar.
              </p>
            ) : (
              <p className="px-5 py-6 text-center text-slate-600 text-xs m-0">
                {isAktenHub
                  ? 'Noch keine unzugeordneten Dateien in diesem Ordner — unter Dokumente hochladen.'
                  : 'Dieser Ordner ist leer — das ist in Ordnung. Oben hochladen, eine Akte ist nicht nötig.'}
              </p>
            )}
          </div>
        ) : (
          <div className="px-5 py-6 text-center text-slate-600 text-xs space-y-1">
            {searchQuery.trim() || groupFilter !== 'alle' ? (
              <>
                <p className="text-slate-400 text-sm m-0">Keine Treffer für Suche oder Filter</p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setGroupFilter('alle');
                  }}
                  className="text-xs text-violet-300 hover:text-violet-100"
                >
                  Filter zurücksetzen
                </button>
              </>
            ) : activeAkte && isAktenHub ? (
              <p className="m-0">Keine Dateien zum Zuordnen — Datei auf die Akte rechts ziehen oder unter Dokumente hochladen.</p>
            ) : (
              <p className="m-0">
                {isAktenHub
                  ? 'Noch keine unzugeordneten Dateien — unter Dokumente hochladen, dann hier zuordnen.'
                  : 'Noch keine Dokumente — oben hochladen. Eine Akte ist nicht nötig.'}
              </p>
            )}
          </div>
        )}
            <p
              className={`m-3 mt-auto rounded-md border border-dashed px-3 py-2 text-center text-[11px] ${
                tileDropTarget === 'documents'
                  ? 'border-violet-400/50 bg-violet-500/10 text-violet-200'
                  : 'border-violet-400/30 text-slate-500'
              }`}
            >
              {isAktenHub
                ? activeAkte
                  ? 'Drop: Datei auf die Akte rechts ziehen'
                  : 'Drop: im Pool ablegen oder einer Akte zuordnen'
                : 'Drop: im Dokumentenbaum ablegen'}
            </p>
          </div>
        </section>

        {isAktenHub ? (
        <section
          className={
            rahmenOpen
              ? 'flex h-full min-h-0 flex-1 flex-col overflow-hidden border-0 bg-transparent'
              : 'min-w-0 flex flex-col overflow-hidden border-0 bg-transparent'
          }
          onClick={() => setFocusedTile('cases')}
          onDragOver={(e) => {
            allowPortalAkteDragOver(e, draggingDocId);
            setTileDropTarget('cases');
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setTileDropTarget('');
              setAkteDropTargetId(null);
            }
          }}
          onDrop={(e) => void handleTileDrop('cases', e)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setFocusedTile('cases');
          }}
          aria-label="Akten fokussieren"
        >
          <div className={
            rahmenOpen
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
              : 'flex-1 min-h-0 overflow-y-auto p-3 space-y-3 custom-scrollbar'
          }>
            {!rahmenOpen ? (
            <>
            <section
              data-testid="portal-akte-list-kopf"
              className="space-y-3 rounded-lg border border-violet-400/30 bg-violet-500/5 px-3.5 py-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-white m-0">Akten</h3>
                  <p className="text-[11px] text-slate-500 m-0 mt-0.5">Anlegen, öffnen und am Akt arbeiten</p>
                </div>
                {portalRole !== 'praktikant' ? (
                  <button
                    type="button"
                    data-testid="portal-akte-neue-kopf"
                    data-portal-shortcut="neu"
                    aria-keyshortcuts={neuShortcut}
                    title={neuShortcut}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFocusedTile('cases');
                      setCreateAkteRequestId((n) => n + 1);
                    }}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500"
                  >
                    <Plus className="w-3 h-3" aria-hidden />
                    Neue Akte
                    <PortalShortcutHint id="neu" className="border-white/25 text-white/70" />
                  </button>
                ) : null}
              </div>
              <label className="relative block">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-500" aria-hidden />
                <input
                  type="search"
                  value={akteListQuery}
                  onChange={(event) => setAkteListQuery(event.target.value)}
                  placeholder="Name, Datum, Ereignis"
                  data-testid="portal-akte-list-search"
                  aria-label="Akten suchen"
                  className="w-full rounded-md border border-white/10 bg-transparent py-1.5 pl-7 pr-2 text-[11px] text-slate-200 placeholder:text-slate-600 outline-none focus:border-white/25"
                />
              </label>
              <p className="m-0 text-[10px] text-slate-500">
                {portalRole === 'it_admin' || isPrimary
                  ? 'Sichtbar: Akten mit Mitgliedschaft, plus Überblick aller Kanzlei-Akten (IT-Admin).'
                  : 'Sichtbar: nur Akten, in denen Sie Mitglied sind. Ordner ohne Akte bleiben kanzleiweit.'}
              </p>
            </section>
            <PortalAktenwaechterPanel
              customerId={customerId}
              kanzleiId={kanzleiId}
              portalRole={portalRole}
              isPrimary={isPrimary}
              authToken={authToken}
              aktePath={currentFolder || undefined}
              showAktenPicker
              linkableDocuments={protokolle}
              completedByEmail={completedByEmail}
              onAktenChange={handleAktenChange}
              onMemberAkteIdsChange={handleMemberAkteIdsChange}
              onFocusedAkteChange={handleFocusedAkteChange}
              onUploadFirstDocument={ingestPortalFile}
              onAssignDropped={(e, akte) => assignDroppedToAkte(e, akte)}
              preferredAkteId={boundAkteId || akteBrowseId}
              openCreateRequestId={createAkteRequestId}
              hideCreateButton
              onCaseListQueryChange={setAkteListQuery}
              showMembers={false}
              forceExpanded
              className="mx-0 border-0 bg-transparent rounded-none"
            />
            {akteListQuery.trim() && listedAkten.length === 0 ? (
              <p className="m-0 text-[11px] text-slate-500" data-testid="portal-akte-list-search-empty">
                Keine Akte zu dieser Suche.
              </p>
            ) : null}
            {listedAkten.length > 0 ? (
              <div className="space-y-1.5">
                {listedAkten.map((akteRow) => {
                  const isFocused = (browseAkte || focusedAkte)?.id === akteRow.id;
                  const renaming = renameAkteId === akteRow.id;
                  const dropping = akteDropTargetId === akteRow.id;
                  const overview = isItAdminAkteOverview(
                    { id: customerId, portal_role: portalRole, is_primary: isPrimary },
                    akteRow,
                    memberAkteIds,
                  );
                  const expanded = expandedAkteId === akteRow.id;
                  const rowAnchors = expanded
                    ? buildAkteVerlaufAnchors({
                        akteId: akteRow.id,
                        today: akteLocalToday(),
                        fristen: akteFristen,
                        lastDocument: (() => {
                          const recent = loadRecentPortalDocuments(customerId, akteRow.id)[0];
                          if (!recent?.display_name?.trim() || !recent.document_url?.trim() || !recent.pb_record_id) {
                            return null;
                          }
                          return {
                            id: recent.pb_record_id,
                            title: recent.display_name.trim(),
                            akteId: akteRow.id,
                          };
                        })(),
                        chatId: arkoBooked ? peekAkteChatId(customerId, akteRow.id) : '',
                        arkoBooked,
                      })
                    : [];
                  return (
                    <div
                      key={akteRow.id}
                      className={`rounded-md border bg-transparent ${
                        dropping
                          ? 'border-white/30'
                          : isFocused
                            ? 'border-white/15'
                            : 'border-white/[0.06]'
                      }`}
                    >
                    <div
                      className="grid grid-cols-[minmax(2.5rem,auto)_minmax(0,1fr)_auto] gap-2 items-center min-h-[3rem] px-2 py-2.5 cursor-pointer"
                      onClick={() => setExpandedAkteId((current) => toggleAkteRowLage(current, akteRow.id))}
                      data-testid="portal-akte-row"
                      onDragOver={(e) => {
                        allowPortalAkteDragOver(e, draggingDocId);
                        setAkteDropTargetId(akteRow.id);
                        setTileDropTarget('cases');
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                          setAkteDropTargetId((cur) => (cur === akteRow.id ? null : cur));
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAkteDropTargetId(null);
                        setTileDropTarget('');
                        void assignDroppedToAkte(e, akteRow);
                      }}
                    >
                      <span className="shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold text-center bg-white/[0.05] text-slate-400">
                        AKTE
                      </span>
                      <div className="min-w-0 overflow-hidden">
                        {renaming ? (
                          <input
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') void renameFocusedAkte(akteRow.id);
                              if (e.key === 'Escape') setRenameAkteId(null);
                            }}
                            className="w-full rounded border border-white/[0.12] bg-slate-900 px-2 py-1 text-xs text-slate-100"
                            aria-label="Neuer Akten-Titel"
                          />
                        ) : (
                          <p className="text-xs text-slate-100 break-words line-clamp-2 m-0">{akteRow.title}</p>
                        )}
                        {(() => {
                          const gegen = akteRow.gegenseite_label?.trim() || '';
                          const frist = nextAkteFrist(akteFristen, akteRow.id, akteLocalToday());
                          if (!gegen && !frist) return null;
                          return (
                            <p className="text-[10px] text-slate-400 m-0 mt-0.5 break-words line-clamp-2" data-testid="portal-akte-row-facts">
                              {gegen ? <span>Gegenseite {gegen}</span> : null}
                              {gegen && frist ? ' · ' : null}
                              {frist ? <span>{frist.name} {formatAkteDay(frist.datum)}</span> : null}
                            </p>
                          );
                        })()}
                        <p className="text-[10px] text-slate-600 m-0 break-words line-clamp-2">
                          {overview
                            ? 'Überblick (IT-Admin, nicht im Kreis)'
                            : portalAkteCustomerFolderHint(akteRow.nc_path)}
                          {isFocused ? ' · fokussiert' : ''}
                        </p>
                      </div>
                      {renaming ? (
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setRenameAkteId(null)}
                            className="text-[10px] text-slate-400 hover:text-slate-200"
                          >
                            Abbrechen
                          </button>
                          <button
                            type="button"
                            onClick={() => void renameFocusedAkte(akteRow.id)}
                            disabled={renameBusy || !canWriteDocuments}
                            className="text-[10px] text-violet-200 hover:text-white"
                          >
                            Speichern
                          </button>
                        </div>
                      ) : (
                        <DokumenteRowActions
                          onRename={() => {
                            setRenameAkteId(akteRow.id);
                            setRenameDraft(akteRow.title);
                          }}
                          onDelete={() => setIngestMsg(AKTE_DELETE_HINT)}
                          renameDisabled={!canWriteDocuments}
                          renameHint={canWriteDocuments ? 'Umbenennen' : 'Umbenennen nur mit Schreibrecht'}
                          deleteDisabled
                          deleteHint={AKTE_DELETE_HINT}
                          dragLocked={Boolean(draggingDocId)}
                        />
                      )}
                    </div>
                    {expanded ? (
                      <div
                        data-testid="portal-akte-row-lage"
                        className="space-y-2 border-t border-white/[0.06] px-2 py-2"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-baseline gap-x-2 gap-y-1">
                          <span className="text-[10px] text-slate-500">Akte</span>
                          <span className="text-[11px] text-slate-100 break-words">{akteRow.title}</span>
                          {akteRow.mandant_label?.trim() ? (
                            <>
                              <span className="text-[10px] text-slate-500">Mandant</span>
                              <span className="text-[11px] text-slate-200 break-words">{akteRow.mandant_label.trim()}</span>
                            </>
                          ) : null}
                          {akteRow.gegenseite_label?.trim() ? (
                            <>
                              <span className="text-[10px] text-slate-500">Gegenseite</span>
                              <span className="text-[11px] text-slate-200 break-words">{akteRow.gegenseite_label.trim()}</span>
                            </>
                          ) : null}
                          {lageSeatLabels.length > 0 ? (
                            <>
                              <span className="text-[10px] text-slate-500">Sichtbar</span>
                              <span className="text-[11px] text-slate-200 break-words" data-testid="portal-akte-row-seats">
                                {lageSeatLabels.join(' · ')}
                              </span>
                            </>
                          ) : null}
                          {lageChecklistStand ? (
                            <>
                              <span className="text-[10px] text-slate-500">Checkliste</span>
                              <span className="text-[11px] text-slate-200" data-testid="portal-akte-row-checklist">
                                {lageChecklistStand}
                              </span>
                            </>
                          ) : null}
                        </div>
                        {rowAnchors.map((anchor) => (
                          <button
                            key={anchor.kind}
                            type="button"
                            data-testid={`portal-akte-row-${anchor.kind}`}
                            onClick={() => openAkte(
                              akteRow,
                              akteLageSection(anchor.kind),
                              anchor.kind === 'dokument' ? anchor.id : undefined,
                            )}
                            className="grid w-full grid-cols-[5.5rem_minmax(0,1fr)] items-baseline gap-x-2 rounded-md px-0 py-0.5 text-left hover:bg-white/[0.04]"
                          >
                            <span className="text-[10px] text-slate-500">
                              {anchor.kind === 'frist' ? 'Frist' : anchor.kind === 'dokument' ? 'Dokument' : 'Chat'}
                            </span>
                            <span className="text-[11px] text-slate-200 break-words">
                              {anchor.kind === 'frist'
                                ? `${anchor.name} ${formatAkteDay(anchor.datum)}`
                                : anchor.kind === 'dokument'
                                  ? anchor.title
                                  : null}
                            </span>
                          </button>
                        ))}
                        <button
                          type="button"
                          data-testid="portal-akte-row-open"
                          onClick={() => openAkte(akteRow)}
                          className="inline-flex items-center rounded-md bg-violet-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-violet-500"
                        >
                          Öffnen
                        </button>
                      </div>
                    ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            </>
            ) : null}
            <div className={rahmenOpen ? 'flex h-full min-h-0 flex-1 flex-col' : 'hidden'}>
            {rahmenOpen && activeAkte && focusedAkteDocumentGroup ? (
              <div
                data-testid="portal-akte-hub"
                data-akte-id={activeAkte.id}
                className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden"
              >
              <PortalAkteRahmenChrome
                akteTitle={activeAkte.title}
                sectionId={rahmenSection}
                sections={rahmenSections}
                onBack={() => {
                  rememberPortalAkteRahmenOpen(null);
                  setRahmenOpen(false);
                  onCloseAkteOnlyoffice?.();
                }}
                onSection={selectRahmenSection}
              >

                {rahmenSection === 'verlauf' ? (
                  <div
                    role="tabpanel"
                    data-testid="portal-akte-rahmen-panel-verlauf"
                    className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3"
                  >
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600 m-0">
                          Mandant
                        </p>
                        <p className="text-xs text-slate-200 m-0 mt-1">
                          {activeAkte.mandant_label || 'Noch nicht hinterlegt'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600 m-0">
                          Gegenseite
                        </p>
                        <p className="text-xs text-slate-200 m-0 mt-1">
                          {activeAkte.gegenseite_label || 'Noch nicht hinterlegt'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-slate-300 m-0 mb-1.5">
                        Wer diesen Akt sieht
                      </p>
                      {kanzleiId?.trim() && authToken?.trim() ? (
                        <PortalAkteMitgliederPanel
                          akte={activeAkte}
                          authToken={authToken.trim()}
                          actorId={customerId}
                          kanzleiId={kanzleiId.trim()}
                          portalRole={portalRole}
                          isPrimary={isPrimary}
                        />
                      ) : (
                        <p className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200 m-0">
                          Der Zugriffskreis wird ohne gültige Sitzung nicht geladen.
                        </p>
                      )}
                    </div>
                    {verlaufAnchors.length > 0 ? (
                      <div className="space-y-1.5" data-testid="portal-akte-verlauf-lage">
                        {verlaufAnchors.map((anchor) => (
                          <button
                            key={anchor.kind}
                            type="button"
                            data-testid={`portal-akte-verlauf-${anchor.kind}`}
                            onClick={() => {
                              const section = akteLageSection(anchor.kind);
                              if (anchor.kind === 'dokument') {
                                const recent = loadRecentPortalDocuments(customerId, activeAkte.id)[0];
                                if (recent?.document_url?.trim() && recent.pb_record_id === anchor.id) {
                                  onOpenAkteOnlyoffice?.({
                                    display_name: recent.display_name,
                                    document_url: recent.document_url,
                                    pb_record_id: recent.pb_record_id,
                                    nextcloud_path: recent.nextcloud_path,
                                    akte_id: activeAkte.id,
                                  });
                                }
                              }
                              selectRahmenSection(section);
                            }}
                            className="block w-full rounded-md border border-white/[0.06] bg-transparent px-3 py-2 text-left text-xs text-slate-200 hover:bg-white/[0.04]"
                          >
                            {anchor.kind === 'frist'
                              ? `${anchor.name} ${formatAkteDay(anchor.datum)}`
                              : anchor.kind === 'dokument'
                                ? anchor.title
                                : 'Chat dieser Akte'}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="m-0 text-sm text-slate-400">Noch kein Verlauf in dieser Akte.</p>
                    )}
                    <div className="space-y-2 border-t border-white/[0.06] pt-3" data-testid="portal-akte-checklist">
                      <div className="flex items-center gap-1.5">
                        <p className="m-0 text-[11px] font-semibold text-slate-300">Checkliste</p>
                        <button
                          type="button"
                          data-testid="portal-akte-checklist-undo"
                          title="Rückgängig"
                          aria-label="Rückgängig"
                          disabled={checklistUndoCount === 0 || Boolean(checklistBusy)}
                          onClick={() => void undoChecklistStep()}
                          className={
                            checklistUndoCount === 0
                              ? 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/20 bg-transparent text-violet-300/35'
                              : 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:opacity-45'
                          }
                        >
                          <Undo2 className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                      {checklistView.length > 0 ? (
                        <ul className="m-0 list-none space-y-1 p-0">
                          {checklistView.map((point) => {
                            const done = point.status === 'done' || point.status === 'waived';
                            const renaming = checklistRenameId === point.itemId;
                            const modesOpen = checklistMenuId === point.itemId;
                            return (
                              <li
                                key={point.itemId}
                                className="flex items-center gap-2 text-[11px] text-slate-200"
                                onMouseEnter={() => setChecklistMenuId(point.itemId)}
                                onMouseLeave={() => setChecklistMenuId(null)}
                              >
                                <button
                                  type="button"
                                  aria-pressed={done}
                                  data-testid={`portal-akte-checklist-toggle-${point.itemId}`}
                                  disabled={checklistBusy === point.itemId}
                                  onClick={() => void toggleChecklistPoint(point.itemId)}
                                  className="h-3.5 w-3.5 shrink-0 rounded-sm border border-white/20 bg-transparent"
                                >
                                  {done ? <span className="block h-full w-full bg-white/70" /> : null}
                                </button>
                                <ChecklistRowModes
                                  open={modesOpen}
                                  favorite={point.favorite}
                                  onDelete={() => {
                                    setChecklistMenuId(null);
                                    void deleteChecklistPoint(point.itemId);
                                  }}
                                  onEdit={() => {
                                    setChecklistRenameDraft(point.label);
                                    setChecklistRenameId(point.itemId);
                                    setChecklistMenuId(null);
                                  }}
                                  onFavorite={() => void favoriteChecklistPoint(point.itemId)}
                                />
                                {renaming ? (
                                  <input
                                    value={checklistRenameDraft}
                                    onChange={(event) => setChecklistRenameDraft(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        event.currentTarget.blur();
                                      }
                                      if (event.key === 'Escape') setChecklistRenameId(null);
                                    }}
                                    onBlur={() => {
                                      const plan = planCustomChecklistPoint(checklistRenameDraft);
                                      if (plan.ok === false) {
                                        setChecklistRenameId(null);
                                        return;
                                      }
                                      void renameChecklistPoint(point.itemId, plan.label);
                                    }}
                                    autoFocus
                                    aria-label="Punkt umbenennen"
                                    data-testid="portal-akte-checklist-rename-input"
                                    className="min-w-0 flex-1 rounded border border-white/15 bg-transparent px-1 py-0.5 text-[11px] text-slate-100"
                                  />
                                ) : (
                                  <span className={`min-w-0 ${done ? 'text-slate-500 line-through' : point.favorite ? 'text-violet-200' : ''}`}>
                                    {point.favorite ? (
                                      <span className="mr-1 text-[10px] text-violet-300" aria-hidden>
                                        ★
                                      </span>
                                    ) : null}
                                    {point.label}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                      <form
                        className="flex items-center gap-1.5"
                        onSubmit={(event) => {
                          event.preventDefault();
                          void addCustomChecklistPoint();
                        }}
                      >
                        <button
                          type="submit"
                          data-testid="portal-akte-checklist-add"
                          disabled={checklistBusy === 'custom'}
                          className="inline-flex shrink-0 items-center rounded-md border border-white/15 px-2 py-1 text-[10px] font-semibold text-slate-200 hover:bg-white/[0.04] disabled:opacity-45"
                        >
                          Punkt hinzufügen
                        </button>
                        <input
                          type="text"
                          value={customChecklistName}
                          onChange={(event) => setCustomChecklistName(event.target.value)}
                          data-testid="portal-akte-checklist-name"
                          aria-label="Eigener Checklistenpunkt"
                          placeholder="Eigener Punkt"
                          className="min-w-0 flex-1 rounded-md border border-white/10 bg-transparent px-2 py-1 text-[11px] text-slate-200 placeholder:text-slate-600"
                        />
                      </form>
                    </div>
                  </div>
                ) : null}

                {rahmenSection === 'dokumente' ? (
                  <div
                    role="tabpanel"
                    data-testid="portal-akte-hub-panel-dokumente"
                    data-akte-rahmen-panel="dokumente"
                    data-drop-class="AKTE_DOC_DND_OR_SKIP"
                    className={`min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-3 transition-colors ${
                      akteDropTargetId === activeAkte.id
                        ? 'bg-violet-500/[0.06] ring-1 ring-inset ring-violet-400/50'
                        : ''
                    }`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      event.dataTransfer.dropEffect = canWriteDocuments ? 'copy' : 'none';
                      if (canWriteDocuments) setAkteDropTargetId(activeAkte.id);
                    }}
                    onDragLeave={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                        setAkteDropTargetId(null);
                      }
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setAkteDropTargetId(null);
                      void assignDroppedToAkte(event, activeAkte);
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[12px] font-semibold text-slate-200 m-0">
                        Dokumente in dieser Akte
                      </p>
                      {canWriteDocuments ? (
                        <button
                          type="button"
                          data-testid="portal-doc-new"
                          onClick={() => void createBlankDocumentInAkte()}
                          disabled={uploadBusy || folderUploadBusy}
                          className="inline-flex items-center gap-1 rounded-md border border-blue-500/40 bg-blue-600/15 px-2 py-1 text-[10px] font-semibold text-blue-200 hover:bg-blue-600/25 disabled:opacity-50"
                        >
                          <FilePenLine className="w-3 h-3" />
                          Neues Dokument
                        </button>
                      ) : null}
                    </div>
                    <label className="relative block">
                      <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-500" aria-hidden />
                      <input
                        type="search"
                        value={akteDocListQuery}
                        onChange={(event) => setAkteDocListQuery(event.target.value)}
                        placeholder="Name in dieser Akte"
                        data-testid="portal-akte-doc-search"
                        aria-label="Dokumente dieser Akte suchen"
                        className="w-full rounded-md border border-white/10 bg-transparent py-1.5 pl-7 pr-2 text-[11px] text-slate-200 placeholder:text-slate-600 outline-none focus:border-white/25"
                      />
                    </label>
                    <div
                      data-testid="portal-akte-doc-dropzone"
                      onDragOver={(event) => {
                        allowPortalAkteDragOver(event, draggingDocId);
                        if (canWriteDocuments) setAkteDropTargetId(activeAkte.id);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setAkteDropTargetId(null);
                        void assignDroppedToAkte(event, activeAkte);
                      }}
                      onClick={() => {
                        if (canWriteDocuments) akteHubFileInputRef.current?.click();
                      }}
                      className={`rounded-lg border border-dashed px-3 py-2 text-[11px] ${
                        canWriteDocuments ? 'cursor-pointer' : ''
                      } ${
                        akteDropTargetId === activeAkte.id
                          ? 'border-violet-300 bg-violet-500/15 text-violet-100'
                          : 'border-violet-400/30 bg-violet-500/5 text-violet-200'
                      }`}
                    >
                      <input
                        ref={akteHubFileInputRef}
                        type="file"
                        accept={PORTAL_DOCUMENT_FILE_ACCEPT}
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          event.target.value = '';
                          if (!file) return;
                          if (isAudioFile(file)) {
                            setIngestMsg(
                              'Audio gehört in den Diktat-Reiter — in der Akte wurde nichts gespeichert.',
                            );
                            return;
                          }
                          const dt = new DataTransfer();
                          dt.items.add(file);
                          const fake = {
                            preventDefault() {},
                            stopPropagation() {},
                            dataTransfer: dt,
                          } as unknown as React.DragEvent;
                          void assignDroppedToAkte(fake, activeAkte);
                        }}
                      />
                      {canWriteDocuments
                        ? 'PDF, DOCX oder andere Dokumentdatei hier ablegen — Kopie in diese Akte.'
                        : 'Dokumente dieser Akte sind für diesen Sitz nur lesbar.'}
                    </div>
                    {akteDocSearchHits ? (
                      akteDocSearchHits.length > 0 ? (
                        <div
                          className="divide-y divide-white/[0.06] rounded-md border border-white/[0.06] overflow-visible"
                          data-testid="portal-akte-doc-search-hits"
                        >
                          {akteDocSearchHits.map((doc) => renderDocumentRow(doc, true))}
                        </div>
                      ) : (
                        <p className="m-0 text-[11px] text-slate-500" data-testid="portal-akte-doc-search-empty">
                          Kein Dokument zu dieser Suche.
                        </p>
                      )
                    ) : explorerSearching && akteExplorerDocs.length > 0 ? (
                      <div className="divide-y divide-white/[0.06] rounded-md border border-white/[0.06] overflow-visible">
                        {akteExplorerDocs.map((doc) => renderDocumentRow(doc, true))}
                      </div>
                    ) : !explorerSearching && (akteHasRows || akteInSubfolder) ? (
                      <div
                        className="w-full min-w-0 rounded-md border border-white/[0.06] overflow-visible"
                        data-testid="portal-akte-doc-tree"
                      >
                        {renderExplorerBreadcrumb(
                          akteExplorerFolder,
                          '',
                          activeAkte.title,
                          openAkteExplorerFolder,
                        )}
                        <div className="divide-y divide-white/[0.08]">
                          {akteChildFolders.map((folder) =>
                            renderExplorerFolderRow(folder, {
                              onOpen: openAkteExplorerFolder,
                              copyOntoAkte: true,
                            }),
                          )}
                        </div>
                        {akteExplorerDocs.length > 0 ? (
                          <div className="divide-y divide-white/[0.05] border-t border-white/[0.06]">
                            {akteExplorerDocs.map((doc) => renderDocumentRow(doc, true))}
                          </div>
                        ) : akteChildFolders.length > 0 ? (
                          <p className="px-4 py-3 text-[11px] text-slate-500 m-0">
                            Ordner öffnen, um die Dokumente darin zu sehen.
                          </p>
                        ) : (
                          <p className="px-4 py-6 text-center text-xs text-slate-300 m-0">
                            Noch keine Dokumente in diesem Ordner.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`min-h-[7rem] rounded-lg border-2 border-dashed px-4 py-6 text-center space-y-1 ${
                          akteDropTargetId === activeAkte.id
                            ? 'border-violet-400 bg-violet-500/15 ring-2 ring-violet-400/70'
                            : 'border-violet-400/35 bg-violet-500/5'
                        }`}
                        onDragOver={(event) => {
                          allowPortalAkteDragOver(event, draggingDocId);
                          if (canWriteDocuments) setAkteDropTargetId(activeAkte.id);
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setAkteDropTargetId(null);
                          void assignDroppedToAkte(event, activeAkte);
                        }}
                      >
                        <p className="text-xs text-slate-300 m-0">Noch keine Dokumente in dieser Akte.</p>
                        <p className="text-[11px] text-violet-200 m-0">
                          Datei links aus dem Pool hierher ziehen oder „Neues Dokument“ wählen.
                        </p>
                      </div>
                    )}
                  </div>
                ) : null}

                {rahmenSection === 'fristen' ? (
                  <div
                    role="tabpanel"
                    data-testid="portal-akte-hub-panel-fristen"
                    data-akte-rahmen-panel="fristen"
                    className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-3"
                  >
                    <p className="text-[11px] text-slate-500 m-0">
                      Fristen dieser Akte direkt anlegen oder ändern. ARKO bleibt optional.
                    </p>
                    {activeAkte.id ? (
                      <PortalFristenAktePanel
                        customerId={customerId}
                        authToken={authToken}
                        akteId={activeAkte.id}
                        akteNcPath={activeAkteFristenPath || activeAkte.nc_path || ''}
                        allowCrud
                        canWrite={canWriteDocuments}
                        actorEmail={completedByEmail || customerEmail}
                        customerStatus={customerStatus}
                        emptyMessage="Keine aktiven Fristen in dieser Akte."
                      />
                    ) : (
                      <p
                        role="status"
                        className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-3 text-[11px] text-amber-200 m-0"
                      >
                        Keine sichtbare Akte — Fristen werden nicht kundenweit geladen.
                      </p>
                    )}
                  </div>
                ) : null}

                {rahmenSection === 'arko' && arkoBooked ? (
                  <div data-testid="portal-akte-rahmen-panel-arko" className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                    <PortalAkteArkoHost />
                  </div>
                ) : null}

                {rahmenSection === 'onlyoffice' ? (
                  <div
                    role="tabpanel"
                    data-testid="portal-akte-rahmen-panel-onlyoffice"
                    data-akte-rahmen-panel="onlyoffice"
                    className="relative z-20 flex h-full min-h-0 flex-1 flex-col overflow-hidden"
                  >
                    {akteOnlyoffice ? (
                      <div className="absolute right-2 top-1.5 z-30">
                        <AkteOoChatFlip
                          closed={akteOoChatClosed}
                          onToggle={() => commitAkteOoSplit(akteOoEditorPct, !akteOoChatClosed)}
                        />
                      </div>
                    ) : null}
                    <div className="relative z-20 flex shrink-0 items-center gap-1 border-b border-white/[0.06] py-1.5 pl-2 pr-28">
                      <div
                        className="flex min-w-0 max-w-full items-center gap-1 overflow-x-auto"
                        role="tablist"
                        aria-label="Geöffnete Dokumente dieser Akte"
                      >
                        {akteOfficeDocs.map((doc) => {
                          const selected = doc.id === akteOfficeActiveId;
                          return (
                            <button
                              key={doc.id}
                              type="button"
                              role="tab"
                              aria-selected={selected}
                              data-testid="portal-akte-oo-doc-tab"
                              onClick={(event) => {
                                event.preventDefault();
                                onSelectAkteOfficeDoc?.(doc.id);
                              }}
                              className={`max-w-[16rem] shrink-0 truncate rounded-md px-2.5 py-1 text-left text-[11px] ${
                                selected
                                  ? 'bg-white/[0.08] text-slate-100'
                                  : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'
                              }`}
                              title={doc.title}
                            >
                              {doc.title}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          data-testid="portal-akte-oo-plus"
                          aria-expanded={ooPlusOpen}
                          aria-label="Dokument in dieser Akte"
                          onClick={() => setOoPlusOpen((open) => !open)}
                          className="inline-flex shrink-0 items-center justify-center rounded-md border border-violet-400/35 bg-violet-500/10 px-2 py-1 text-[10px] font-semibold text-violet-200 hover:bg-violet-500/20"
                        >
                          <Plus className="h-3 w-3" aria-hidden />
                        </button>
                      </div>
                      {ooPlusOpen ? (
                        <div
                          data-testid="portal-akte-oo-plus-menu"
                          className="absolute left-2 right-2 top-full z-20 mt-1 grid max-h-[32rem] grid-cols-1 overflow-hidden rounded-lg border border-violet-400/30 bg-[#07070a] shadow-lg sm:grid-cols-2"
                        >
                          <section className="flex max-h-[32rem] min-h-[16rem] flex-col border-white/[0.08] p-3 sm:border-r">
                            <p className="m-0 text-[12px] font-semibold text-slate-100">In dieser Akte</p>
                            <p className="m-0 mt-0.5 text-[10px] text-slate-500">Schon hier. Öffnen bleibt in diesem Reiter.</p>
                            <input
                              type="search"
                              value={ooPlusAkteQuery}
                              onChange={(event) => setOoPlusAkteQuery(event.target.value)}
                              placeholder="Name"
                              data-testid="portal-akte-oo-plus-search"
                              aria-label="Dokumente in dieser Akte suchen"
                              className="mt-2 w-full rounded-md border border-white/10 bg-transparent px-2 py-1.5 text-[11px] text-slate-200 placeholder:text-slate-600 outline-none focus:border-white/25"
                            />
                            <div className="mt-2 min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                              {filterDocsByFilename(akteOoPlusDocs.inAkte, ooPlusAkteQuery).length > 0 ? (
                                filterDocsByFilename(akteOoPlusDocs.inAkte, ooPlusAkteQuery).map((doc) => (
                                  <button
                                    key={doc.id}
                                    type="button"
                                    data-testid="portal-akte-oo-plus-open"
                                    onClick={() => openAkteDocFromPlus(doc)}
                                    className="flex w-full items-start gap-2 rounded-md px-1 py-1.5 text-left hover:bg-white/[0.04]"
                                  >
                                    <AkteFileTypeName filename={doc.filename} />
                                  </button>
                                ))
                              ) : (
                                <p className="m-0 px-1 py-1 text-[11px] text-slate-500">
                                  {ooPlusAkteQuery.trim() ? 'Kein Dokument zu dieser Suche.' : 'Keine weiteren Dokumente in dieser Akte.'}
                                </p>
                              )}
                            </div>
                          </section>
                          <section className="flex max-h-[32rem] min-h-[16rem] flex-col bg-violet-500/5 p-3">
                            <p className="m-0 text-[12px] font-semibold text-violet-100">Hinzufügen</p>
                            <p className="m-0 mt-0.5 text-[10px] text-violet-200/70">Kommt neu in diese Akte und öffnet danach hier.</p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <button
                                type="button"
                                data-testid="portal-akte-oo-plus-pc"
                                onClick={() => akteOoPlusFileRef.current?.click()}
                                className="inline-flex items-center rounded-md border border-violet-400/35 bg-violet-500/10 px-2 py-1 text-[10px] font-semibold text-violet-200 hover:bg-violet-500/20"
                              >
                                Vom PC
                              </button>
                              <button
                                type="button"
                                data-testid="portal-akte-oo-plus-blank"
                                onClick={() => {
                                  setOoPlusOpen(false);
                                  void createBlankDocumentInAkte();
                                }}
                                className="inline-flex items-center rounded-md bg-violet-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-violet-500"
                              >
                                Neues Dokument
                              </button>
                            </div>
                            <div className="mt-2 min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                              {akteOoPlusDocs.pool.map((doc) => (
                                <button
                                  key={doc.id}
                                  type="button"
                                  data-testid="portal-akte-oo-plus-pool"
                                  onClick={() => void addPoolDocFromPlus(doc)}
                                  className="flex w-full items-start gap-2 rounded-md px-1 py-1.5 text-left hover:bg-white/[0.04]"
                                >
                                  <AkteFileTypeName filename={doc.filename} />
                                </button>
                              ))}
                            </div>
                            <input
                              ref={akteOoPlusFileRef}
                              type="file"
                              accept={PORTAL_DOCUMENT_FILE_ACCEPT}
                              className="hidden"
                              onChange={(event) => {
                                const file = event.target.files?.[0] || null;
                                event.target.value = '';
                                if (!file) return;
                                void addPcFileFromPlus(file);
                              }}
                            />
                          </section>
                        </div>
                      ) : null}
                    </div>
                    <div
                      ref={akteOoSplitRef}
                      className="flex h-full min-h-[61vh] w-full min-w-0 flex-1 flex-col overflow-hidden lg:min-h-0 lg:flex-row"
                      data-testid="portal-akte-oo-split"
                      style={{ ['--akte-oo-editor-pct' as string]: `${akteOoEditorPct}%` }}
                    >
                      <div
                        className={`flex min-h-0 min-w-0 flex-col overflow-hidden ${
                          akteOnlyoffice && !akteOoChatClosed
                            ? 'flex-1 lg:w-[var(--akte-oo-editor-pct)] lg:max-w-[var(--akte-oo-editor-pct)] lg:flex-none'
                            : 'flex-1'
                        }`}
                      >
                      <div className="flex min-h-0 flex-1 flex-col overflow-hidden" data-testid="portal-akte-oo-host">
                        {akteOnlyoffice ? (
                          akteOnlyoffice
                        ) : (
                          <p className="m-0 px-3 py-6 text-sm text-slate-400">
                            Noch kein Dokument dieser Akte geöffnet. In Dokumente ein Dokument wählen.
                          </p>
                        )}
                      </div>
                      </div>
                      {akteOnlyoffice && !akteOoChatClosed ? (
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          aria-label="Breite zwischen Dokument und ARKO"
                          data-testid="portal-akte-oo-split-handle"
                          tabIndex={0}
                          onPointerDown={onAkteOoSplitPointerDown}
                          onKeyDown={(event) => {
                            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                            event.preventDefault();
                            const delta = event.key === 'ArrowRight' ? 2 : -2;
                            commitAkteOoSplit(akteOoEditorPct + delta, false);
                          }}
                          className="hidden h-2 shrink-0 cursor-row-resize bg-white/[0.06] hover:bg-sky-500/40 lg:flex lg:h-auto lg:w-1 lg:min-w-[4px] lg:cursor-col-resize"
                        />
                      ) : null}
                      {akteOnlyoffice && !akteOoChatClosed ? (
                        <div
                          className="flex min-h-[24rem] min-w-0 flex-1 flex-col overflow-hidden border-t border-white/[0.06] lg:min-h-0 lg:border-l lg:border-t-0"
                          data-testid="portal-akte-oo-chat"
                        >
                          <PortalAkteArkoHost />
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {akteHubTab === 'schreiben' ? (
                  <div
                    role="tabpanel"
                    data-testid="portal-akte-hub-panel-schreiben"
                    className="space-y-3 px-3 py-3"
                  >
                    <PortalAkteSchreibenPanel
                      key={`schreiben:${activeAkte.id}`}
                      akteId={activeAkte.id}
                      akteTitle={activeAkte.title}
                      kanzleiName={kanzleiName}
                      canWrite={canWriteDocuments}
                      docxAvailable={canPortalDocumentUpload}
                      pdfAvailable={Boolean(
                        ARKO_SCHREIBEN_PDF_URL
                          && PORTAL_NEXTCLOUD_SHARE_IMPORT_URL
                          && PORTAL_DOCUMENT_FOLDER_URL
                          && canPortalNcShareImport,
                      )}
                      onExport={exportPortalAkteSchreiben}
                      onShowDocuments={() => setAkteHubTab('dokumente')}
                    />

                    {contractGeneratorAvailable ? (
                      <section
                        data-testid="portal-akte-contract-generator"
                        className="rounded-lg border border-violet-400/30 bg-[#07070a] p-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold text-slate-200 m-0">
                              Bestehender Vertragsersteller
                            </p>
                            <p className="text-[11px] text-slate-500 m-0 mt-1">
                              Dieselben Vertragskarten und Gerüste; ein erzeugtes Dokument wird erst
                              nach bestätigter <code>akte_id</code> als Erfolg geöffnet.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setContractGeneratorOpen((open) => !open)}
                            aria-expanded={contractGeneratorOpen}
                            className="inline-flex items-center gap-2 rounded-md border border-violet-400/40 bg-[#07070a] px-2 py-1 text-[11px] font-semibold text-violet-100 hover:bg-violet-600/30"
                          >
                            <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
                              <span
                                className={`relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d] ${contractGeneratorOpen ? 'rotate-x-180' : ''}`}
                              >
                                <span className="absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 [backface-visibility:hidden]">
                                  <Plus className="h-3.5 w-3.5" />
                                </span>
                                <span
                                  className="absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md bg-violet-600 text-white [backface-visibility:hidden]"
                                  style={{ transform: 'rotateX(180deg)' }}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </span>
                              </span>
                            </span>
                            {contractGeneratorOpen ? 'Vertragsersteller schließen' : 'Vertrag erstellen'}
                          </button>
                        </div>
                        {contractGeneratorOpen ? (
                          <div className="mt-4 border-t border-violet-400/30 pt-4">
                            <ContractGeneratorView
                              customerId={customerId}
                              customerStatus={customerStatus}
                              customerEmail={customerEmail}
                              targetAkteId={activeAkte.id}
                              targetFolder={portalAkteAssignTargetFolder(activeAkte.id)}
                              onDocumentReady={handleContractDocumentReadyInAkte}
                            />
                          </div>
                        ) : null}
                      </section>
                    ) : (
                      <p className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-3 text-[11px] text-slate-400 m-0">
                        Allgemeine Schreiben sind verfügbar. Der bestehende Vertragsersteller bleibt
                        an seinen Premium- und Feature-Vertrag gebunden.
                      </p>
                    )}
                  </div>
                ) : null}
              </PortalAkteRahmenChrome>
              </div>
            ) : !activeAkte ? (
              <div className="border-t border-white/[0.08] px-3 py-4">
                <p className="m-0 text-sm text-slate-400">
                  Akte in der Liste öffnen.
                </p>
              </div>
            ) : null}
            </div>
            {!rahmenOpen ? (
            <>
            <p className="text-[10px] text-slate-600 m-0">
              Es erscheinen nur Dateien, die Sie in dieser Kanzlei sehen dürfen.
              Leere Ordner am Sitz-Root sind erlaubt.
            </p>
            <div
              onDragOver={(e) => {
                if (!activeAkte) return;
                allowPortalAkteDragOver(e, draggingDocId);
                setAkteDropTargetId(activeAkte.id);
                setTileDropTarget('cases');
              }}
              onDrop={(e) => {
                if (!activeAkte) return;
                e.preventDefault();
                e.stopPropagation();
                setAkteDropTargetId(null);
                void assignDroppedToAkte(e, activeAkte);
              }}
              className={`rounded-md border-2 border-dashed min-h-[3.5rem] px-3 py-3 text-center text-[11px] ${
                tileDropTarget === 'cases' || (activeAkte && akteDropTargetId === activeAkte.id)
                  ? 'border-violet-400 bg-violet-500/15 text-violet-200 ring-2 ring-violet-400/50'
                  : 'border-white/[0.08] text-slate-500'
              }`}
            >
              {activeAkte
                ? `Drop: der offenen Akte „${activeAkte.title}" zuordnen`
                : 'Drop: zuerst eine Akte fokussieren'}
            </div>
            </>
            ) : null}
          </div>
        </section>
        ) : null}

        {!isAktenHub ? (
        <section
          className={dokumenteTileChromeClass(focusedTile === 'quick', tileDropTarget === 'quick')}
          onClick={() => setFocusedTile('quick')}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = portalDragIsInternalDoc(e.dataTransfer, draggingDocId)
              ? 'move'
              : 'copy';
            setTileDropTarget('quick');
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setTileDropTarget('');
          }}
          onDrop={(e) => void handleTileDrop('quick', e)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setFocusedTile('quick');
          }}
          aria-label="Schnellzugriff fokussieren"
        >
          <header className="border-b border-violet-400/30 bg-[#07070a] px-3.5 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-white m-0">Schnellzugriff</h3>
                <p className="text-[11px] text-slate-500 m-0 mt-0.5">Zuletzt geöffnet + Favoriten</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${focusedTile === 'quick' ? 'bg-violet-500/20 text-violet-200' : 'text-slate-500'}`}>
                {focusedTile === 'quick' ? 'Aktiv' : 'Fokussieren'}
              </span>
            </div>
          </header>
          <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3 custom-scrollbar">
            <div>
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold text-slate-300 m-0">Zuletzt geöffnet</p>
                <span className="text-[10px] text-slate-600">letzte 5</span>
              </div>
              <div className="mt-1.5 space-y-1.5">
                {recentDocs.length ? (
                  recentDocs.slice(0, 5).map((recent) => {
                    const pbMatch = recent.pb_record_id
                      ? protokolle.find((p) => p.id === recent.pb_record_id)
                      : undefined;
                    const document_url = resolveProtokollDocumentUrl(protokolle, {
                      pb_record_id: recent.pb_record_id,
                      document_url: recent.document_url,
                    });
                    const nextcloud_path = recent.nextcloud_path || pbMatch?.nextcloud_path;
                    const openable = canOpenDocumentInPortal(
                      isPremium,
                      recent.display_name,
                      recent.nextcloud_path,
                      document_url,
                    );
                    return (
                      <div
                        key={`${recent.pb_record_id || recent.document_url}-${recent.openedAt}`}
                        className={`grid grid-cols-[minmax(2.5rem,auto)_minmax(0,1fr)_auto] gap-2 items-center rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1.5 ${
                          draggingDocId === pbMatch?.id ? 'opacity-50' : ''
                        } ${pbMatch?.nextcloud_path ? 'cursor-grab' : ''}`}
                        {...bindDocumentDrag(pbMatch)}
                      >
                        <span className="shrink-0 rounded border border-violet-400/30 bg-[#07070a] px-1 py-0.5 text-center text-[10px] font-semibold text-violet-200">
                          DATEI
                        </span>
                        <div className="min-w-0 overflow-hidden">
                          <p className="text-xs text-slate-200 break-words line-clamp-2 m-0">{recent.display_name}</p>
                          <p className="text-[10px] text-slate-600 m-0">{formatDeAtDateTime(recent.openedAt)}</p>
                        </div>
                        <DokumenteRowActions
                          onOpen={() => {
                            if (pbMatch) openProtokoll(pbMatch);
                            else if (openable) {
                              routeDocumentOpen({
                                display_name: recent.display_name,
                                document_url,
                                pb_record_id: pbMatch?.id,
                                nextcloud_path,
                                updated: pbMatch?.updated,
                                akte_id: pbMatch?.akte_id,
                              });
                            } else if (document_url) {
                              window.open(document_url, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          onRename={() => {
                            if (pbMatch) beginRenameDocument(pbMatch);
                          }}
                          onDelete={() => onRecentDocsChange?.()}
                          openDisabled={!openable && !document_url}
                          renameDisabled={!pbMatch || !canWriteDocuments || !pbMatch.nextcloud_path?.trim()}
                          renameHint={
                            pbMatch && canWriteDocuments && pbMatch.nextcloud_path?.trim()
                              ? 'Umbenennen'
                              : 'Umbenennen braucht eine Datei mit Nextcloud-Pfad'
                          }
                          deleteDisabled
                          deleteHint="Verlauf löschen ist nur über den Browser-Speicher möglich."
                        />
                      </div>
                    );
                  })
                ) : (
                  <p className="text-[11px] text-slate-600 m-0">Noch kein Verlauf in diesem Browser.</p>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold text-slate-300 m-0">Favoriten</p>
                <span className="text-[10px] text-slate-600">Stern</span>
              </div>
              <div className="mt-1.5 space-y-1.5">
                {favorites.length ? (
                  favorites.map((fav) => {
                    const doc = fav.pb_record_id
                      ? protokolle.find((row) => row.id === fav.pb_record_id)
                      : undefined;
                    return (
                      <div
                        key={fav.id}
                        className={`grid grid-cols-[minmax(2.5rem,auto)_minmax(0,1fr)_auto] gap-2 items-center rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1.5 ${
                          draggingDocId === doc?.id ? 'opacity-50' : ''
                        } ${doc?.nextcloud_path ? 'cursor-grab' : ''}`}
                        {...bindDocumentDrag(doc)}
                      >
                        <button
                          type="button"
                          onClick={() => setFavorites((prev) => togglePortalDocFavorite(prev, fav))}
                          title="Favorit lösen"
                          aria-label="Favorit lösen"
                          className="p-1 rounded-md text-amber-300"
                        >
                          <Star className="w-3.5 h-3.5" fill="currentColor" aria-hidden />
                        </button>
                        <p className="min-w-0 text-xs text-slate-200 break-words line-clamp-2 m-0">{fav.title}</p>
                        <DokumenteRowActions
                          onOpen={() => {
                            if (doc) openProtokoll(doc);
                            else if (fav.kind === 'folder') {
                              setCurrentFolder(sanitizePortalFolderInput(fav.nextcloud_path || ''));
                              setFocusedTile('documents');
                            }
                          }}
                          onRename={() => {
                            if (doc) beginRenameDocument(doc);
                          }}
                          onDelete={() => setFavorites((prev) => prev.filter((row) => row.id !== fav.id))}
                          renameDisabled={!doc || !canWriteDocuments || !doc.nextcloud_path?.trim()}
                          renameHint={
                            doc && canWriteDocuments && doc.nextcloud_path?.trim()
                              ? 'Umbenennen'
                              : FOLDER_RENAME_HINT
                          }
                          deleteHint="Aus Favoriten lösen (Browser)"
                        />
                      </div>
                    );
                  })
                ) : (
                  <p className="text-[11px] text-slate-600 m-0">Noch keine Favoriten — Stern in der Dateiliste oder Datei hier ablegen.</p>
                )}
              </div>
            </div>
            <p className="text-[10px] text-slate-600 m-0">
              Zuletzt geöffnet und Favoriten liegen nur in diesem Browser (pro Benutzer und Akte).
            </p>
            <p className="text-[10px] text-slate-600 m-0">
              Favoriten und Verlauf werden lokal gespeichert, solange das Backend keine Felder liefert.
            </p>
            <p
              className={`rounded-md border border-dashed px-3 py-2 text-center text-[11px] ${
                tileDropTarget === 'quick'
                  ? 'border-violet-400/50 bg-violet-500/10 text-violet-200'
                  : 'border-violet-400/30 text-slate-500'
              }`}
            >
              Drop: als Favorit merken
            </p>
          </div>
        </section>
        ) : null}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleManualRefresh()}
          disabled={manualRefreshing}
          title="Aktualisieren"
          aria-label="Aktualisieren"
          className={
            isAktenHub
              ? 'flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 px-2 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors disabled:opacity-60'
              : KANZLEI_DOC_ICON
          }
        >
          {manualRefreshing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          {isAktenHub ? 'Aktualisieren' : <span className="sr-only">Aktualisieren</span>}
        </button>
      </div>

      {protokolleError ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3 text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {protokolleError}
        </div>
      ) : null}

      {!hasAnyDocuments && !protokolleLoading && recentDocs.length > 0 ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3 text-amber-200/90 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            „Zuletzt geöffnet“ stammt nur aus diesem Browser. Die Server-Liste ist leer — bitte{' '}
            <button type="button" onClick={() => void handleManualRefresh()} className="underline hover:text-amber-100">
              aktualisieren
            </button>{' '}
            oder mit{' '}
            <a href="?forceLogin=1" className="underline hover:text-amber-100">
              ?forceLogin=1
            </a>{' '}
            neu anmelden.
          </span>
        </div>
      ) : null}

      <div className="flex items-start gap-2 text-slate-600 text-[11px] leading-relaxed px-1">
        <ShieldCheck
          className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${isAktenHub ? 'text-blue-400/70' : 'text-violet-300/80'}`}
        />
        <span>
          Gespeicherte Dokumente liegen in Frankfurt (EU). Zuletzt geöffnet ist nur ein
          Browser-Verlauf. Bei abgelaufenem Link:{' '}
          <a
            href="mailto:office@arkorbit.at"
            className={isAktenHub ? 'text-blue-400 hover:text-blue-300' : 'text-violet-300 hover:text-violet-100'}
          >
            office@arkorbit.at
          </a>
        </span>
      </div>

      {shareDoc && sharePopover && authToken ? (
        <PortalDocumentSharePanel
          doc={shareDoc}
          viewerId={customerId}
          kanzleiId={kanzleiId || ''}
          authToken={authToken}
          canShare={canWriteDocuments && Boolean(kanzleiId)}
          seats={kanzleiSeats}
          shares={docShares}
          matrixRoomId={matrixRoomId}
          anchor={{ left: sharePopover.left, top: sharePopover.top }}
          ignoreCloseRef={shareAnchorRef}
          onClose={closeSharePopover}
          onSharesChanged={refreshSitzePrivacy}
        />
      ) : null}

      {rowContextMenu ? (
        <div
          className="fixed z-[80] min-w-[12rem] rounded-lg border border-white/[0.12] bg-slate-950 py-1 shadow-xl"
          style={{ left: rowContextMenu.x, top: rowContextMenu.y }}
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-1.5 text-left text-xs text-slate-200 hover:bg-white/[0.06]"
            onClick={(event) => {
              const doc = protokolle.find((row) => row.id === rowContextMenu.docId);
              if (doc) beginMoveToAkte(doc, event.currentTarget);
            }}
          >
            In Akte kopieren oder einfügen…
          </button>
        </div>
      ) : null}

      {moveDoc && movePopover && typeof document !== 'undefined'
        ? createPortal(
        <div
          ref={movePopoverRef}
          className="fixed z-[90] rounded-lg border border-violet-500/25 bg-slate-950 p-3 space-y-2 shadow-xl outline-none"
          style={{
            left: movePopover.left,
            top: movePopover.top,
            width: 'min(320px, calc(100vw - 1rem))',
          }}
          role="dialog"
          aria-modal="false"
          aria-labelledby="move-to-akte-title"
          data-testid="portal-move-to-akte-popover"
          tabIndex={-1}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 id="move-to-akte-title" className="text-[11px] font-semibold text-violet-100 m-0">
                In Akte kopieren oder einfügen
              </h3>
              <p className="m-0 mt-1 truncate text-[11px] text-slate-400">{moveDoc.filename}</p>
            </div>
            <button
              type="button"
              onClick={closeMovePopover}
              className="rounded-md p-1 text-slate-500 hover:text-slate-100"
              aria-label="Aktenauswahl schließen"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {portalAkten.length === 0 ? (
            <p className="text-xs text-slate-400 m-0">
              Noch keine Akte. Anlegen im Reiter Akten — optional, die Ablage bleibt nutzbar.
            </p>
          ) : (
            <>
              <ul className="max-h-48 overflow-y-auto space-y-1 m-0 p-0 list-none">
                {portalAkten.map((akteRow) => (
                  <li key={akteRow.id}>
                    <button
                      type="button"
                      aria-pressed={moveTargetAkteId === akteRow.id}
                      disabled={movingDocId === moveDoc.id}
                      className={`w-full rounded-md border px-3 py-2 text-left text-xs disabled:cursor-wait disabled:opacity-50 ${
                        moveTargetAkteId === akteRow.id
                          ? 'border-violet-400/70 bg-violet-500/15 text-white'
                          : 'border-white/[0.08] text-slate-100 hover:border-violet-400/50 hover:bg-violet-500/10'
                      }`}
                      onClick={() => {
                        setMoveError(null);
                        setMoveTargetAkteId(akteRow.id);
                      }}
                    >
                      {akteRow.title}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-1 gap-1.5">
                <button
                  type="button"
                  data-testid="portal-akte-copy-btn"
                  disabled={!moveTargetAkteId || movingDocId === moveDoc.id}
                  className="w-full rounded-md border border-violet-500/40 bg-violet-600/20 px-3 py-2 text-left text-[11px] font-semibold text-violet-100 hover:bg-violet-600/30 disabled:cursor-wait disabled:opacity-50"
                  onClick={() => {
                    const akteRow = portalAkten.find((row) => row.id === moveTargetAkteId);
                    if (!akteRow) {
                      setMoveError('Bitte zuerst eine Akte wählen.');
                      return;
                    }
                    setMoveError(null);
                    void copyDocumentToAkte(moveDoc, akteRow).then((result) => {
                      if (result.ok) closeMovePopover();
                      else if (result.ok === false) setMoveError(result.error);
                    });
                  }}
                >
                  In Akte kopieren
                </button>
                <button
                  type="button"
                  data-testid="portal-akte-cut-btn"
                  disabled={!moveTargetAkteId || movingDocId === moveDoc.id}
                  className="w-full rounded-md border border-white/[0.12] bg-white/[0.04] px-3 py-2 text-left text-[11px] font-semibold text-slate-100 hover:bg-white/[0.08] disabled:cursor-wait disabled:opacity-50"
                  onClick={() => {
                    const akteRow = portalAkten.find((row) => row.id === moveTargetAkteId);
                    if (!akteRow) {
                      setMoveError('Bitte zuerst eine Akte wählen.');
                      return;
                    }
                    setMoveError(null);
                    void assignDocumentToAkte(moveDoc, akteRow).then((result) => {
                      if (result.ok) closeMovePopover();
                      else if (result.ok === false) setMoveError(result.error);
                    });
                  }}
                >
                  In Akte einfügen
                </button>
              </div>
              <p className="m-0 text-[10px] leading-snug text-slate-500">
                Kopieren: Original bleibt im Pool. Einfügen: Datei nur noch in der Akte.
              </p>
            </>
          )}
          {moveError ? (
            <p
              role="alert"
              data-testid="portal-move-to-akte-error"
              className="m-0 rounded-md border border-red-500/25 bg-red-500/5 px-2 py-1.5 text-[11px] text-red-200"
            >
              {moveError}
            </p>
          ) : null}
          {onGoToAkten && portalAkten.length === 0 ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  closeMovePopover();
                  onGoToAkten();
                }}
                className="rounded-md bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-violet-500"
              >
                Zu Akten
              </button>
            </div>
          ) : null}
        </div>,
        document.body,
      )
      : null}
    </div>
  );
};

/** Verfahrensmotor Slice 1 — Dokument-Spiegel der Approval-Card (gleiche `card_id` wie Chat). */
export { FristApprovalDocumentMirror } from '../verfahrensmotor/FristApprovalDocumentMirror';
